import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { autonomyBandStyle } from '../services/trustBands';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
// usePlatform removed — ControlScorecardPanel uses direct API resolution instead
import { usePersistedState } from '../hooks/usePersistedState';
import * as api from '../services/api';
import PolicySkillWizard from '../components/policy/PolicySkillWizard';
import EnforcementReviewPanel from '../components/policy/EnforcementReviewPanel';
// ComplianceDashboard replaced by inline ControlScorecardPanel
import PatternLibraryPanel from '../components/policy/PatternLibraryPanel';
import DataClassificationRulesPanel from '../components/policy/DataClassificationRulesPanel';
import PageHeader from '../components/common/PageHeader';
/* G93 — re-skin chrome with v2 components. v1 panels (PolicySkillWizard,
 * EnforcementReviewPanel, PatternLibraryPanel, DataClassificationRulesPanel)
 * and all hooks/handlers/api calls stay. v2 imports are aliased to avoid
 * collision with v1's `Modal`, `Activity`, etc. */
import {
  V2PageShell,
  TabNav as V2TabNav,
  Button as V2Button,
  Pill as V2Pill,
} from '../components/v2';
import {
  Shield, FileCheck, BarChart3, BookOpen, Plus, RefreshCw,
  ChevronRight, AlertTriangle, CheckCircle2, Clock, Archive, ExternalLink,
  Search, Filter, Activity, Layers, Zap, Eye, Settings, TrendingUp,
  Lock, ShieldCheck, ShieldAlert, FileText, Tag, MoreHorizontal,
  ArrowUpRight, ChevronDown, ChevronUp, X, Sparkles, AlertCircle, Globe2,
  CheckCircle, Library, LayoutGrid, List, Loader, Wrench, Trash2, Link2,
  Building2, Users, Bot, ToggleLeft, ToggleRight, Target,
  ArrowUpCircle, ArrowDownCircle, TrendingDown, ClipboardCheck,
  Inbox, Send, Upload, Download, Database,
} from 'lucide-react';
import { Modal } from '../components/common/Modal';

/* ══════════════════════════════════════════════════════════════════════════════
   DESIGN SYSTEM — Credo.ai-inspired Enterprise Governance Aesthetic
   Clean, data-dense, premium feel with subtle depth and motion
   ══════════════════════════════════════════════════════════════════════════════ */

const BRAND = {
  primary: '#0d9488', dark: '#0a766b', light: '#e3f4f1',
  glow: 'rgba(226, 74, 58, 0.06)',
  mid: '#d4453a', soft: '#f8d7d4', muted: '#eab3ad', tint: '#fff5f4',
  gradient: 'linear-gradient(135deg, #0d9488 0%, #0a766b 50%, #a03025 100%)',
  gradientSubtle: 'linear-gradient(135deg, rgba(226,74,58,0.04) 0%, rgba(197,58,43,0.02) 100%)',
};

const S = { // Stone palette
  0: '#ffffff', 25: '#fdfdfc', 50: '#fafaf9', 100: '#f5f5f4', 150: '#eeeded',
  200: '#e7e5e4', 300: '#d6d3d1', 400: '#a8a29e', 500: '#78716c',
  600: '#57534e', 700: '#44403c', 800: '#292524', 900: '#1c1917',
};

const ACCENT = {
  emerald:  { bg: '#ecfdf5', text: '#065f46', border: '#a7f3d0', icon: '#10b981' },
  amber:    { bg: '#fffbeb', text: '#92400e', border: '#fde68a', icon: '#f59e0b' },
  sky:      { bg: '#f0f9ff', text: '#0c4a6e', border: '#bae6fd', icon: '#0ea5e9' },
  rose:     { bg: '#fff1f2', text: '#9f1239', border: '#fecdd3', icon: '#f43f5e' },
  violet:   { bg: '#f5f3ff', text: '#5b21b6', border: '#ddd6fe', icon: '#8b5cf6' },
  slate:    { bg: '#f8fafc', text: '#475569', border: '#e2e8f0', icon: '#64748b' },
};

const STATUS_MAP = {
  draft:    { bg: S[100],   text: S[600],  border: S[200],       label: 'Draft',    icon: FileCheck, accent: ACCENT.slate },
  testing:  { bg: '#fef3c7', text: '#92400e', border: '#fde68a', label: 'Testing',  icon: Activity,  accent: ACCENT.amber },
  active:   { bg: '#dcfce7', text: '#166534', border: '#a7f3d0', label: 'Active',   icon: Zap,       accent: ACCENT.emerald },
  paused:   { bg: BRAND.light, text: BRAND.dark, border: BRAND.soft, label: 'Paused', icon: Clock,   accent: ACCENT.rose },
  archived: { bg: S[100],   text: S[400],  border: S[200],       label: 'Archived', icon: Archive,   accent: ACCENT.slate },
};

const TABS = [
  { key: 'configurations',  label: 'Policy Skills',       icon: Shield,   desc: 'Deterministic enforcement rules for agent execution' },
  { key: 'policy-library',  label: 'Policy Library',      icon: Library,  desc: 'Compliance frameworks & regulatory controls' },
  { key: 'control-config',  label: 'Control Config',      icon: Target,   desc: 'Assign framework controls to Org / Tenant / Agents' },
  { key: 'framework-coverage', label: 'Framework Coverage', icon: Layers,  desc: 'Gap analysis: framework controls vs active PEP enforcement' },
  { key: 'control-drift',   label: 'Control Drift',       icon: TrendingUp, desc: 'Detect when control assignments deviate from approved baselines' },
  { key: 'control-scorecard', label: 'Control Scorecard',   icon: BarChart3, desc: 'Agent performance scores against assigned controls' },
  { key: 'classification',  label: 'Data Classification', icon: Database, desc: 'DPG rules: PII / credential / financial / health detection patterns' },
  { key: 'patterns',        label: 'Pattern Library',     icon: BookOpen, desc: 'Reusable enforcement pattern templates' },
];

/* ─── Shared Base Styles ───────────────────────────────────────────────── */
const btnBase = {
  border: 'none', cursor: 'pointer', borderRadius: 8, fontFamily: 'inherit',
  display: 'inline-flex', alignItems: 'center', gap: 6, transition: 'all 0.15s ease',
};
const inputBase = {
  width: '100%', padding: '9px 12px', border: `1px solid ${S[200]}`, borderRadius: 8,
  fontSize: 13, fontFamily: 'inherit', background: '#fff', color: S[900],
  outline: 'none', transition: 'border-color 0.15s, box-shadow 0.15s',
};

/* ─── CSS Keyframes (injected once) ─────────────────────────────────────── */
const GlobalStyles = () => (
  <style>{`
    @keyframes pcpSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
    @keyframes pcpPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
    @keyframes pcpSlideUp { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes pcpShimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
    @keyframes pcpFadeIn { from { opacity: 0; } to { opacity: 1; } }
    .pcp-card:hover { border-color: ${BRAND.primary}30 !important; box-shadow: 0 4px 20px ${BRAND.glow}, 0 1px 3px rgba(0,0,0,0.04) !important; transform: translateY(-1px); }
    .pcp-card { transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important; }
    .pcp-btn-ghost:hover { background: ${S[100]} !important; }
    .pcp-btn-primary:hover { filter: brightness(1.08); box-shadow: 0 2px 8px rgba(226,74,58,0.25); }
    .pcp-stat-card:hover { border-color: ${S[300]} !important; box-shadow: 0 2px 12px rgba(0,0,0,0.04) !important; }
    .pcp-tab:hover { color: ${S[700]} !important; background: ${S[50]} !important; }
    .pcp-search:focus { border-color: ${BRAND.primary}60 !important; box-shadow: 0 0 0 3px ${BRAND.glow} !important; }
    .pcp-row:hover { background: ${S[25]} !important; }
    .pcp-action-btn:hover { background: ${S[150]} !important; color: ${S[800]} !important; }
  `}</style>
);

/* ══════════════════════════════════════════════════════════════════════════════
   REUSABLE COMPONENTS
   ══════════════════════════════════════════════════════════════════════════════ */

function StatusBadge({ status, size = 'default' }) {
  const s = STATUS_MAP[status] || STATUS_MAP.draft;
  const Icon = s.icon;
  const isSmall = size === 'small';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: isSmall ? 4 : 5,
      fontSize: isSmall ? 10 : 11, fontWeight: 600,
      padding: isSmall ? '2px 8px' : '3px 10px',
      borderRadius: 6, background: s.bg, color: s.text,
      border: `1px solid ${s.border}`,
      fontFamily: "'IBM Plex Mono', 'SF Mono', monospace",
      letterSpacing: '0.01em',
    }}>
      <Icon size={isSmall ? 10 : 11} />
      {s.label}
    </span>
  );
}

function MetricPill({ value, label, icon: Icon, trend }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontSize: 11, color: S[500], background: S[50], padding: '3px 10px',
      borderRadius: 6, border: `1px solid ${S[200]}`,
    }}>
      {Icon && <Icon size={11} color={S[400]} />}
      <strong style={{ color: S[700], fontWeight: 600 }}>{value}</strong>
      {label && <span>{label}</span>}
      {trend && <TrendingUp size={10} color={ACCENT.emerald.icon} />}
    </span>
  );
}

/* ── Stat Card (Hero Row) ───────────────────────────────────────────────── */
function StatCard({ label, value, subtitle, icon: Icon, accent, loading: isLoading }) {
  const a = accent || ACCENT.slate;
  return (
    <div className="pcp-stat-card" style={{
      flex: 1, minWidth: 180, background: '#fff', border: `1px solid ${S[200]}`,
      borderRadius: 12, padding: '18px 20px', position: 'relative', overflow: 'hidden',
      cursor: 'default', transition: 'all 0.2s ease',
    }}>
      {/* Accent top line */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: a.icon, opacity: 0.15 }} />
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <div style={{
            fontSize: 10, fontWeight: 700, color: a.text, textTransform: 'uppercase',
            letterSpacing: '0.08em', marginBottom: 8,
          }}>{label}</div>
          {isLoading ? (
            <div style={{ width: 48, height: 28, borderRadius: 6, background: `linear-gradient(90deg, ${S[100]} 25%, ${S[50]} 50%, ${S[100]} 75%)`, backgroundSize: '200% 100%', animation: 'pcpShimmer 1.5s infinite' }} />
          ) : (
            <div style={{ fontSize: 30, fontWeight: 800, color: S[900], lineHeight: 1, letterSpacing: '-0.03em', fontFeatureSettings: "'tnum'" }}>{value}</div>
          )}
          {subtitle && <div style={{ fontSize: 11, color: S[500], marginTop: 6, lineHeight: 1.3 }}>{subtitle}</div>}
        </div>
        <div style={{
          width: 36, height: 36, borderRadius: 10, background: a.bg, border: `1px solid ${a.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          {Icon && <Icon size={17} color={a.icon} strokeWidth={2} />}
        </div>
      </div>
    </div>
  );
}

/* ── Policy Skill Card (Grid Item) ──────────────────────────────────────── */
function PolicySkillCard({ config, onOpen, onReview }) {
  const statusInfo = STATUS_MAP[config.activationStatus] || STATUS_MAP.draft;
  const hasViolations = (config.totalViolations || 0) > 0;
  const isActive = config.activationStatus === 'active';
  const docCount = config.documentIds?.length || 0;
  const ruleCount = config.activeRuleCount || 0;
  const evalCount = config.totalEvaluations || 0;
  const isGelBridge = (config.skillName || '').startsWith('GEL:');

  return (
    <div className="pcp-card" onClick={() => onOpen(config)} style={{
      background: isGelBridge ? '#fef7f0' : '#fff',
      border: `1px solid ${isGelBridge ? '#fed7aa' : S[200]}`, borderRadius: 12,
      padding: 0, cursor: 'pointer', display: 'flex', flexDirection: 'column',
      position: 'relative', overflow: 'hidden',
    }}>
      {/* Status accent bar */}
      <div style={{ height: 3, background: isGelBridge ? '#f97316' : statusInfo.accent.icon, opacity: isActive ? 0.6 : 0.2 }} />

      <div style={{ padding: '18px 20px 16px', flex: 1, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Header row */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
            <div style={{
              width: 38, height: 38, borderRadius: 10, flexShrink: 0,
              background: isGelBridge ? '#fff7ed' : statusInfo.accent.bg,
              border: `1px solid ${isGelBridge ? '#fed7aa' : statusInfo.accent.border}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {isGelBridge
                ? <Globe2 size={17} color="#ea580c" strokeWidth={2} />
                : <Shield size={17} color={statusInfo.accent.icon} strokeWidth={2} />
              }
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{
                  fontSize: 14, fontWeight: 700, color: S[900], lineHeight: 1.3,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {config.skillName || `Skill ${config.skillId?.substring(0, 8)}...`}
                </div>
                {isGelBridge && (
                  <span style={{
                    fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
                    background: 'linear-gradient(135deg, #ea580c, #c2410c)',
                    color: '#fff', letterSpacing: '0.05em', flexShrink: 0,
                    textTransform: 'uppercase',
                  }}>GEL Bridge</span>
                )}
              </div>
              <div style={{ fontSize: 11, color: S[500], marginTop: 2 }}>
                {isGelBridge
                  ? 'External agent proxy — rules materialized from GEL Governance Policies'
                  : (config.description || 'Policy enforcement skill')
                }
              </div>
            </div>
          </div>
          <StatusBadge status={config.activationStatus} size="small" />
        </div>

        {/* Metrics row */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <MetricPill value={docCount} label="docs" icon={FileText} />
          <MetricPill value={ruleCount} label="rules" icon={Lock} />
          <MetricPill value={evalCount.toLocaleString()} label="evals" icon={Activity} />
          {hasViolations && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              fontSize: 11, fontWeight: 600, color: ACCENT.rose.text,
              background: ACCENT.rose.bg, padding: '3px 10px', borderRadius: 6,
              border: `1px solid ${ACCENT.rose.border}`,
            }}>
              <AlertCircle size={11} />
              {config.totalViolations} violations
            </span>
          )}
        </div>

        {/* Rule type tags */}
        {config.ruleTypes && config.ruleTypes.length > 0 && (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {config.ruleTypes.slice(0, 4).map(type => (
              <span key={type} style={{
                fontSize: 10, fontWeight: 500, color: S[500], background: S[50],
                padding: '2px 7px', borderRadius: 4, border: `1px solid ${S[150]}`,
                fontFamily: "'IBM Plex Mono', monospace",
              }}>{type}</span>
            ))}
            {config.ruleTypes.length > 4 && (
              <span style={{ fontSize: 10, color: S[400] }}>+{config.ruleTypes.length - 4}</span>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{
        padding: '10px 20px', borderTop: `1px solid ${S[100]}`, background: S[25],
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <span style={{ fontSize: 10, color: S[400] }}>
          {isGelBridge
            ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <Link2 size={9} /> Managed via External Agent Registrations
              </span>
            : (config.updatedAt ? `Updated ${new Date(config.updatedAt).toLocaleDateString()}` : 'Recently created')
          }
        </span>
        <div style={{ display: 'flex', gap: 4 }} onClick={e => e.stopPropagation()}>
          {isActive && (
            <button className="pcp-action-btn" onClick={() => onReview(config.skillId)} style={{
              ...btnBase, padding: '4px 10px', fontSize: 10, fontWeight: 600,
              background: S[100], color: S[600], borderRadius: 6,
            }}>
              <Eye size={11} /> Review
            </button>
          )}
          <button className="pcp-action-btn" onClick={() => onOpen(config)} style={{
            ...btnBase, padding: '4px 10px', fontSize: 10, fontWeight: 600,
            background: S[100], color: S[600], borderRadius: 6,
          }}>
            Configure <ChevronRight size={11} />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Empty State ────────────────────────────────────────────────────────── */
function EmptyState({ onCreate }) {
  return (
    <div style={{
      textAlign: 'center', padding: '64px 32px', background: '#fff',
      border: `2px dashed ${S[200]}`, borderRadius: 16,
      animation: 'pcpFadeIn 0.4s ease',
    }}>
      <div style={{
        width: 64, height: 64, borderRadius: 16, margin: '0 auto 20px',
        background: BRAND.gradientSubtle, border: `1px solid ${S[200]}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Shield size={28} color={S[400]} strokeWidth={1.5} />
      </div>
      <div style={{ fontSize: 18, fontWeight: 700, color: S[900], marginBottom: 8 }}>
        No Policy Skills Configured
      </div>
      <div style={{ fontSize: 13, color: S[500], maxWidth: 440, margin: '0 auto 24px', lineHeight: 1.6 }}>
        Upload policy documents, extract enforcement rules, and configure deterministic
        controls that execute before every LLM call. Build your protection foundation.
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 10 }}>
        <button className="pcp-btn-primary" onClick={onCreate} style={{
          ...btnBase, padding: '10px 22px', fontSize: 13, fontWeight: 700,
          background: BRAND.gradient, color: '#fff', borderRadius: 10,
          boxShadow: '0 2px 8px rgba(226,74,58,0.2)',
        }}>
          <Plus size={15} /> Create Policy Skill
        </button>
        <button className="pcp-btn-ghost" style={{
          ...btnBase, padding: '10px 18px', fontSize: 13, fontWeight: 500,
          background: 'transparent', color: S[600], border: `1px solid ${S[200]}`,
          borderRadius: 10,
        }}>
          <BookOpen size={14} /> View Patterns
        </button>
      </div>
    </div>
  );
}

/* ── Loading Skeleton ───────────────────────────────────────────────────── */
function LoadingSkeleton() {
  const shimmer = {
    background: `linear-gradient(90deg, ${S[100]} 25%, ${S[50]} 50%, ${S[100]} 75%)`,
    backgroundSize: '200% 100%', animation: 'pcpShimmer 1.5s infinite', borderRadius: 8,
  };
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 14 }}>
      {[1, 2, 3].map(i => (
        <div key={i} style={{
          background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12,
          padding: 20, animation: 'pcpFadeIn 0.3s ease', animationDelay: `${i * 0.1}s`,
          animationFillMode: 'backwards',
        }}>
          <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
            <div style={{ width: 38, height: 38, borderRadius: 10, ...shimmer }} />
            <div style={{ flex: 1 }}>
              <div style={{ width: '60%', height: 16, marginBottom: 8, ...shimmer }} />
              <div style={{ width: '40%', height: 12, ...shimmer }} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ width: 60, height: 22, ...shimmer }} />
            <div style={{ width: 60, height: 22, ...shimmer }} />
            <div style={{ width: 80, height: 22, ...shimmer }} />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   MAIN PAGE COMPONENT
   ══════════════════════════════════════════════════════════════════════════════ */
export default function PolicyConfigPage() {
  const { session, currentRole } = useAuth();
  const [activeTab, setActiveTab] = usePersistedState('policyConfig.tab', 'configurations');

  // ── Deep-link support — honor ?tab=<key> on mount/URL change ─────────
  // When other parts of the UI (canvas Coverage tab, agent detail Trust
  // page, etc.) want to drop the user on a specific Policy Config tab,
  // they navigate to /policy-config?tab=control-config (or whichever tab
  // key is in TABS). We pick that up here, override the persisted tab,
  // then strip the param from the URL so a refresh doesn't pin them on
  // the deep-linked tab forever — they get the normal usePersistedState
  // experience after the first render.
  //
  // Guard against re-entry: the only mutation is setActiveTab(requested)
  // which makes `requested !== activeTab` false on the next render, then
  // the param-strip removes `tab` from the URL so `requested` becomes null
  // on the render after that. Steady state reached in 2 renders, no loop.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const requested = searchParams.get('tab');
    if (!requested) return;
    const validKeys = TABS.map((t) => t.key);
    if (validKeys.includes(requested) && requested !== activeTab) {
      setActiveTab(requested);
    }
    // Drop the consumed param so future refreshes use persisted state.
    const next = new URLSearchParams(searchParams);
    next.delete('tab');
    setSearchParams(next, { replace: true });
  }, [searchParams, activeTab, setActiveTab, setSearchParams]);
  const [configs, setConfigs] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showWizard, setShowWizard] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [wizardPresets, setWizardPresets] = useState(null); // { preSelectedPatterns, preSourceMode, preBindingConfig }
  const [reviewSkillId, setReviewSkillId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = usePersistedState('policyConfig.view', 'grid');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [cfgRes, statsRes] = await Promise.all([
        api.getPolicyConfigs(),
        api.getPolicyConfigStats(),
      ]);
      if (cfgRes.ok) setConfigs(cfgRes.data || []);
      if (statsRes.ok) setStats(statsRes.data || {});
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleCreateNew = () => { setSelectedConfig(null); setShowWizard(true); };
  const handleOpenConfig = (config) => {
    setSelectedConfig(config);
    if (config.activationStatus === 'draft' || config.activationStatus === 'testing') {
      setShowWizard(true);
    } else {
      setReviewSkillId(config.skillId);
    }
  };
  const handleWizardComplete = () => { setShowWizard(false); setSelectedConfig(null); loadData(); };

  // Filtered configs
  const filtered = useMemo(() => {
    return configs.filter(c => {
      if (statusFilter !== 'all' && c.activationStatus !== statusFilter) return false;
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        return (c.skillName || '').toLowerCase().includes(q)
          || (c.description || '').toLowerCase().includes(q);
      }
      return true;
    });
  }, [configs, statusFilter, searchTerm]);

  // Counts
  const totalConfigs = configs.length;
  const activeCount = configs.filter(c => c.activationStatus === 'active').length;
  const draftCount = configs.filter(c => c.activationStatus === 'draft').length;
  const testingCount = configs.filter(c => c.activationStatus === 'testing').length;
  const totalRules = configs.reduce((sum, c) => sum + (c.activeRuleCount || 0), 0);
  const totalEvals = configs.reduce((sum, c) => sum + (c.totalEvaluations || 0), 0);

  // ── Full-screen Wizard ──
  if (showWizard) {
    return (
      <PolicySkillWizard
        config={selectedConfig}
        onComplete={() => { handleWizardComplete(); setWizardPresets(null); }}
        onCancel={() => { setShowWizard(false); setSelectedConfig(null); setWizardPresets(null); }}
        preSelectedPatterns={wizardPresets?.preSelectedPatterns}
        preSourceMode={wizardPresets?.preSourceMode}
        preBindingConfig={wizardPresets?.preBindingConfig}
        frameworkBindingId={wizardPresets?.frameworkBindingId}
        frameworkId={wizardPresets?.frameworkId}
        frameworkName={wizardPresets?.frameworkName}
        // Phase 4 handoff: when the operator clicks "Open in Wizard" on a
        // gap row, the panel passes the gap control's UUID(s) so the wizard
        // can pre-select just those candidates after Stage 2 extraction.
        preSelectedFrameworkControlIds={wizardPresets?.preSelectedFrameworkControlIds}
      />
    );
  }

  // ── Enforcement Review ──
  if (reviewSkillId) {
    return (
      <div style={{ padding: '28px 32px', maxWidth: 1400, margin: '0 auto' }}>
        <button onClick={() => setReviewSkillId(null)} style={{
          ...btnBase, padding: '6px 12px', background: S[100], color: S[600],
          fontSize: 12, fontWeight: 500, marginBottom: 16, border: `1px solid ${S[200]}`,
        }}>
          <ChevronRight size={13} style={{ transform: 'rotate(180deg)' }} />
          Back to Policy Library
        </button>
        <EnforcementReviewPanel skillId={reviewSkillId} onBack={() => setReviewSkillId(null)} />
      </div>
    );
  }

  /* G93 — v2 TabNav config + active-tab description for the helper strip. */
  const v2Tabs = TABS.map(t => ({
    id: t.key,
    label: t.label,
    badge: (t.key === 'configurations' && totalConfigs > 0) ? totalConfigs : undefined,
  }));
  const activeTabMeta = TABS.find(t => t.key === activeTab);

  return (
    <V2PageShell
      title="Policy Library"
      subtitle="Configure deterministic enforcement rules, manage policy documents, and protect agent behavior."
      tags={<V2Pill tone="cyan" size="sm">Protect</V2Pill>}
      actions={
        <>
          <V2Button
            variant="ghost"
            size="sm"
            iconLeft={<RefreshCw size={13} />}
            onClick={loadData}
          >
            Refresh
          </V2Button>
          <V2Button
            variant="primary"
            size="sm"
            iconLeft={<Plus size={14} />}
            onClick={handleCreateNew}
          >
            New Policy Skill
          </V2Button>
        </>
      }
    >
      <GlobalStyles />

      <V2TabNav
        tabs={v2Tabs}
        activeId={activeTab}
        onChange={setActiveTab}
        ariaLabel="Policy Library sections"
      />

      {/* ─── Active tab description (kept from v1) ─── */}
      {activeTabMeta && (
        <div className="px-3 py-2 mt-3 mb-4 rounded-v2-sm border border-v2-border-subtle bg-v2-muted text-[12px] text-v2-ink-secondary flex items-center gap-2">
          {React.createElement(activeTabMeta.icon, { size: 12 })}
          <span>{activeTabMeta.desc}</span>
        </div>
      )}

      {/* ═══ CONTENT ═══ */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px 40px' }}>

        {/* ── Error Banner ── */}
        {error && (
          <div style={{
            padding: '12px 16px', borderRadius: 10, marginBottom: 20,
            background: ACCENT.rose.bg, border: `1px solid ${ACCENT.rose.border}`,
            color: ACCENT.rose.text, fontSize: 13,
            display: 'flex', alignItems: 'center', gap: 8,
            animation: 'pcpSlideUp 0.3s ease',
          }}>
            <AlertTriangle size={16} /> {error}
            <button onClick={() => setError(null)} style={{ ...btnBase, marginLeft: 'auto', padding: 4, background: 'transparent', color: ACCENT.rose.text }}>
              <X size={14} />
            </button>
          </div>
        )}

        {activeTab === 'configurations' && (
          <>
            {/* ── Stat Cards Row ── */}
            <div style={{ display: 'flex', gap: 14, marginBottom: 24, animation: 'pcpFadeIn 0.3s ease' }}>
              <StatCard label="Total Skills" value={totalConfigs} subtitle="Policy configurations" icon={Layers} accent={ACCENT.sky} loading={loading} />
              <StatCard label="Active" value={activeCount} subtitle="Enforcing in production" icon={Zap} accent={ACCENT.emerald} loading={loading} />
              <StatCard label="Testing" value={testingCount} subtitle="Dry-run validation" icon={Activity} accent={ACCENT.amber} loading={loading} />
              <StatCard label="Total Rules" value={totalRules} subtitle={`${totalEvals.toLocaleString()} total evaluations`} icon={Lock} accent={ACCENT.violet} loading={loading} />
            </div>

            {/* ── Search & Filter Bar ── */}
            <div style={{
              display: 'flex', gap: 10, marginBottom: 20, alignItems: 'center',
              animation: 'pcpFadeIn 0.3s ease', animationDelay: '0.1s', animationFillMode: 'backwards',
            }}>
              <div style={{ position: 'relative', flex: 1, maxWidth: 360 }}>
                <Search size={14} style={{ position: 'absolute', left: 11, top: 11, color: S[400], pointerEvents: 'none' }} />
                <input
                  className="pcp-search"
                  placeholder="Search policy skills..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  style={{ ...inputBase, paddingLeft: 32 }}
                />
                {searchTerm && (
                  <button onClick={() => setSearchTerm('')} style={{
                    ...btnBase, position: 'absolute', right: 8, top: 8, padding: 3,
                    background: S[100], color: S[500], borderRadius: 4,
                  }}>
                    <X size={12} />
                  </button>
                )}
              </div>

              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                style={{ ...inputBase, width: 'auto', minWidth: 130, cursor: 'pointer', color: statusFilter === 'all' ? S[500] : S[900] }}
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="testing">Testing</option>
                <option value="draft">Draft</option>
                <option value="paused">Paused</option>
                <option value="archived">Archived</option>
              </select>

              {/* View mode toggles */}
              <div style={{ display: 'flex', border: `1px solid ${S[200]}`, borderRadius: 8, overflow: 'hidden', marginLeft: 'auto' }}>
                {[
                  { key: 'grid', icon: Layers },
                  { key: 'list', icon: Filter },
                ].map(v => (
                  <button key={v.key} onClick={() => setViewMode(v.key)} style={{
                    ...btnBase, padding: '7px 10px', borderRadius: 0,
                    background: viewMode === v.key ? S[100] : '#fff',
                    color: viewMode === v.key ? S[800] : S[400],
                    borderRight: v.key === 'grid' ? `1px solid ${S[200]}` : 'none',
                  }}>
                    <v.icon size={14} />
                  </button>
                ))}
              </div>

              {/* Results count */}
              {!loading && (
                <span style={{ fontSize: 11, color: S[400], whiteSpace: 'nowrap' }}>
                  {filtered.length} of {totalConfigs}
                </span>
              )}
            </div>

            {/* ── Configurations Content ── */}
            {loading ? (
              <LoadingSkeleton />
            ) : filtered.length === 0 && configs.length === 0 ? (
              <EmptyState onCreate={handleCreateNew} />
            ) : filtered.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '48px 24px', color: S[500] }}>
                <Search size={32} style={{ marginBottom: 12, opacity: 0.3 }} />
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4, color: S[700] }}>No matching skills</div>
                <div style={{ fontSize: 12, color: S[500] }}>
                  Try adjusting your search or filter criteria.
                </div>
                <button onClick={() => { setSearchTerm(''); setStatusFilter('all'); }} style={{
                  ...btnBase, marginTop: 12, padding: '6px 14px', fontSize: 12,
                  background: S[100], color: S[600], border: `1px solid ${S[200]}`,
                }}>
                  Clear Filters
                </button>
              </div>
            ) : viewMode === 'grid' ? (
              /* ── Grid View ── */
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
                gap: 14,
                animation: 'pcpFadeIn 0.3s ease', animationDelay: '0.15s', animationFillMode: 'backwards',
              }}>
                {filtered.map((config, i) => (
                  <div key={config.id} style={{ animation: 'pcpSlideUp 0.3s ease', animationDelay: `${i * 0.04}s`, animationFillMode: 'backwards' }}>
                    <PolicySkillCard config={config} onOpen={handleOpenConfig} onReview={(skillId) => setReviewSkillId(skillId)} />
                  </div>
                ))}
              </div>
            ) : (
              /* ── List View ── */
              <div style={{
                background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12, overflow: 'hidden',
                animation: 'pcpFadeIn 0.3s ease', animationDelay: '0.15s', animationFillMode: 'backwards',
              }}>
                {/* Table header */}
                <div style={{
                  display: 'grid', gridTemplateColumns: '1fr 100px 80px 80px 100px 100px 120px',
                  gap: 0, padding: '10px 20px', background: S[50], borderBottom: `1px solid ${S[200]}`,
                }}>
                  {['Skill Name', 'Status', 'Docs', 'Rules', 'Evaluations', 'Violations', ''].map(h => (
                    <div key={h} style={{
                      fontSize: 10, fontWeight: 700, color: S[400], textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                    }}>{h}</div>
                  ))}
                </div>
                {/* Table rows */}
                {filtered.map(config => {
                  const hasViolations = (config.totalViolations || 0) > 0;
                  return (
                    <div key={config.id} className="pcp-row" onClick={() => handleOpenConfig(config)} style={{
                      display: 'grid', gridTemplateColumns: '1fr 100px 80px 80px 100px 100px 120px',
                      gap: 0, padding: '12px 20px', borderBottom: `1px solid ${S[100]}`,
                      cursor: 'pointer', alignItems: 'center', transition: 'background 0.1s',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                        <Shield size={15} color={S[400]} />
                        <span style={{ fontSize: 13, fontWeight: 600, color: S[900], overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {config.skillName || `Skill ${config.skillId?.substring(0, 8)}...`}
                        </span>
                      </div>
                      <div><StatusBadge status={config.activationStatus} size="small" /></div>
                      <div style={{ fontSize: 13, color: S[600], fontWeight: 500, fontFeatureSettings: "'tnum'" }}>{config.documentIds?.length || 0}</div>
                      <div style={{ fontSize: 13, color: S[600], fontWeight: 500, fontFeatureSettings: "'tnum'" }}>{config.activeRuleCount || 0}</div>
                      <div style={{ fontSize: 13, color: S[600], fontWeight: 500, fontFeatureSettings: "'tnum'" }}>{(config.totalEvaluations || 0).toLocaleString()}</div>
                      <div style={{ fontSize: 13, fontWeight: 500, color: hasViolations ? ACCENT.rose.text : S[400], fontFeatureSettings: "'tnum'" }}>
                        {hasViolations ? config.totalViolations : '0'}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 4 }} onClick={e => e.stopPropagation()}>
                        {config.activationStatus === 'active' && (
                          <button className="pcp-action-btn" onClick={() => setReviewSkillId(config.skillId)} style={{
                            ...btnBase, padding: '4px 8px', fontSize: 10, fontWeight: 600,
                            background: S[50], color: S[500], borderRadius: 5,
                          }}>
                            <Eye size={10} /> Review
                          </button>
                        )}
                        <ChevronRight size={14} color={S[400]} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {activeTab === 'policy-library' && <PolicyLibraryPanel />}

        {activeTab === 'control-config' && <ControlConfigPanel />}

        {activeTab === 'framework-coverage' && <FrameworkCoveragePanel onOpenWizard={(wizardProps) => {
          setSelectedConfig(wizardProps?.config || null);
          setWizardPresets(wizardProps?.presets || null);
          setShowWizard(true);
        }} />}

        {activeTab === 'control-drift' && <ControlDriftPanel />}

        {activeTab === 'control-scorecard' && <ControlScorecardPanel />}

        {activeTab === 'classification' && <DataClassificationRulesPanel />}

        {activeTab === 'patterns' && <PatternLibraryPanel />}
      </div>
    </V2PageShell>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   POLICY LIBRARY PANEL — Credo.ai-inspired Framework & Controls Browser
   Hero stat cards, grid/list toggle, animated entries, hover micro-interactions
   ══════════════════════════════════════════════════════════════════════════════ */

const SECTOR_COLORS = {
  AI_SPECIFIC: { bg: '#ecfdf5', text: '#065f46', border: '#a7f3d0', icon: '🤖', label: 'AI-Specific' },
  FINANCE:     { bg: '#eff6ff', text: '#1e40af', border: '#bfdbfe', icon: '🏦', label: 'Finance' },
  INSURANCE:   { bg: '#fef3c7', text: '#92400e', border: '#fde68a', icon: '🛡️', label: 'Insurance' },
  HEALTHCARE:  { bg: '#fdf2f8', text: '#9d174d', border: '#fbcfe8', icon: '🏥', label: 'Healthcare' },
  SECURITY:    { bg: '#f0f9ff', text: '#0c4a6e', border: '#bae6fd', icon: '🔒', label: 'Security' },
  EMPLOYMENT:  { bg: '#f5f3ff', text: '#5b21b6', border: '#ddd6fe', icon: '👔', label: 'Employment' },
  GENERAL:     { bg: S[50],     text: S[600],    border: S[200],     icon: '📋', label: 'General' },
};

function PolicyLibraryPanel() {
  const [frameworks, setFrameworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sectorFilter, setSectorFilter] = useState('ALL');
  const [expandedFw, setExpandedFw] = useState(null);
  const [controls, setControls] = useState({});
  const [controlsLoading, setControlsLoading] = useState({});
  const [importing, setImporting] = useState(false);
  const [lastImportResult, setLastImportResult] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  // Add Framework modal state
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ name: '', type: '', description: '', enabled: true, autoGenerate: false });
  const [saving, setSaving] = useState(false);

  const fetchFrameworks = useCallback(async () => {
    try {
      const res = await api.ccListFrameworks();
      if (res.ok) setFrameworks(Array.isArray(res.data) ? res.data : res.data?.frameworks || []);
    } catch (_) { /* noop */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchFrameworks(); }, [fetchFrameworks]);

  const handleReimportAll = async () => {
    setImporting(true);
    try {
      const res = await api.ccReimportAllFrameworks();
      if (res.ok) {
        setLastImportResult(res.data);
        setControls({});
        await fetchFrameworks();
      }
    } catch (_) { /* noop */ }
    setImporting(false);
  };

  const loadControls = useCallback(async (fwId) => {
    if (controls[fwId]) return;
    setControlsLoading(prev => ({ ...prev, [fwId]: true }));
    try {
      const res = await api.ccGetFrameworkControls(fwId);
      if (res.ok) setControls(prev => ({ ...prev, [fwId]: Array.isArray(res.data) ? res.data : res.data?.controls || [] }));
    } catch (_) { /* noop */ }
    setControlsLoading(prev => ({ ...prev, [fwId]: false }));
  }, [controls]);

  const handleSaveFramework = async () => {
    if (!formData.name.trim() || !formData.type.trim()) return;
    setSaving(true);
    try {
      let res;
      if (editingId) {
        res = await api.updateComplianceFramework(editingId, formData);
      } else {
        res = await api.createComplianceFramework(formData);
      }
      if (res.ok) {
        setIsAddOpen(false);
        setEditingId(null);
        setFormData({ name: '', type: '', description: '', enabled: true, autoGenerate: false });
        await fetchFrameworks();
      }
    } catch (_) { /* noop */ }
    setSaving(false);
  };

  const handleDeleteFramework = async (id) => {
    if (!window.confirm('Delete this framework?')) return;
    try {
      const res = await api.deleteComplianceFramework(id);
      if (res.ok) await fetchFrameworks();
    } catch (_) { /* noop */ }
  };

  const openEditFramework = (fw) => {
    setEditingId(fw.id);
    setFormData({ name: fw.name || '', type: fw.type || fw.sector || '', description: fw.description || '', enabled: fw.enabled ?? true, autoGenerate: fw.autoGenerate ?? false });
    setIsAddOpen(true);
  };

  const filtered = frameworks.filter(f => {
    const matchSearch = (f.name || '').toLowerCase().includes(search.toLowerCase());
    const matchSector = sectorFilter === 'ALL' || f.sector === sectorFilter;
    return matchSearch && matchSector;
  });

  const grouped = Object.entries(
    filtered.reduce((acc, f) => {
      const sector = f.sector || 'GENERAL';
      if (!acc[sector]) acc[sector] = [];
      acc[sector].push(f);
      return acc;
    }, {})
  ).sort();

  const sectors = [...new Set(frameworks.map(f => f.sector || 'GENERAL'))].sort();
  const activeCount = frameworks.filter(f => f.enabled || f.isEnabled).length;
  const totalControls = frameworks.reduce((s, f) => s + (f.controlCount || 0), 0);

  if (loading) {
    return (
      <div style={{ padding: '48px 0', textAlign: 'center' }}>
        <Loader size={24} style={{ animation: 'pcpSpin 1s linear infinite', color: BRAND.primary, margin: '0 auto' }} />
        <div style={{ fontSize: 13, color: S[500], marginTop: 12 }}>Loading frameworks…</div>
      </div>
    );
  }

  return (
    <div style={{ animation: 'pcpFadeIn 0.3s ease' }}>
      {/* ── Hero Stat Cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 }}>
        {[
          { label: 'Total Frameworks', value: frameworks.length, accent: ACCENT.sky, Icon: Library },
          { label: 'Active', value: activeCount, accent: ACCENT.emerald, Icon: CheckCircle },
          { label: 'Total Controls', value: totalControls, accent: ACCENT.violet, Icon: Shield },
          { label: 'Sectors Covered', value: sectors.length, accent: ACCENT.amber, Icon: Globe2 },
        ].map((stat, i) => (
          <div key={stat.label} className="pcp-stat-card" style={{
            background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12,
            padding: '18px 20px', position: 'relative', overflow: 'hidden',
            animation: 'pcpSlideUp 0.3s ease', animationDelay: `${i * 0.06}s`, animationFillMode: 'backwards',
          }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: stat.accent.icon, opacity: 0.15 }} />
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <div>
                <div style={{
                  fontSize: 10, fontWeight: 700, color: stat.accent.text, textTransform: 'uppercase',
                  letterSpacing: '0.08em', marginBottom: 8,
                }}>{stat.label}</div>
                <div style={{ fontSize: 30, fontWeight: 800, color: S[900], lineHeight: 1, letterSpacing: '-0.03em', fontFeatureSettings: "'tnum'" }}>
                  {stat.value}
                </div>
              </div>
              <div style={{
                width: 36, height: 36, borderRadius: 10, background: stat.accent.bg,
                border: `1px solid ${stat.accent.border}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <stat.Icon size={18} style={{ color: stat.accent.icon }} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Toolbar: Search + Filter + View Toggle + Actions ── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, flexWrap: 'wrap',
      }}>
        {/* Search */}
        <div style={{ position: 'relative', flex: '1 1 260px', maxWidth: 360 }}>
          <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: S[400], pointerEvents: 'none' }} />
          <input
            className="pcp-search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search frameworks…"
            style={{ ...inputBase, paddingLeft: 34 }}
          />
          {search && (
            <button onClick={() => setSearch('')} style={{
              position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
              ...btnBase, padding: 4, background: S[100], color: S[500], borderRadius: 4,
            }}>
              <X size={12} />
            </button>
          )}
        </div>

        {/* Sector filter */}
        <select
          value={sectorFilter}
          onChange={e => setSectorFilter(e.target.value)}
          style={{ ...inputBase, width: 'auto', minWidth: 150, cursor: 'pointer', color: sectorFilter === 'ALL' ? S[500] : S[900] }}
        >
          <option value="ALL">All Sectors</option>
          {sectors.map(s => (
            <option key={s} value={s}>{SECTOR_COLORS[s]?.label || s}</option>
          ))}
        </select>

        {/* View toggle */}
        <div style={{ display: 'flex', border: `1px solid ${S[200]}`, borderRadius: 8, overflow: 'hidden', marginLeft: 'auto' }}>
          {[
            { key: 'grid', icon: LayoutGrid },
            { key: 'list', icon: List },
          ].map(v => (
            <button key={v.key} onClick={() => setViewMode(v.key)} style={{
              ...btnBase, padding: '7px 10px', borderRadius: 0,
              background: viewMode === v.key ? S[100] : '#fff',
              color: viewMode === v.key ? S[800] : S[400],
              borderRight: v.key === 'grid' ? `1px solid ${S[200]}` : 'none',
            }}>
              <v.icon size={14} />
            </button>
          ))}
        </div>

        {/* Actions */}
        <button className="pcp-btn-primary" onClick={handleReimportAll} disabled={importing} style={{
          ...btnBase, padding: '8px 16px', fontSize: 12, fontWeight: 600,
          background: importing ? S[300] : BRAND.gradient, color: '#fff', borderRadius: 8,
        }}>
          {importing ? <Loader size={14} style={{ animation: 'pcpSpin 1s linear infinite' }} /> : <RefreshCw size={14} />}
          {importing ? 'Importing…' : 'Re-Import All'}
        </button>

        <button className="pcp-btn-primary" onClick={() => { setEditingId(null); setFormData({ name: '', type: '', description: '', enabled: true, autoGenerate: false }); setIsAddOpen(true); }} style={{
          ...btnBase, padding: '8px 16px', fontSize: 12, fontWeight: 600,
          background: BRAND.gradient, color: '#fff', borderRadius: 8,
        }}>
          <Plus size={14} /> Add Framework
        </button>

        {/* Results count */}
        <span style={{ fontSize: 11, color: S[400], whiteSpace: 'nowrap' }}>
          {filtered.length} of {frameworks.length}
        </span>
      </div>

      {/* ── Import Result Banner ── */}
      {lastImportResult && (
        <div style={{
          marginBottom: 16, padding: '12px 16px', borderRadius: 10,
          background: ACCENT.emerald.bg, border: `1px solid ${ACCENT.emerald.border}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          animation: 'pcpSlideUp 0.25s ease',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <CheckCircle size={16} style={{ color: ACCENT.emerald.icon }} />
            <span style={{ fontSize: 13, color: ACCENT.emerald.text, fontWeight: 500 }}>
              {lastImportResult.successCount || 0} frameworks refreshed
              {(lastImportResult.failedCount || 0) > 0 && `, ${lastImportResult.failedCount} failed`}
            </span>
          </div>
          <button onClick={() => setLastImportResult(null)} style={{ ...btnBase, padding: 4, background: 'transparent', color: ACCENT.emerald.text }}>
            <X size={14} />
          </button>
        </div>
      )}

      {/* ── Content ── */}
      {grouped.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 24px', color: S[500] }}>
          <Library size={32} style={{ marginBottom: 12, opacity: 0.3 }} />
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4, color: S[700] }}>No frameworks found</div>
          <div style={{ fontSize: 12, color: S[500] }}>
            {search || sectorFilter !== 'ALL' ? 'Try adjusting your search or filter criteria.' : 'Import frameworks to get started.'}
          </div>
        </div>
      ) : viewMode === 'grid' ? (
        /* ── Grid View: Cards grouped by sector ── */
        <div>
          {grouped.map(([sector, sectorFrameworks], gi) => {
            const sc = SECTOR_COLORS[sector] || SECTOR_COLORS.GENERAL;
            return (
              <div key={sector} style={{ marginBottom: 28, animation: 'pcpSlideUp 0.3s ease', animationDelay: `${gi * 0.08}s`, animationFillMode: 'backwards' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <span style={{ fontSize: 16 }}>{sc.icon}</span>
                  <span style={{ fontSize: 11, fontWeight: 800, color: sc.text, textTransform: 'uppercase', letterSpacing: '0.1em' }}>{sc.label || sector}</span>
                  <span style={{ fontSize: 11, color: S[400], fontWeight: 500 }}>({sectorFrameworks.length})</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12 }}>
                  {sectorFrameworks.map((fw, fi) => {
                    const isActive = fw.enabled || fw.isEnabled;
                    const isExpanded = expandedFw === fw.id;
                    return (
                      <div key={fw.id} className="pcp-card" style={{
                        background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12, overflow: 'hidden',
                        animation: 'pcpSlideUp 0.3s ease', animationDelay: `${(gi * 0.08) + (fi * 0.04)}s`, animationFillMode: 'backwards',
                      }}>
                        <div style={{ height: 3, background: isActive ? ACCENT.emerald.icon : S[300], transition: 'background 0.3s' }} />
                        <div style={{ padding: '16px 18px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 700, fontSize: 14, color: S[900], lineHeight: 1.3, marginBottom: 4 }}>{fw.name}</div>
                              <div style={{ fontSize: 11, color: S[500] }}>
                                {fw.controlCount || 0} controls
                                {fw.lastImportedAt && ` · ${new Date(fw.lastImportedAt).toLocaleDateString()}`}
                              </div>
                            </div>
                            <span style={{
                              fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 4,
                              background: isActive ? ACCENT.emerald.bg : S[100],
                              color: isActive ? ACCENT.emerald.text : S[500],
                              border: `1px solid ${isActive ? ACCENT.emerald.border : S[200]}`,
                            }}>
                              {isActive ? '⚡ Active' : 'Inactive'}
                            </span>
                          </div>
                          {fw.sourceUrl && (
                            <a href={fw.sourceUrl} target="_blank" rel="noopener noreferrer"
                              onClick={e => e.stopPropagation()}
                              style={{ fontSize: 11, color: BRAND.primary, fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 4, textDecoration: 'none', marginBottom: 10 }}>
                              <ExternalLink size={10} /> Source Document
                            </a>
                          )}
                          <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
                            <button className="pcp-action-btn" onClick={() => openEditFramework(fw)} style={{
                              ...btnBase, padding: '4px 8px', fontSize: 10, fontWeight: 600,
                              background: S[50], color: S[500], borderRadius: 5,
                            }}>
                              <Settings size={10} /> Edit
                            </button>
                            <button className="pcp-action-btn" onClick={() => handleDeleteFramework(fw.id)} style={{
                              ...btnBase, padding: '4px 8px', fontSize: 10, fontWeight: 600,
                              background: ACCENT.rose.bg, color: ACCENT.rose.text, borderRadius: 5,
                            }}>
                              <Trash2 size={10} /> Delete
                            </button>
                          </div>
                          <button
                            onClick={() => { isExpanded ? setExpandedFw(null) : (setExpandedFw(fw.id), loadControls(fw.id)); }}
                            style={{
                              ...btnBase, width: '100%', justifyContent: 'center', padding: '6px 0', marginTop: 6,
                              background: S[50], color: S[600], borderRadius: 6, fontSize: 11, fontWeight: 600,
                            }}
                          >
                            {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                            {isExpanded ? 'Hide Controls' : `View ${fw.controlCount || 0} Controls`}
                          </button>
                        </div>
                        {isExpanded && (
                          <div style={{ padding: '0 18px 16px', borderTop: `1px solid ${S[100]}`, paddingTop: 12 }}>
                            {controlsLoading[fw.id] ? (
                              <div style={{ textAlign: 'center', padding: 16 }}>
                                <Loader size={16} style={{ animation: 'pcpSpin 1s linear infinite', color: BRAND.primary }} />
                              </div>
                            ) : (controls[fw.id] || []).length === 0 ? (
                              <div style={{ fontSize: 12, color: S[400], textAlign: 'center', padding: 12 }}>No controls loaded</div>
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 280, overflowY: 'auto' }}>
                                {(controls[fw.id] || []).map(c => (
                                  <div key={c.id} style={{
                                    padding: '8px 10px', background: S[25], borderRadius: 6,
                                    border: `1px solid ${S[100]}`, fontSize: 12,
                                  }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                                      <span style={{ fontWeight: 700, color: BRAND.primary, fontSize: 11 }}>{c.controlCode || c.code}</span>
                                      <span style={{ fontWeight: 600, color: S[800] }}>{c.controlName || c.name}</span>
                                    </div>
                                    <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                                      <span style={{
                                        fontSize: 9, fontWeight: 600, padding: '2px 6px', borderRadius: 3,
                                        background: ACCENT.sky.bg, color: ACCENT.sky.text, border: `1px solid ${ACCENT.sky.border}`,
                                      }}>{c.controlCategory || c.category}</span>
                                      {(c.automatable || c.isAutomatable) && (
                                        <span style={{
                                          fontSize: 9, fontWeight: 600, padding: '2px 6px', borderRadius: 3,
                                          background: ACCENT.emerald.bg, color: ACCENT.emerald.text, border: `1px solid ${ACCENT.emerald.border}`,
                                        }}>⚡ Automatable</span>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* ── List View: Dense table ── */
        <div style={{
          background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12, overflow: 'hidden',
          animation: 'pcpFadeIn 0.3s ease',
        }}>
          {/* Table header */}
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 100px 90px 120px 100px 80px',
            gap: 0, padding: '10px 20px', background: S[50], borderBottom: `1px solid ${S[200]}`,
          }}>
            {['Framework', 'Sector', 'Controls', 'Imported', 'Status', ''].map(h => (
              <div key={h} style={{
                fontSize: 10, fontWeight: 700, color: S[400], textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}>{h}</div>
            ))}
          </div>
          {/* Table rows */}
          {filtered.map((fw, i) => {
            const isActive = fw.enabled || fw.isEnabled;
            const sc = SECTOR_COLORS[fw.sector] || SECTOR_COLORS.GENERAL;
            return (
              <div key={fw.id} className="pcp-row" style={{
                display: 'grid', gridTemplateColumns: '1fr 100px 90px 120px 100px 80px',
                gap: 0, padding: '12px 20px', borderBottom: `1px solid ${S[100]}`,
                cursor: 'pointer', alignItems: 'center', transition: 'background 0.1s',
                animation: 'pcpSlideUp 0.2s ease', animationDelay: `${i * 0.02}s`, animationFillMode: 'backwards',
              }} onClick={() => { expandedFw === fw.id ? setExpandedFw(null) : (setExpandedFw(fw.id), loadControls(fw.id)); }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                  <Shield size={14} color={isActive ? ACCENT.emerald.icon : S[400]} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: S[900], overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {fw.name}
                  </span>
                </div>
                <div>
                  <span style={{
                    fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 3,
                    background: sc.bg, color: sc.text, border: `1px solid ${sc.border}`,
                  }}>{sc.label || fw.sector}</span>
                </div>
                <div style={{ fontSize: 13, color: S[600], fontWeight: 500, fontFeatureSettings: "'tnum'" }}>{fw.controlCount || 0}</div>
                <div style={{ fontSize: 12, color: S[500] }}>{fw.lastImportedAt ? new Date(fw.lastImportedAt).toLocaleDateString() : '—'}</div>
                <div>
                  <span style={{
                    fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 4,
                    background: isActive ? ACCENT.emerald.bg : S[100],
                    color: isActive ? ACCENT.emerald.text : S[500],
                    border: `1px solid ${isActive ? ACCENT.emerald.border : S[200]}`,
                  }}>{isActive ? '⚡ Active' : 'Inactive'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  {expandedFw === fw.id ? <ChevronUp size={14} color={S[400]} /> : <ChevronDown size={14} color={S[400]} />}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Add / Edit Framework Modal ── */}
      <Modal isOpen={isAddOpen} onClose={() => { setIsAddOpen(false); setEditingId(null); }} title={editingId ? 'Edit Framework' : 'Add Framework'}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: S[600], marginBottom: 6 }}>Name *</label>
            <input value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g. NIST AI Risk Management Framework"
              style={{ ...inputBase }} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: S[600], marginBottom: 6 }}>Type / Sector *</label>
            <select value={formData.type} onChange={e => setFormData({ ...formData, type: e.target.value })} style={{ ...inputBase }}>
              <option value="">-- Select --</option>
              {Object.entries(SECTOR_COLORS).map(([k, v]) => (
                <option key={k} value={k}>{v.icon} {v.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: S[600], marginBottom: 6 }}>Description</label>
            <textarea value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })}
              rows={3} placeholder="Brief description of this framework…"
              style={{ ...inputBase, resize: 'vertical' }} />
          </div>
          <div style={{ display: 'flex', gap: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: S[700], cursor: 'pointer' }}>
              <input type="checkbox" checked={formData.enabled} onChange={e => setFormData({ ...formData, enabled: e.target.checked })} />
              Enabled
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: S[700], cursor: 'pointer' }}>
              <input type="checkbox" checked={formData.autoGenerate} onChange={e => setFormData({ ...formData, autoGenerate: e.target.checked })} />
              Auto-generate controls
            </label>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button className="pcp-btn-primary" onClick={handleSaveFramework} disabled={saving || !formData.name.trim() || !formData.type} style={{
              ...btnBase, padding: '9px 20px', fontSize: 12, fontWeight: 600,
              background: (saving || !formData.name.trim() || !formData.type) ? S[300] : BRAND.gradient, color: '#fff', borderRadius: 8,
            }}>
              {saving ? 'Saving…' : editingId ? 'Update Framework' : 'Create Framework'}
            </button>
            <button className="pcp-btn-ghost" onClick={() => { setIsAddOpen(false); setEditingId(null); }} style={{
              ...btnBase, padding: '9px 16px', fontSize: 12, fontWeight: 600,
              background: 'transparent', color: S[600], borderRadius: 8,
            }}>
              Cancel
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   CONTROL CONFIG PANEL — Assign Framework Controls to Org / Tenant / Agent
   Credo.ai-inspired: hero stats, scope selector, assigned frameworks grid
   ══════════════════════════════════════════════════════════════════════════════ */

const COMPLIANCE_LEVELS = [
  { value: 'REQUIRED', label: 'Required', desc: 'Must be fully compliant — violations block deployment', accent: ACCENT.rose },
  { value: 'RECOMMENDED', label: 'Recommended', desc: 'Should be compliant — violations generate warnings', accent: ACCENT.amber },
  { value: 'OPTIONAL', label: 'Optional', desc: 'Nice to have — tracked for reporting only', accent: ACCENT.slate },
];

// ═══════════════════════════════════════════════════════════════════════════════
// CONTROL DRIFT PANEL — Detect when control assignments deviate from baselines
// ═══════════════════════════════════════════════════════════════════════════════

const DRIFT_SEVERITY_STYLES = {
  CRITICAL: { bg: '#fef2f2', text: '#991b1b', border: '#fecaca', icon: '#dc2626', label: 'Critical' },
  HIGH:     { bg: '#fff7ed', text: '#9a3412', border: '#fed7aa', icon: '#ea580c', label: 'High' },
  MEDIUM:   { bg: '#fffbeb', text: '#92400e', border: '#fde68a', icon: '#d97706', label: 'Medium' },
  LOW:      { bg: '#f0fdf4', text: '#166534', border: '#bbf7d0', icon: '#16a34a', label: 'Low' },
};

const DRIFT_STATUS_STYLES = {
  OPEN:         { bg: ACCENT.rose.bg, text: ACCENT.rose.text, border: ACCENT.rose.border, label: 'Open' },
  ACKNOWLEDGED: { bg: ACCENT.amber.bg, text: ACCENT.amber.text, border: ACCENT.amber.border, label: 'Acknowledged' },
  RESOLVED:     { bg: ACCENT.emerald.bg, text: ACCENT.emerald.text, border: ACCENT.emerald.border, label: 'Resolved' },
  SUPPRESSED:   { bg: '#f5f5f4', text: S[600], border: S[200], label: 'Suppressed' },
};

/* ═══════════════════════════════════════════════════════════════════════
   Framework Coverage Panel — Framework-to-PEP Bridge
   Auto-detects assigned frameworks from Control Config (ccListConfigs),
   shows scope context (Org/Tenant/Agent), surfaces agents without policy
   skills, and enables one-click skill generation from matching patterns.
   ═══════════════════════════════════════════════════════════════════════ */
const COVERAGE_STATUS = {
  COVERED:    { bg: ACCENT.emerald.bg, text: ACCENT.emerald.text, border: ACCENT.emerald.border, label: 'PEP Active', icon: '✓' },
  ATTESTED:   { bg: ACCENT.sky.bg, text: ACCENT.sky.text, border: ACCENT.sky.border, label: 'Attested', icon: '✓' },
  ATTEST_PENDING: { bg: '#fef3c7', text: '#92400e', border: '#fde68a', label: 'Attest Pending', icon: '◔' },
  PARTIAL:    { bg: ACCENT.amber.bg, text: ACCENT.amber.text, border: ACCENT.amber.border, label: 'Partial', icon: '◐' },
  GAP:        { bg: ACCENT.rose.bg, text: ACCENT.rose.text, border: ACCENT.rose.border, label: 'No PEP', icon: '!' },
};
const SCOPE_STYLES = {
  ORG:    { bg: '#eff6ff', text: '#1e40af', border: '#bfdbfe', label: 'Org-Level' },
  TENANT: { bg: '#faf5ff', text: '#6b21a8', border: '#e9d5ff', label: 'Tenant-Level' },
  AGENT:  { bg: '#f0fdf4', text: '#166534', border: '#bbf7d0', label: 'Agent-Level' },
};

function FrameworkCoveragePanel({ onOpenWizard }) {
  const { session, user: authUser } = useAuth();
  const [frameworks, setFrameworks] = useState([]);
  const [agents, setAgents] = useState([]);
  const [complianceConfigs, setComplianceConfigs] = useState([]); // effective compliance — actual assignments
  const [policyConfigs, setPolicyConfigs] = useState([]); // from getPolicyConfigs — active skill bindings
  const [patterns, setPatterns] = useState([]);
  // Unified coverage projection — single source of truth across all three rule-creation paths
  // (Control Config, Policy Skill wizard, Policy Library). Replaces per-framework client-side
  // gap computation. Shape: CoverageProjection DTO { frameworks[], agents[], ... }.
  const [coverage, setCoverage] = useState(null);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  // frameworkControls retained only for the attestation queue panel, which reads task-level
  // descriptive metadata (description, etc.) that the coverage projection does not surface.
  const [frameworkControls, setFrameworkControls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [coverageFilter, setCoverageFilter] = useState('all');
  const [agentViewFilter, setAgentViewFilter] = useState('all'); // 'all' | 'covered' | 'gap'

  // ── Dual-Path Bridge Gap + Attestation state ──
  const [bridgeGapModal, setBridgeGapModal] = useState(null); // { ctrl, targetAgentId, targetAgentName, scope }
  const [attestationPanel, setAttestationPanel] = useState(null); // { ctrl, targetAgentId, targetAgentName }
  const [attestationTasks, setAttestationTasks] = useState([]); // loaded attestation tasks for current assignment
  const [attestEvidence, setAttestEvidence] = useState({ notes: '', evidenceType: 'DOCUMENT', fileName: '', externalUrl: '', fileData: null });
  const [attestSelectedAgentId, setAttestSelectedAgentId] = useState('');
  const [attestProcessing, setAttestProcessing] = useState(false);
  const [attestError, setAttestError] = useState(null);
  // ── Attestation Queue Panel state ──
  const [showAttestQueue, setShowAttestQueue] = useState(false);
  const [attestQueueView, setAttestQueueView] = useState('MY_QUEUE'); // MY_QUEUE | REVIEW_QUEUE | HISTORY
  const [attestQueueItems, setAttestQueueItems] = useState([]);
  const [attestQueueLoading, setAttestQueueLoading] = useState(false);
  const [expandedQueueId, setExpandedQueueId] = useState(null);
  const [reviewingId, setReviewingId] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');

  // Reload everything — exposed as a callback so mutations (wizard completion, assignment
  // adds/removes, attestation review) can refresh the panel immediately. The coverage
  // projection is the critical fetch; others drive UI chrome (framework list, agent roster,
  // scope-level assignment rows, active skill bindings, wizard pattern presets).
  const loadAll = useCallback(async () => {
    const effectiveParams = {};
    if (session?.orgId) effectiveParams.orgId = session.orgId;
    if (session?.tenantId) effectiveParams.tenantId = session.tenantId;
    try {
      const [fwRes, agRes, ccRes, pcRes, patRes, covRes] = await Promise.all([
        api.ccListFrameworks({}),
        api.getAgents({}),
        api.ccGetEffectiveCompliance(effectiveParams),
        api.getPolicyConfigs('active'),
        api.getEnforcementPatterns(),
        api.ccGetCoverage(effectiveParams), // Unified projection across all three rule paths
      ]);
      if (fwRes.ok) setFrameworks(Array.isArray(fwRes.data) ? fwRes.data : fwRes.data?.frameworks || []);
      const agentListRaw = agRes.ok ? (Array.isArray(agRes.data) ? agRes.data : agRes.data?.agents || []) : [];
      // Exclude system agents — internal platform agents should not appear in compliance flows
      const agentList = agentListRaw.filter(a => !a.isSystemAgent && a.category !== 'system');
      setAgents(agentList);
      if (ccRes.ok) {
        const d = ccRes.data;
        setComplianceConfigs(Array.isArray(d) ? d : d?.frameworks || d?.configs || []);
      }
      if (pcRes.ok) setPolicyConfigs(Array.isArray(pcRes.data) ? pcRes.data : pcRes.data?.configs || pcRes.data?.configurations || []);
      if (patRes.ok) setPatterns(Array.isArray(patRes.data) ? patRes.data : patRes.data?.patterns || []);
      if (covRes.ok) setCoverage(covRes.data || null);
    } catch (_) {}
  }, [session?.orgId, session?.tenantId]);

  // Refetch just the coverage projection — cheaper than loadAll when only rule/binding
  // state changes (wizard complete, pattern toggle, policy-library assignment, attestation review).
  const refreshCoverage = useCallback(async () => {
    const effectiveParams = {};
    if (session?.orgId) effectiveParams.orgId = session.orgId;
    if (session?.tenantId) effectiveParams.tenantId = session.tenantId;
    try {
      const res = await api.ccGetCoverage(effectiveParams);
      if (res.ok) setCoverage(res.data || null);
    } catch (_) {}
  }, [session?.orgId, session?.tenantId]);

  // Initial load on mount + when org/tenant scope changes
  useEffect(() => {
    (async () => {
      setLoading(true);
      await loadAll();
      setLoading(false);
    })();
  }, [loadAll]);

  // Build the assignment map: which frameworks are assigned at which scope
  const assignmentMap = React.useMemo(() => {
    const map = [];
    complianceConfigs.forEach(cfg => {
      const fwId = cfg.frameworkId || cfg.id;
      const fw = frameworks.find(f => f.id === fwId);
      const fwName = cfg.name || cfg.frameworkName || fw?.name || fwId;
      const scope = cfg.scope || (cfg.agentId ? 'AGENT' : cfg.tenantId ? 'TENANT' : 'ORG');

      // Count agents affected by this assignment
      let affectedAgents = [];
      if (scope === 'AGENT') {
        affectedAgents = agents.filter(a => a.id === cfg.agentId);
      } else if (scope === 'TENANT') {
        affectedAgents = agents.filter(a => a.tenantId === cfg.tenantId);
      } else {
        affectedAgents = agents; // ORG-level: all agents inherit
      }

      // Check which affected agents have active policy skill bindings
      const agentsWithSkills = new Set();
      const agentSkillMap = new Map(); // agentId → array of skill names
      policyConfigs.forEach(pc => {
        if (pc.activationStatus === 'active' || pc.activationStatus === 'testing') {
          const boundAgents = pc.bindingConfig?.agentIds || [];
          const boundScope = pc.bindingConfig?.scope;
          const skillName = pc.skillName || pc.name || 'Policy Skill';
          if (boundScope === 'TENANT' || boundScope === 'ORG') {
            affectedAgents.forEach(a => {
              agentsWithSkills.add(a.id);
              if (!agentSkillMap.has(a.id)) agentSkillMap.set(a.id, []);
              agentSkillMap.get(a.id).push(skillName);
            });
          } else {
            boundAgents.forEach(id => {
              agentsWithSkills.add(id);
              if (!agentSkillMap.has(id)) agentSkillMap.set(id, []);
              agentSkillMap.get(id).push(skillName);
            });
          }
        }
      });

      // Build full agent list with coverage status
      const allAgentsWithStatus = affectedAgents.map(a => ({
        ...a,
        hasPolicySkill: agentsWithSkills.has(a.id),
        policySkillNames: agentSkillMap.get(a.id) || [],
      }));
      const unprotectedAgents = allAgentsWithStatus.filter(a => !a.hasPolicySkill);
      const protectedAgents = allAgentsWithStatus.filter(a => a.hasPolicySkill);

      map.push({
        id: cfg.id || `${fwId}-${scope}`,
        configId: cfg.configId || cfg.id, // ComplianceConfig.id for binding provenance
        frameworkId: fwId,
        frameworkName: fwName,
        frameworkVersion: cfg.version || fw?.version,
        controlCount: cfg.controlCount || fw?.controlCount,
        scope,
        orgId: cfg.orgId,
        tenantId: cfg.tenantId,
        agentId: cfg.agentId,
        totalAgents: affectedAgents.length,
        protectedAgents,
        protectedCount: protectedAgents.length,
        unprotectedAgents,
        allAgentsWithStatus,
        complianceLevel: cfg.complianceLevel,
        assignedAt: cfg.createdAt || cfg.assignedAt,
      });
    });
    return map;
  }, [complianceConfigs, frameworks, agents, policyConfigs]);

  // When an assignment is selected, load its raw controls list. The unified coverage
  // projection drives coverage status / rules / patterns / attestations — this fetch
  // only supplies descriptive metadata (e.g. full description string) used by the
  // attestation queue panel below, which cross-references task.controlId → control.
  const selAssign = assignmentMap.find(a => a.id === selectedAssignment);
  useEffect(() => {
    if (!selAssign) { setFrameworkControls([]); return; }
    (async () => {
      try {
        const ctrlRes = await api.ccGetFrameworkControls(selAssign.frameworkId);
        if (ctrlRes.ok) setFrameworkControls(Array.isArray(ctrlRes.data) ? ctrlRes.data : ctrlRes.data?.controls || []);
      } catch (_) {}
    })();
  }, [selAssign?.frameworkId]); // eslint-disable-line

  // ── Load attestation tasks for selected assignment (control-level evidence tracking) ──
  const refreshAttestationTasks = React.useCallback(async () => {
    if (!selAssign) { setAttestationTasks([]); return; }
    try {
      // Load ALL attestation tasks (no agent filter) so coverage works across agents/scopes
      // The attestationMap builder in coverageAnalysis matches by controlId regardless of agent
      const res = await api.ccListAttestations({});
      if (res.ok) {
        setAttestationTasks(Array.isArray(res.data) ? res.data : res.data?.content || []);
      }
    } catch (_) { /* attestation service may not be running */ }
  }, [selAssign]);

  useEffect(() => {
    refreshAttestationTasks();
  }, [selAssign?.configId]); // eslint-disable-line

  // Unified coverage analysis — reads directly from the backend CoverageProjection for the
  // selected framework. Status, rules, matching patterns, and attestations are all joined
  // server-side across the three rule-creation paths (Control Config, Policy Skill wizard,
  // Policy Library), so every mutation on any of the three surfaces flows through here.
  const coverageAnalysis = React.useMemo(() => {
    const empty = { controls: [], stats: { total: 0, covered: 0, partial: 0, gap: 0, attested: 0, attestPending: 0 } };
    if (!coverage || !selAssign) return empty;
    const fwCov = (coverage.frameworks || []).find(f => f.frameworkId === selAssign.frameworkId);
    if (!fwCov) return empty;

    const controls = (fwCov.controls || []).map(c => {
      const matchingPatterns = (c.matchingPatterns || []).map(p => ({
        id: p.patternId,
        patternId: p.patternId,
        patternName: p.patternName,
        domain: p.domain,
        mapsToRuleType: p.mapsToRuleType,
        enabled: p.enabled,
      }));
      const enforcementRules = c.rules || [];
      const enabledRules = enforcementRules.filter(r => r.enabled);
      return {
        id: c.controlId,
        controlId: c.controlCode,       // UI expects controlCode-style string at .controlId
        controlCode: c.controlCode,
        controlName: c.controlName,
        controlCategory: c.controlCategory,
        category: c.controlCategory,
        evidenceType: c.evidenceType,
        isAutomatable: c.isAutomatable,
        coverageStatus: c.status,       // COVERED | ATTESTED | PARTIAL | GAP | ATTEST_PENDING
        enforcementRuleCount: enabledRules.length,
        enforcementRules,
        matchingPatterns,
        attestations: c.attestations || [],
        isAttested: c.status === 'ATTESTED',
        isAttestPending: c.status === 'ATTEST_PENDING',
        requiresManualReview: c.evidenceType === 'MANUAL',
      };
    });

    const agg = fwCov.aggregates || {};
    return {
      controls,
      stats: {
        total: fwCov.totalControls ?? controls.length,
        covered: agg.coveredCount ?? 0,
        attested: agg.attestedCount ?? 0,
        attestPending: agg.attestPendingCount ?? 0,
        partial: agg.partialCount ?? 0,
        gap: agg.gapCount ?? 0,
      },
    };
  }, [coverage, selAssign]);

  const filteredControls = coverageAnalysis.controls.filter(c => {
    if (coverageFilter === 'gap') return c.coverageStatus === 'GAP' || c.coverageStatus === 'PARTIAL' || c.coverageStatus === 'ATTEST_PENDING';
    if (coverageFilter === 'covered') return c.coverageStatus === 'COVERED' || c.coverageStatus === 'ATTESTED';
    return true;
  });

  // Combined coverage: automated + attested as % of total
  const automatedPct = coverageAnalysis.stats.total > 0
    ? Math.round((coverageAnalysis.stats.covered / coverageAnalysis.stats.total) * 100) : 0;
  const attestedPct = coverageAnalysis.stats.total > 0
    ? Math.round((coverageAnalysis.stats.attested / coverageAnalysis.stats.total) * 100) : 0;
  const coveragePct = coverageAnalysis.stats.total > 0
    ? Math.round(((coverageAnalysis.stats.covered + coverageAnalysis.stats.attested) / coverageAnalysis.stats.total) * 100) : 0;

  // Total unprotected agents across all assignments
  const totalUnprotected = React.useMemo(() => {
    const uniqIds = new Set();
    assignmentMap.forEach(a => a.unprotectedAgents.forEach(ag => uniqIds.add(ag.id)));
    return uniqIds.size;
  }, [assignmentMap]);

  // Handle "Create Binding from Patterns" — opens wizard pre-configured for agent
  const handleCreateSkillFromPatterns = async (agentId, agentName, frameworkId, frameworkName, complianceConfigId, orgId, tenantId) => {
    // Step 1: Create FrameworkBinding (idempotent — returns existing if duplicate)
    let bindingId = null;
    try {
      const bindingRes = await api.ccCreateFrameworkBinding({
        complianceConfigId,
        frameworkId,
        agentId,
        agentName: agentName || 'Unknown Agent',
        orgId: orgId || session?.orgId || undefined,
        tenantId: tenantId || session?.tenantId || undefined,
      });
      if (bindingRes.ok && bindingRes.data) {
        bindingId = bindingRes.data.id;
      }
    } catch (e) {
      console.warn('[FrameworkBinding] Could not create binding, proceeding without provenance:', e);
    }

    // Step 2: Pre-select patterns matching the framework's domain
    const domainPatterns = patterns.filter(p => p.enabled !== false);
    if (onOpenWizard) {
      onOpenWizard({
        config: null,
        presets: {
          preSourceMode: 'patterns',
          preSelectedPatterns: domainPatterns.slice(0, 10),
          preBindingConfig: {
            scope: 'AGENT',
            enforcementPoints: ['ALL'],
            agentIds: agentId ? [agentId] : [],
          },
          // Provenance context — passed to wizard for state machine tracking
          frameworkBindingId: bindingId,
          frameworkId,
          frameworkName,
          agentName,
        },
      });
    }
  };

  // ── Resolve target agent from current assignment context ──
  const resolveTarget = useCallback(() => {
    if (!selAssign) return { targetAgentId: null, targetAgentName: null, scope: 'ORG' };
    let targetAgentId = selAssign.agentId || null;
    let targetAgentName = null;
    if (!targetAgentId && selAssign.unprotectedAgents?.length > 0) {
      targetAgentId = selAssign.unprotectedAgents[0].id;
      targetAgentName = selAssign.unprotectedAgents[0].name;
    } else if (!targetAgentId && selAssign.allAgentsWithStatus?.length > 0) {
      targetAgentId = selAssign.allAgentsWithStatus[0].id;
      targetAgentName = selAssign.allAgentsWithStatus[0].name;
    }
    const scope = targetAgentId ? 'AGENT' : selAssign.tenantId ? 'TENANT' : 'ORG';
    return { targetAgentId, targetAgentName, scope };
  }, [selAssign]);

  /** Collect all agents in scope for this assignment (for agent selector) */
  const scopeAgents = React.useMemo(() => {
    if (!selAssign) return [];
    // Agent-level assignment → single agent
    if (selAssign.agentId) {
      const a = agents.find(ag => ag.id === selAssign.agentId);
      return a ? [a] : [];
    }
    // Org/tenant-level → all agents (unprotected first, then protected)
    const unprotected = selAssign.unprotectedAgents || [];
    const allWithStatus = selAssign.allAgentsWithStatus || [];
    const seen = new Set();
    const result = [];
    [...unprotected, ...allWithStatus].forEach(a => {
      if (!seen.has(a.id)) { seen.add(a.id); result.push(a); }
    });
    return result.length > 0 ? result : agents;
  }, [selAssign, agents]);

  /**
   * Bridge Gap: Dual-path dispatcher.
   * - If control is MANUAL-only → opens attestation panel directly (no choice modal)
   * - If control is AUTOMATED-only → opens Policy Skill wizard directly
   * - If control is HYBRID or undetermined → opens dual-path choice modal
   */
  const handleBridgeGap = (ctrl) => {
    if (!selAssign) return;
    const { targetAgentId, targetAgentName, scope } = resolveTarget();
    const evType = ctrl.evidenceType || (ctrl.isAutomatable !== false ? 'AUTOMATED' : 'MANUAL');

    if (evType === 'MANUAL') {
      // Direct to attestation — pass null agent so user picks from dropdown
      setAttestationPanel({ ctrl, targetAgentId: null, targetAgentName: null });
      return;
    }
    if (evType === 'AUTOMATED' && ctrl.matchingPatterns?.length > 0) {
      // Direct to Policy Skill wizard — fully automatable
      handleAutomatePath(ctrl, targetAgentId, targetAgentName, scope);
      return;
    }
    // HYBRID or AUTOMATED without patterns — show dual-path choice
    setBridgeGapModal({ ctrl, targetAgentId, targetAgentName, scope });
  };

  /** Path A: Automate with Policy Skill (existing wizard flow) */
  const handleAutomatePath = async (ctrl, targetAgentId, targetAgentName, scope) => {
    setBridgeGapModal(null);
    const controlPatterns = ctrl.matchingPatterns?.length > 0
      ? ctrl.matchingPatterns
      : patterns.filter(p => p.enabled !== false).slice(0, 5);

    let bindingId = null;
    if (targetAgentId) {
      try {
        const bindingRes = await api.ccCreateFrameworkBinding({
          complianceConfigId: selAssign.configId,
          frameworkId: selAssign.frameworkId,
          agentId: targetAgentId,
          agentName: targetAgentName || 'Unknown Agent',
          orgId: selAssign.orgId || session?.orgId || undefined,
          tenantId: selAssign.tenantId || session?.tenantId || undefined,
        });
        if (bindingRes.ok && bindingRes.data) bindingId = bindingRes.data.id;
      } catch (e) {
        console.warn('[FrameworkBinding] Bridge Gap automate: could not create binding:', e);
      }
    }

    if (onOpenWizard) {
      onOpenWizard({
        config: null,
        presets: {
          preSourceMode: 'patterns',
          preSelectedPatterns: controlPatterns,
          preBindingConfig: { scope, enforcementPoints: ['ALL'], agentIds: targetAgentId ? [targetAgentId] : [] },
          frameworkBindingId: bindingId,
          frameworkId: selAssign.frameworkId,
          frameworkName: selAssign.frameworkName,
          agentName: targetAgentName,
        },
      });
    }
  };

  /** Path B: Manual Attestation — open inline attestation panel */
  const handleAttestPath = (ctrl, targetAgentId, targetAgentName) => {
    setBridgeGapModal(null);
    setAttestEvidence({ notes: '', evidenceType: 'DOCUMENT', fileName: '', externalUrl: '', fileData: null });
    setAttestError(null);
    setAttestationPanel({ ctrl, targetAgentId: null, targetAgentName: null }); // let user pick agent
  };

  /**
   * Path C: Activate via Framework Controls (unified pipeline / Phase 4 handoff)
   *
   * Opens the wizard with sourceMode='framework' and pre-selects only the
   * given control. Used for two cases the legacy paths can't cover well:
   *
   *  1. AUTOMATABLE controls that have no matching pattern template in the
   *     library — previously a dead-end ("No matching pattern"). The
   *     unified pipeline turns the framework control itself into a rule
   *     candidate, so the gap can be remediated without authoring a pattern.
   *
   *  2. HYBRID controls where the operator wants framework-traceable
   *     bindings (rather than going through the pattern library).
   *
   * Provenance: idempotent FrameworkBinding.create + frameworkBindingId
   * threaded through the wizard so Stage 5 commits an immutable
   * PolicyAssignmentActivation snapshot tied to this gap.
   */
  const handleFrameworkPath = async (ctrl) => {
    if (!selAssign) return;
    setBridgeGapModal(null);
    const { targetAgentId, targetAgentName, scope } = resolveTarget();

    let bindingId = null;
    if (targetAgentId) {
      try {
        const bindingRes = await api.ccCreateFrameworkBinding({
          complianceConfigId: selAssign.configId,
          frameworkId: selAssign.frameworkId,
          agentId: targetAgentId,
          agentName: targetAgentName || 'Unknown Agent',
          orgId: selAssign.orgId || session?.orgId || undefined,
          tenantId: selAssign.tenantId || session?.tenantId || undefined,
        });
        if (bindingRes.ok && bindingRes.data) bindingId = bindingRes.data.id;
      } catch (e) {
        console.warn('[FrameworkBinding] Framework path: could not create binding (continuing without provenance):', e);
      }
    }

    if (onOpenWizard) {
      onOpenWizard({
        config: null,
        presets: {
          preSourceMode: 'framework',
          preBindingConfig: {
            scope,
            enforcementPoints: [], // wizard's adaptive PEP picker fills this in Stage 4
            agentIds: targetAgentId ? [targetAgentId] : [],
          },
          frameworkBindingId: bindingId,
          frameworkId: selAssign.frameworkId,
          frameworkName: selAssign.frameworkName,
          agentName: targetAgentName,
          // ctrl.id is the canonical control UUID — wizard auto-selects
          // the candidate(s) extracted from this control after Stage 2.
          preSelectedFrameworkControlIds: ctrl.id ? [ctrl.id] : [],
        },
      });
    }
  };

  /** Convert file to base64 for upload */
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAttestEvidence(prev => ({ ...prev, fileName: file.name }));
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result?.split(',')[1] || '';
      setAttestEvidence(prev => ({ ...prev, fileData: base64 }));
    };
    reader.readAsDataURL(file);
  };

  /** Submit attestation: create task + upload evidence */
  // ── Attestation Queue handlers ──
  const loadAttestQueue = async (view) => {
    const v = view || attestQueueView;
    setAttestQueueLoading(true);
    try {
      let items = [];
      if (v === 'REVIEW_QUEUE') {
        const res = await api.ccGetReviewQueue();
        if (res.ok) items = Array.isArray(res.data) ? res.data : res.data?.content || [];
      } else if (v === 'MY_QUEUE') {
        // Fetch tasks assigned to the current user via the my-queue endpoint
        const res = await api.ccGetMyAttestationQueue();
        if (res.ok) items = Array.isArray(res.data) ? res.data : res.data?.content || [];
      } else if (v === 'HISTORY') {
        // History = all completed/terminal tasks (APPROVED, REJECTED, CANCELLED, EXPIRED)
        const [approvedRes, rejectedRes, cancelledRes, expiredRes] = await Promise.all([
          api.ccListAttestations({ status: 'APPROVED' }),
          api.ccListAttestations({ status: 'REJECTED' }),
          api.ccListAttestations({ status: 'CANCELLED' }),
          api.ccListAttestations({ status: 'EXPIRED' }),
        ]);
        const merge = (res) => res.ok ? (Array.isArray(res.data) ? res.data : res.data?.content || []) : [];
        items = [...merge(approvedRes), ...merge(rejectedRes), ...merge(cancelledRes), ...merge(expiredRes)];
        // Sort by most recently updated first
        items.sort((a, b) => {
          const da = a.reviewedAt || a.updatedAt || a.createdAt || '';
          const db = b.reviewedAt || b.updatedAt || b.createdAt || '';
          return db.localeCompare(da);
        });
      }
      setAttestQueueItems(items);
    } catch (_) { setAttestQueueItems([]); }
    setAttestQueueLoading(false);
  };

  const handleOpenAttestQueue = () => {
    setShowAttestQueue(true);
    loadAttestQueue();
  };

  const handleAttestQueueViewChange = (v) => {
    setAttestQueueView(v);
    setExpandedQueueId(null);
    loadAttestQueue(v);
  };

  const handleReviewAttestation = async (taskId, approved) => {
    setReviewingId(taskId);
    try {
      const res = await api.ccReviewAttestation(taskId, {
        approved,
        notes: reviewNotes,
        feedback: approved ? 'Evidence verified and approved.' : reviewNotes || 'Evidence insufficient — please re-submit.',
      });
      if (res.ok) {
        setReviewNotes('');
        setExpandedQueueId(null);
        loadAttestQueue(); // refresh current view
        // Refresh coverage projection — review may advance a control from
        // ATTEST_PENDING → ATTESTED (approved) or back to GAP (rejected).
        refreshAttestationTasks();
        refreshCoverage();
      }
    } catch (e) { console.error('Review failed:', e); }
    setReviewingId(null);
  };

  const handleSubmitAttestation = async () => {
    if (!attestationPanel) return;
    const { ctrl } = attestationPanel;
    const agentId = attestSelectedAgentId || attestationPanel.targetAgentId;
    if (!agentId) {
      setAttestError('Please select an agent to attest for.');
      return;
    }
    setAttestProcessing(true);
    setAttestError(null);
    try {
      // Step 1: Create attestation task (pass frameworkId so backend can resolve it)
      const createRes = await api.ccCreateAttestation({
        agentId,
        controlId: ctrl.id,
        frameworkId: selAssign?.frameworkId || undefined,
        assignedTo: session?.userId || undefined,
        dueAt: null, // backend defaults to 30 days
      });
      if (!createRes.ok) throw new Error(createRes.error || 'Failed to create attestation task');

      // Use taskId (actual DB UUID) instead of workflowId (Temporal OSS workflow string)
      const taskId = createRes.data?.taskId || createRes.data?.id;

      if (!taskId) throw new Error('Attestation task was created but task ID not returned. Please try again.');

      // Step 2: Upload evidence (file or metadata)
      if (attestEvidence.notes || attestEvidence.fileName || attestEvidence.externalUrl || attestEvidence.fileData) {
        const evidencePayload = {
          fileName: attestEvidence.fileName || 'manual-attestation',
          evidenceType: attestEvidence.evidenceType,
          description: attestEvidence.notes,
        };
        if (attestEvidence.externalUrl) evidencePayload.externalUrl = attestEvidence.externalUrl;
        if (attestEvidence.fileData) evidencePayload.contentBase64 = attestEvidence.fileData;
        await api.ccUploadEvidence(taskId, evidencePayload);
      }

      // Step 3: Submit for review
      await api.ccSubmitAttestation(taskId, {
        evidence: { notes: attestEvidence.notes, type: attestEvidence.evidenceType },
        notes: attestEvidence.notes,
      });

      // Refresh attestation tasks AND the unified coverage projection —
      // submit should flip the control from GAP → ATTEST_PENDING immediately.
      refreshAttestationTasks();
      refreshCoverage();

      setAttestationPanel(null);
      setAttestSelectedAgentId('');
    } catch (e) {
      setAttestError(e.message);
    } finally {
      setAttestProcessing(false);
    }
  };

  if (loading) return (
    <div style={{ padding: '48px 0', textAlign: 'center' }}>
      <Loader size={24} style={{ animation: 'pcpSpin 1s linear infinite', color: BRAND.primary, margin: '0 auto' }} />
      <div style={{ fontSize: 13, color: S[500], marginTop: 12 }}>Loading framework coverage analysis...</div>
    </div>
  );

  return (
    <div style={{ animation: 'pcpFadeIn 0.3s ease' }}>
      {/* Hero Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 }}>
        {[
          { label: 'Assigned Frameworks', value: assignmentMap.length, sub: `Across ${new Set(assignmentMap.map(a => a.frameworkId)).size} unique framework(s)`, accent: ACCENT.sky, Icon: Shield },
          { label: 'Total Agents', value: agents.length, sub: 'Within assigned scopes', accent: ACCENT.violet, Icon: Bot },
          { label: 'Unprotected Agents', value: totalUnprotected, sub: 'Framework assigned, no Policy Skill PEP', accent: ACCENT.rose, Icon: AlertTriangle },
          { label: 'PEP Coverage', value: selectedAssignment ? `${coveragePct}%` : '—', sub: selectedAssignment ? `${coverageAnalysis.stats.covered}/${coverageAnalysis.stats.total} controls` : 'Select an assignment', accent: ACCENT.emerald, Icon: CheckCircle },
        ].map((stat, i) => (
          <div key={stat.label} className="pcp-stat-card" style={{
            background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12,
            padding: '18px 20px', position: 'relative', overflow: 'hidden',
            animation: 'pcpSlideUp 0.3s ease', animationDelay: `${i * 0.06}s`, animationFillMode: 'backwards',
          }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: stat.accent.icon, opacity: 0.15 }} />
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: stat.accent.text || S[500], textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>{stat.label}</div>
                <div style={{ fontSize: 30, fontWeight: 800, color: S[900], lineHeight: 1, letterSpacing: '-0.03em', fontFeatureSettings: "'tnum'" }}>{stat.value}</div>
                <div style={{ fontSize: 11, color: S[500], marginTop: 6 }}>{stat.sub}</div>
              </div>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: stat.accent.bg, border: `1px solid ${stat.accent.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <stat.Icon size={18} style={{ color: stat.accent.icon }} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Attestation Queue Action Bar ── */}
      <div style={{
        display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 14,
      }}>
        <button onClick={handleOpenAttestQueue} style={{
          ...btnBase, padding: '8px 16px', fontSize: 12, fontWeight: 600,
          borderRadius: 8, background: '#fff', color: ACCENT.violet.text,
          border: `1px solid ${ACCENT.violet.border}`,
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        }}>
          <ClipboardCheck size={14} /> Attestation Queue
          {attestationTasks.filter(t => ['EVIDENCE_SUBMITTED','UNDER_REVIEW','CREATED','ASSIGNED'].includes(t.status)).length > 0 && (
            <span style={{
              marginLeft: 6, background: '#ef4444', color: '#fff', fontSize: 10, fontWeight: 700,
              padding: '1px 6px', borderRadius: 10, minWidth: 18, textAlign: 'center', display: 'inline-block',
            }}>
              {attestationTasks.filter(t => ['EVIDENCE_SUBMITTED','UNDER_REVIEW','CREATED','ASSIGNED'].includes(t.status)).length}
            </span>
          )}
        </button>
      </div>

      {/* ── Assigned Frameworks (auto-detected from Control Config) ── */}
      <div style={{
        background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12,
        marginBottom: 20, overflow: 'hidden',
      }}>
        <div style={{ padding: '14px 20px', borderBottom: `1px solid ${S[200]}`, background: S[50], display: 'flex', alignItems: 'center', gap: 10 }}>
          <Layers size={15} color={S[600]} />
          <span style={{ fontSize: 13, fontWeight: 700, color: S[700] }}>Assigned Frameworks</span>
          <span style={{ fontSize: 11, color: S[400] }}>Auto-detected from Control Config assignments</span>
        </div>

        {assignmentMap.length === 0 && (
          <div style={{ textAlign: 'center', padding: '36px 20px' }}>
            <Layers size={36} color={S[300]} style={{ marginBottom: 12 }} />
            <div style={{ fontSize: 14, fontWeight: 600, color: S[600], marginBottom: 6 }}>No Framework Assignments Detected</div>
            <div style={{ fontSize: 12, color: S[500], maxWidth: 420, margin: '0 auto' }}>
              Go to the Control Config tab and assign a framework (SOC 2, GDPR, HIPAA, etc.) at the Org, Tenant, or Agent level first.
              Assignments will appear here automatically.
            </div>
          </div>
        )}

        {assignmentMap.map((assign, i) => {
          const isActive = selectedAssignment === assign.id;
          const sc = SCOPE_STYLES[assign.scope] || SCOPE_STYLES.ORG;
          const hasGap = assign.unprotectedAgents.length > 0;
          return (
            <div key={assign.id} onClick={() => setSelectedAssignment(isActive ? null : assign.id)} style={{
              display: 'grid', gridTemplateColumns: '1fr auto auto auto auto',
              gap: 14, alignItems: 'center', padding: '14px 20px',
              borderBottom: `1px solid ${S[100]}`, cursor: 'pointer',
              background: isActive ? BRAND.light + '30' : 'transparent',
              transition: 'background 0.15s',
              animation: 'pcpFadeIn 0.2s ease', animationDelay: `${i * 0.04}s`, animationFillMode: 'backwards',
            }}>
              {/* Framework + Scope */}
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: S[900] }}>
                    {assign.frameworkName}
                  </span>
                  {assign.frameworkVersion && (
                    <span style={{ fontSize: 10, color: S[400], fontFamily: "'IBM Plex Mono', monospace" }}>v{assign.frameworkVersion}</span>
                  )}
                  <span style={{
                    padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700,
                    background: sc.bg, color: sc.text, border: `1px solid ${sc.border}`,
                  }}>{sc.label}</span>
                </div>
                <div style={{ fontSize: 11, color: S[500] }}>
                  {assign.controlCount && <span>{assign.controlCount} controls · </span>}
                  {assign.scope === 'ORG' && 'Inherited by all tenants and agents in this organization'}
                  {assign.scope === 'TENANT' && `Inherited by all agents in tenant ${(assign.tenantId || '').substring(0, 8)}…`}
                  {assign.scope === 'AGENT' && `Directly assigned to agent ${(assign.agentId || '').substring(0, 8)}…`}
                </div>
              </div>

              {/* Covered agents */}
              <div style={{
                textAlign: 'center', padding: '6px 14px', borderRadius: 8,
                background: ACCENT.emerald.bg, border: `1px solid ${ACCENT.emerald.border}`,
              }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: ACCENT.emerald.icon }}>{assign.protectedCount}</div>
                <div style={{ fontSize: 9, fontWeight: 600, color: ACCENT.emerald.text }}>COVERED</div>
              </div>

              {/* Unprotected Count */}
              <div style={{
                textAlign: 'center', padding: '6px 14px', borderRadius: 8,
                background: hasGap ? ACCENT.rose.bg : ACCENT.emerald.bg,
                border: `1px solid ${hasGap ? ACCENT.rose.border : ACCENT.emerald.border}`,
              }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: hasGap ? ACCENT.rose.icon : ACCENT.emerald.icon }}>
                  {assign.unprotectedAgents.length}
                </div>
                <div style={{ fontSize: 9, fontWeight: 600, color: hasGap ? ACCENT.rose.text : ACCENT.emerald.text }}>
                  {hasGap ? 'NO PEP' : 'ALL CLEAR'}
                </div>
              </div>

              {/* Total */}
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: S[900] }}>{assign.totalAgents}</div>
                <div style={{ fontSize: 10, color: S[500] }}>Total</div>
              </div>

              {/* Expand indicator */}
              <ChevronRight size={16} color={S[400]} style={{
                transform: isActive ? 'rotate(90deg)' : 'rotate(0)', transition: 'transform 0.15s',
              }} />
            </div>
          );
        })}
      </div>

      {/* ── Agent Coverage Detail (when assignment selected) — shows ALL agents ── */}
      {selAssign && selAssign.allAgentsWithStatus.length > 0 && (
        <div style={{
          background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12,
          marginBottom: 20, overflow: 'hidden',
          animation: 'pcpSlideUp 0.2s ease',
        }}>
          {/* Header with filter tabs */}
          <div style={{ padding: '12px 20px', borderBottom: `1px solid ${S[200]}`, background: S[50], display: 'flex', alignItems: 'center', gap: 10 }}>
            <Bot size={15} color={S[600]} />
            <span style={{ fontSize: 13, fontWeight: 700, color: S[700] }}>
              Agent Coverage — {selAssign.frameworkName}
            </span>
            <div style={{ flex: 1 }} />
            <div style={{ display: 'flex', gap: 4 }}>
              {[
                { key: 'all', label: `All (${selAssign.allAgentsWithStatus.length})`, accent: null },
                { key: 'covered', label: `Covered (${selAssign.protectedCount})`, accent: ACCENT.emerald },
                { key: 'gap', label: `No PEP (${selAssign.unprotectedAgents.length})`, accent: ACCENT.rose },
              ].map(f => (
                <button key={f.key} onClick={(e) => { e.stopPropagation(); setAgentViewFilter(f.key); }} className="pcp-btn-ghost" style={{
                  ...btnBase, padding: '4px 10px', fontSize: 10, fontWeight: 600, borderRadius: 5,
                  background: agentViewFilter === f.key ? (f.accent ? f.accent.bg : BRAND.light) : S[50],
                  color: agentViewFilter === f.key ? (f.accent ? f.accent.text : BRAND.dark) : S[500],
                  border: `1px solid ${agentViewFilter === f.key ? (f.accent ? f.accent.border : BRAND.primary + '40') : S[200]}`,
                }}>{f.label}</button>
              ))}
            </div>
          </div>

          <div style={{ maxHeight: 360, overflowY: 'auto' }}>
            {selAssign.allAgentsWithStatus
              .filter(ag => {
                if (agentViewFilter === 'covered') return ag.hasPolicySkill;
                if (agentViewFilter === 'gap') return !ag.hasPolicySkill;
                return true;
              })
              .map((ag, i) => (
              <div key={ag.id} className="pcp-row" style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '10px 20px',
                borderBottom: `1px solid ${S[100]}`,
                borderLeft: `3px solid ${ag.hasPolicySkill ? ACCENT.emerald.icon : ACCENT.rose.icon}`,
              }}>
                <Bot size={16} color={ag.hasPolicySkill ? ACCENT.emerald.icon : S[500]} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: S[900] }}>
                    {ag.name || `Agent ${ag.id.substring(0, 8)}…`}
                  </div>
                  <div style={{ fontSize: 11, color: S[500] }}>
                    {ag.status && <span>{ag.status}</span>}
                    {ag.environment && <span> · {ag.environment}</span>}
                    {ag.agentType && <span> · {ag.agentType}</span>}
                    {ag.hasPolicySkill && ag.policySkillNames.length > 0 && (
                      <span style={{ color: ACCENT.emerald.text }}> · {ag.policySkillNames.join(', ')}</span>
                    )}
                  </div>
                </div>

                {/* Coverage status badge */}
                <span style={{
                  padding: '3px 10px', borderRadius: 4, fontSize: 10, fontWeight: 700,
                  background: ag.hasPolicySkill ? ACCENT.emerald.bg : ACCENT.rose.bg,
                  color: ag.hasPolicySkill ? ACCENT.emerald.text : ACCENT.rose.text,
                  border: `1px solid ${ag.hasPolicySkill ? ACCENT.emerald.border : ACCENT.rose.border}`,
                }}>
                  {ag.hasPolicySkill ? (
                    <><CheckCircle size={10} style={{ verticalAlign: -1, marginRight: 3 }} />PEP Active</>
                  ) : 'No Policy Skill'}
                </span>

                {/* Action buttons */}
                {!ag.hasPolicySkill && (
                  <button
                    className="pcp-btn-ghost"
                    onClick={(e) => { e.stopPropagation(); handleCreateSkillFromPatterns(ag.id, ag.name, selAssign.frameworkId, selAssign.frameworkName, selAssign.configId, selAssign.orgId, selAssign.tenantId); }}
                    style={{
                      ...btnBase, padding: '5px 12px', fontSize: 11, fontWeight: 600,
                      borderRadius: 6, background: BRAND.light, color: BRAND.dark,
                      border: `1px solid ${BRAND.primary}30`,
                    }}
                  >
                    <Zap size={11} /> Create Binding from Patterns
                  </button>
                )}
                {ag.hasPolicySkill && (
                  <button
                    className="pcp-btn-ghost"
                    onClick={(e) => { e.stopPropagation(); handleCreateSkillFromPatterns(ag.id, ag.name, selAssign.frameworkId, selAssign.frameworkName, selAssign.configId, selAssign.orgId, selAssign.tenantId); }}
                    style={{
                      ...btnBase, padding: '5px 12px', fontSize: 11, fontWeight: 600,
                      borderRadius: 6, background: ACCENT.emerald.bg, color: ACCENT.emerald.text,
                      border: `1px solid ${ACCENT.emerald.border}`,
                    }}
                  >
                    <Plus size={11} /> Add Rules from Patterns
                  </button>
                )}
              </div>
            ))}
            {selAssign.allAgentsWithStatus.filter(ag => {
              if (agentViewFilter === 'covered') return ag.hasPolicySkill;
              if (agentViewFilter === 'gap') return !ag.hasPolicySkill;
              return true;
            }).length === 0 && (
              <div style={{ textAlign: 'center', padding: 32, color: S[400], fontSize: 13 }}>
                {agentViewFilter === 'covered' ? 'No agents with active PEP enforcement yet' :
                  agentViewFilter === 'gap' ? 'All agents have active enforcement — no gaps!' : 'No agents found'}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Control-Level Coverage (when assignment selected) ── */}
      {selAssign && (
        <>
          {/* Filter toolbar */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14,
            padding: '10px 18px', background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 10,
          }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: S[700] }}>
              {selAssign.frameworkName} Controls
            </span>
            <div style={{ flex: 1 }} />
            <div style={{ display: 'flex', gap: 6 }}>
              {[
                { key: 'all', label: 'All Controls' },
                { key: 'gap', label: 'Gaps Only' },
                { key: 'covered', label: 'Covered Only' },
              ].map(f => (
                <button key={f.key} onClick={() => setCoverageFilter(f.key)} className="pcp-btn-ghost" style={{
                  ...btnBase, padding: '5px 12px', fontSize: 11, fontWeight: 600, borderRadius: 6,
                  background: coverageFilter === f.key ? BRAND.primary : S[50],
                  color: coverageFilter === f.key ? '#fff' : S[600],
                  border: `1px solid ${coverageFilter === f.key ? BRAND.primary : S[200]}`,
                }}>{f.label}</button>
              ))}
            </div>
          </div>

          <div style={{ background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12, overflow: 'hidden' }}>
            {/* Progress Bar */}
            <div style={{ padding: '14px 20px', borderBottom: `1px solid ${S[200]}`, background: S[50] }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: S[700] }}>PEP Enforcement Coverage</span>
                <span style={{ fontSize: 22, fontWeight: 800, color: coveragePct >= 80 ? ACCENT.emerald.icon : coveragePct >= 50 ? ACCENT.amber.icon : ACCENT.rose.icon }}>
                  {coveragePct}%
                </span>
              </div>
              <div style={{ height: 8, borderRadius: 4, background: S[200], overflow: 'hidden', display: 'flex' }}>
                <div style={{
                  height: '100%', transition: 'width 0.5s ease',
                  width: `${automatedPct}%`,
                  background: ACCENT.sky.icon,
                  borderRadius: attestedPct > 0 ? '4px 0 0 4px' : 4,
                }} />
                <div style={{
                  height: '100%', transition: 'width 0.5s ease',
                  width: `${attestedPct}%`,
                  background: ACCENT.emerald.icon,
                  borderRadius: automatedPct > 0 ? '0 4px 4px 0' : 4,
                }} />
              </div>
              {/* G06: Automated vs Attestation Coverage split — live scoring */}
              {selAssign && (
                <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
                  <div style={{ flex: 1, padding: '6px 10px', borderRadius: 6, background: ACCENT.sky.bg, border: `1px solid ${ACCENT.sky.border}` }}>
                    <div style={{ fontSize: 9, fontWeight: 700, color: ACCENT.sky.text, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Automated</div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: ACCENT.sky.icon, fontFeatureSettings: "'tnum'" }}>
                      {automatedPct}%
                    </div>
                    <div style={{ fontSize: 9, color: ACCENT.sky.text, opacity: 0.7, marginTop: 2 }}>
                      {coverageAnalysis.stats.covered}/{coverageAnalysis.stats.total} controls
                    </div>
                  </div>
                  <div style={{ flex: 1, padding: '6px 10px', borderRadius: 6, background: ACCENT.emerald.bg, border: `1px solid ${ACCENT.emerald.border}` }}>
                    <div style={{ fontSize: 9, fontWeight: 700, color: ACCENT.emerald.text, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Attested</div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: ACCENT.emerald.icon, fontFeatureSettings: "'tnum'" }}>
                      {attestedPct}%
                    </div>
                    <div style={{ fontSize: 9, color: ACCENT.emerald.text, opacity: 0.7, marginTop: 2 }}>
                      {coverageAnalysis.stats.attested}/{coverageAnalysis.stats.total} controls
                    </div>
                  </div>
                  {coverageAnalysis.stats.attestPending > 0 && (
                    <div style={{ flex: 1, padding: '6px 10px', borderRadius: 6, background: ACCENT.amber.bg, border: `1px solid ${ACCENT.amber.border}` }}>
                      <div style={{ fontSize: 9, fontWeight: 700, color: ACCENT.amber.text, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Pending Review</div>
                      <div style={{ fontSize: 16, fontWeight: 800, color: ACCENT.amber.icon, fontFeatureSettings: "'tnum'" }}>
                        {coverageAnalysis.stats.attestPending}
                      </div>
                      <div style={{ fontSize: 9, color: ACCENT.amber.text, opacity: 0.7, marginTop: 2 }}>
                        awaiting approval
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Control List */}
            <div style={{ maxHeight: 460, overflowY: 'auto' }}>
              {filteredControls.map((ctrl, i) => {
                const cs = COVERAGE_STATUS[ctrl.coverageStatus] || COVERAGE_STATUS.GAP;
                const evType = ctrl.evidenceType || 'AUTOMATED';
                return (
                  <div key={ctrl.id || i} style={{
                    display: 'grid', gridTemplateColumns: '100px 1fr auto auto',
                    gap: 14, alignItems: 'center', padding: '14px 20px',
                    borderBottom: `1px solid ${S[100]}`,
                    animation: 'pcpFadeIn 0.2s ease', animationDelay: `${i * 0.02}s`, animationFillMode: 'backwards',
                  }}>
                    {/* Status Badge */}
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '4px 10px', borderRadius: 6, fontSize: 10, fontWeight: 700,
                      background: cs.bg, color: cs.text, border: `1px solid ${cs.border}`, whiteSpace: 'nowrap',
                    }}>
                      {cs.icon} {cs.label}
                    </span>

                    {/* Control Info + Evidence Type indicator */}
                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: S[900] }}>
                          {ctrl.controlCode || ctrl.controlId || ctrl.name || '—'} — {ctrl.controlName || ctrl.title || ctrl.description?.substring(0, 80) || 'Control'}
                        </span>
                        {/* Evidence type micro-badge */}
                        <span style={{
                          padding: '1px 6px', borderRadius: 3, fontSize: 8, fontWeight: 700, textTransform: 'uppercase',
                          letterSpacing: '0.06em',
                          background: evType === 'MANUAL' ? ACCENT.violet.bg : evType === 'HYBRID' ? '#fef3c7' : ACCENT.sky.bg,
                          color: evType === 'MANUAL' ? ACCENT.violet.text : evType === 'HYBRID' ? '#92400e' : ACCENT.sky.text,
                          border: `1px solid ${evType === 'MANUAL' ? ACCENT.violet.border : evType === 'HYBRID' ? '#fde68a' : ACCENT.sky.border}`,
                        }}>
                          {evType === 'MANUAL' ? 'Manual' : evType === 'HYBRID' ? 'Hybrid' : 'Auto'}
                        </span>
                      </div>
                      <div style={{ fontSize: 11, color: S[500] }}>
                        {ctrl.controlCategory || ctrl.category || ctrl.domain || ''}
                        {ctrl.enforcementRuleCount > 0 && <> · {ctrl.enforcementRuleCount} rule(s)</>}
                        {ctrl.isAttested && <> · <span style={{ color: ACCENT.emerald.icon }}>Evidence approved</span></>}
                        {ctrl.isAttestPending && <> · <span style={{ color: ACCENT.amber.icon }}>Evidence under review</span></>}
                      </div>
                    </div>

                    {/* Pattern tags (only for automatable) */}
                    {ctrl.isAutomatable && ctrl.matchingPatterns.length > 0 && (
                      <div style={{ display: 'flex', gap: 4 }}>
                        {ctrl.matchingPatterns.slice(0, 2).map(p => (
                          <span key={p.id} style={{
                            padding: '3px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                            background: ACCENT.violet.bg, color: ACCENT.violet.text,
                            border: `1px solid ${ACCENT.violet.border}`, whiteSpace: 'nowrap',
                          }}>
                            {p.patternName.length > 20 ? p.patternName.substring(0, 18) + '...' : p.patternName}
                          </span>
                        ))}
                        {ctrl.matchingPatterns.length > 2 && (
                          <span style={{ fontSize: 10, color: S[400], alignSelf: 'center' }}>+{ctrl.matchingPatterns.length - 2}</span>
                        )}
                      </div>
                    )}
                    {!ctrl.isAutomatable && ctrl.matchingPatterns.length === 0 && <div />}

                    {/* ── Action Buttons (dual-path aware) ── */}
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      {/* GAP: Manual-only → "Attest" button */}
                      {ctrl.coverageStatus === 'GAP' && evType === 'MANUAL' && (
                        <button className="pcp-btn-ghost" onClick={() => handleBridgeGap(ctrl)} style={{
                          ...btnBase, padding: '5px 12px', fontSize: 11, fontWeight: 600,
                          borderRadius: 6, background: ACCENT.violet.bg, color: ACCENT.violet.text, border: `1px solid ${ACCENT.violet.border}`,
                        }}><FileText size={11} /> Upload Evidence</button>
                      )}

                      {/* GAP: Automatable with patterns → "Create Binding" */}
                      {ctrl.coverageStatus === 'GAP' && evType === 'AUTOMATED' && ctrl.matchingPatterns.length > 0 && (
                        <button className="pcp-btn-ghost" onClick={() => handleBridgeGap(ctrl)} style={{
                          ...btnBase, padding: '5px 12px', fontSize: 11, fontWeight: 600,
                          borderRadius: 6, background: BRAND.light, color: BRAND.dark, border: `1px solid ${BRAND.primary}30`,
                        }}><Zap size={11} /> Create Binding</button>
                      )}

                      {/* GAP: Automatable without patterns → "Open in Wizard" via unified pipeline.
                          Spec: docs/UNIFIED_POLICY_PIPELINE_SPEC.md (Phase 4). Previously this was
                          a dead-end ("No matching pattern"); the framework-source mode lets us bind
                          the control directly without requiring a pattern template. */}
                      {ctrl.coverageStatus === 'GAP' && evType === 'AUTOMATED' && ctrl.matchingPatterns.length === 0 && (
                        <button className="pcp-btn-ghost" onClick={() => handleFrameworkPath(ctrl)} style={{
                          ...btnBase, padding: '5px 12px', fontSize: 11, fontWeight: 600,
                          borderRadius: 6, background: '#eff6ff', color: '#1e40af', border: '1px solid #bfdbfe',
                        }}><Shield size={11} /> Open in Wizard</button>
                      )}

                      {/* GAP: Hybrid → "Bridge Gap" (opens dual-path modal) */}
                      {ctrl.coverageStatus === 'GAP' && evType === 'HYBRID' && (
                        <button className="pcp-btn-ghost" onClick={() => handleBridgeGap(ctrl)} style={{
                          ...btnBase, padding: '5px 12px', fontSize: 11, fontWeight: 600,
                          borderRadius: 6, background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a',
                        }}><Layers size={11} /> Bridge Gap</button>
                      )}

                      {/* COVERED: Active enforcement */}
                      {ctrl.coverageStatus === 'COVERED' && (
                        <span style={{ fontSize: 10, color: ACCENT.emerald.icon, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3 }}>
                          <CheckCircle size={12} />Active
                        </span>
                      )}

                      {/* ATTESTED: Evidence approved */}
                      {ctrl.coverageStatus === 'ATTESTED' && (
                        <span style={{ fontSize: 10, color: ACCENT.sky.icon, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3 }}>
                          <ShieldCheck size={12} />Attested
                        </span>
                      )}

                      {/* ATTEST_PENDING: Evidence under review */}
                      {ctrl.coverageStatus === 'ATTEST_PENDING' && (
                        <span style={{ fontSize: 10, color: ACCENT.amber.icon, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3 }}>
                          <Clock size={12} />Under Review
                        </span>
                      )}

                      {/* PARTIAL: Bridge Gap (opens appropriate path) */}
                      {ctrl.coverageStatus === 'PARTIAL' && (
                        <button className="pcp-btn-ghost" onClick={() => handleBridgeGap(ctrl)} style={{
                          ...btnBase, padding: '5px 12px', fontSize: 11, fontWeight: 600,
                          borderRadius: 6, background: ACCENT.amber.bg, color: ACCENT.amber.text, border: `1px solid ${ACCENT.amber.border}`,
                        }}><ArrowUpRight size={11} /> Bridge Gap</button>
                      )}
                    </div>
                  </div>
                );
              })}
              {filteredControls.length === 0 && (
                <div style={{ textAlign: 'center', padding: 32, color: S[400], fontSize: 13 }}>
                  {coverageFilter === 'gap' ? 'No coverage gaps — all controls have active enforcement or attestation' :
                    coverageFilter === 'covered' ? 'No fully covered controls yet' : 'No controls found for this framework'}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* ═══ Dual-Path Bridge Gap Choice Modal ═══ */}
      {bridgeGapModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
          animation: 'pcpFadeIn 0.2s ease',
        }} onClick={() => setBridgeGapModal(null)}>
          <div style={{
            background: '#fff', borderRadius: 16, padding: 32, maxWidth: 520, width: '90%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)', animation: 'pcpSlideUp 0.3s ease',
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: S[900], marginBottom: 4 }}>Bridge Coverage Gap</div>
                <div style={{ fontSize: 12, color: S[500] }}>
                  {bridgeGapModal.ctrl.controlCode} — {bridgeGapModal.ctrl.controlName || bridgeGapModal.ctrl.title}
                </div>
              </div>
              <button onClick={() => setBridgeGapModal(null)} style={{ ...btnBase, padding: 6, background: S[100], borderRadius: 6, color: S[500] }}>
                <X size={14} />
              </button>
            </div>

            <div style={{ fontSize: 12, color: S[600], marginBottom: 20, lineHeight: 1.6 }}>
              This control supports both automated enforcement and manual attestation. Choose how to satisfy this requirement:
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {/* Path A: Automate */}
              {bridgeGapModal.ctrl.matchingPatterns?.length > 0 && (
                <button onClick={() => handleAutomatePath(bridgeGapModal.ctrl, bridgeGapModal.targetAgentId, bridgeGapModal.targetAgentName, bridgeGapModal.scope)} style={{
                  ...btnBase, padding: '16px 20px', borderRadius: 12, textAlign: 'left',
                  background: ACCENT.sky.bg, border: `1px solid ${ACCENT.sky.border}`, width: '100%',
                  flexDirection: 'column', alignItems: 'flex-start', gap: 8,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Zap size={16} style={{ color: ACCENT.sky.icon }} />
                    <span style={{ fontSize: 14, fontWeight: 700, color: ACCENT.sky.text }}>Automate with Policy Skill</span>
                  </div>
                  <div style={{ fontSize: 11, color: ACCENT.sky.text, opacity: 0.8, lineHeight: 1.5 }}>
                    Create deterministic enforcement rules from matching patterns. Real-time automated enforcement at the PEP layer.
                    <br /><span style={{ fontWeight: 600 }}>{bridgeGapModal.ctrl.matchingPatterns.length} matching pattern(s) available</span>
                  </div>
                </button>
              )}

              {/* Path B: Attest */}
              <button onClick={() => handleAttestPath(bridgeGapModal.ctrl, bridgeGapModal.targetAgentId, bridgeGapModal.targetAgentName)} style={{
                ...btnBase, padding: '16px 20px', borderRadius: 12, textAlign: 'left',
                background: ACCENT.violet.bg, border: `1px solid ${ACCENT.violet.border}`, width: '100%',
                flexDirection: 'column', alignItems: 'flex-start', gap: 8,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FileText size={16} style={{ color: ACCENT.violet.icon }} />
                  <span style={{ fontSize: 14, fontWeight: 700, color: ACCENT.violet.text }}>Manual Attestation</span>
                </div>
                <div style={{ fontSize: 11, color: ACCENT.violet.text, opacity: 0.8, lineHeight: 1.5 }}>
                  Upload evidence of compliance (documents, screenshots, links). Assigned for review and approval.
                  Counts toward attestation coverage score.
                </div>
              </button>

              {/* Path C: Open in Wizard via unified pipeline.
                  Spec: docs/UNIFIED_POLICY_PIPELINE_SPEC.md (Phase 4).
                  Bind the framework control directly without needing a pattern
                  — the wizard's framework-source mode pulls automatable controls
                  as rule candidates and manual controls as attestation tasks. */}
              <button onClick={() => handleFrameworkPath(bridgeGapModal.ctrl)} style={{
                ...btnBase, padding: '16px 20px', borderRadius: 12, textAlign: 'left',
                background: '#eff6ff', border: '1px solid #bfdbfe', width: '100%',
                flexDirection: 'column', alignItems: 'flex-start', gap: 8,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Shield size={16} style={{ color: '#1e40af' }} />
                  <span style={{ fontSize: 14, fontWeight: 700, color: '#1e40af' }}>Open in Wizard (Framework Controls)</span>
                </div>
                <div style={{ fontSize: 11, color: '#1e40af', opacity: 0.85, lineHeight: 1.5 }}>
                  Bind this control through the unified policy assignment pipeline. Pre-selected
                  for review with adaptive PEPs and an immutable audit snapshot on activate.
                  No pattern template required.
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Attestation Evidence Panel (inline slide-up) ═══ */}
      {attestationPanel && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
          animation: 'pcpFadeIn 0.2s ease',
        }} onClick={() => { setAttestationPanel(null); setAttestError(null); setAttestSelectedAgentId(''); }}>
          <div style={{
            background: '#fff', borderRadius: 16, padding: 0, maxWidth: 580, width: '90%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)', animation: 'pcpSlideUp 0.3s ease', overflow: 'hidden',
          }} onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div style={{ padding: '20px 24px', borderBottom: `1px solid ${S[200]}`, background: ACCENT.violet.bg }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <FileText size={18} style={{ color: ACCENT.violet.icon }} />
                    <span style={{ fontSize: 15, fontWeight: 700, color: S[900] }}>Upload Compliance Evidence</span>
                  </div>
                  <div style={{ fontSize: 12, color: S[600] }}>
                    {attestationPanel.ctrl.controlCode} — {attestationPanel.ctrl.controlName || attestationPanel.ctrl.title}
                  </div>
                </div>
                <button onClick={() => { setAttestationPanel(null); setAttestError(null); setAttestSelectedAgentId(''); }} style={{ ...btnBase, padding: 6, background: S[100], borderRadius: 6, color: S[500] }}>
                  <X size={14} />
                </button>
              </div>
            </div>

            {/* Body */}
            <div style={{ padding: '20px 24px', maxHeight: '70vh', overflowY: 'auto' }}>
              {/* Agent Selector */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: S[600], textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
                  Attest for Agent <span style={{ color: ACCENT.rose.icon }}>*</span>
                </label>
                <select
                  value={attestSelectedAgentId}
                  onChange={e => setAttestSelectedAgentId(e.target.value)}
                  style={{ ...inputBase, width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${S[200]}`, fontSize: 12, appearance: 'none',
                    backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath d=\'M6 8L1 3h10z\' fill=\'%2378716c\'/%3E%3C/svg%3E")',
                    backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center', paddingRight: 28,
                  }}
                >
                  <option value="">— Select an agent —</option>
                  {scopeAgents.map(a => (
                    <option key={a.id} value={a.id}>{a.name}{a.isExternal ? ' [EXT]' : ''}</option>
                  ))}
                </select>
              </div>

              {/* Evidence Type Selector */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: S[600], textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                  Evidence Type
                </label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {[
                    { key: 'DOCUMENT', label: 'Document', icon: FileText },
                    { key: 'SCREENSHOT', label: 'Screenshot', icon: Eye },
                    { key: 'LINK', label: 'External Link', icon: ExternalLink },
                    { key: 'FORM', label: 'Attestation Form', icon: FileCheck },
                  ].map(et => (
                    <button key={et.key} onClick={() => setAttestEvidence(prev => ({ ...prev, evidenceType: et.key, fileData: null, fileName: '', externalUrl: '' }))} style={{
                      ...btnBase, padding: '6px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                      background: attestEvidence.evidenceType === et.key ? ACCENT.violet.bg : S[50],
                      color: attestEvidence.evidenceType === et.key ? ACCENT.violet.text : S[600],
                      border: `1px solid ${attestEvidence.evidenceType === et.key ? ACCENT.violet.border : S[200]}`,
                    }}>
                      <et.icon size={12} /> {et.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* External URL (for LINK type) */}
              {attestEvidence.evidenceType === 'LINK' && (
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: S[600], textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
                    Evidence URL
                  </label>
                  <input
                    type="url"
                    value={attestEvidence.externalUrl}
                    onChange={e => setAttestEvidence(prev => ({ ...prev, externalUrl: e.target.value }))}
                    placeholder="https://confluence.example.com/data-processing-register"
                    style={{ ...inputBase, width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${S[200]}`, fontSize: 12 }}
                  />
                </div>
              )}

              {/* File Upload (for DOCUMENT/SCREENSHOT) */}
              {(attestEvidence.evidenceType === 'DOCUMENT' || attestEvidence.evidenceType === 'SCREENSHOT') && (
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: S[600], textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
                    Upload Evidence File
                  </label>
                  <div style={{
                    border: `2px dashed ${attestEvidence.fileData ? ACCENT.emerald.border : S[300]}`,
                    borderRadius: 10, padding: '16px 20px', textAlign: 'center',
                    background: attestEvidence.fileData ? ACCENT.emerald.bg : S[50],
                    cursor: 'pointer', transition: 'all 0.15s ease',
                  }}>
                    <input
                      type="file"
                      accept={attestEvidence.evidenceType === 'SCREENSHOT' ? 'image/*' : '.pdf,.doc,.docx,.xlsx,.csv,.txt,.png,.jpg,.jpeg'}
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                      id="attest-file-upload"
                    />
                    <label htmlFor="attest-file-upload" style={{ cursor: 'pointer', display: 'block' }}>
                      {attestEvidence.fileData ? (
                        <div>
                          <CheckCircle size={20} style={{ color: ACCENT.emerald.icon, marginBottom: 4 }} />
                          <div style={{ fontSize: 12, fontWeight: 600, color: ACCENT.emerald.text }}>{attestEvidence.fileName}</div>
                          <div style={{ fontSize: 10, color: ACCENT.emerald.text, opacity: 0.7, marginTop: 2 }}>Click to replace</div>
                        </div>
                      ) : (
                        <div>
                          <ArrowUpRight size={20} style={{ color: S[400], marginBottom: 4 }} />
                          <div style={{ fontSize: 12, fontWeight: 600, color: S[600] }}>
                            Click to upload {attestEvidence.evidenceType === 'SCREENSHOT' ? 'screenshot' : 'document'}
                          </div>
                          <div style={{ fontSize: 10, color: S[400], marginTop: 2 }}>
                            {attestEvidence.evidenceType === 'SCREENSHOT' ? 'PNG, JPG, JPEG' : 'PDF, DOCX, XLSX, CSV, TXT, images'}
                          </div>
                        </div>
                      )}
                    </label>
                  </div>
                </div>
              )}

              {/* Evidence Notes */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: S[600], textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
                  Evidence Notes
                </label>
                <textarea
                  value={attestEvidence.notes}
                  onChange={e => setAttestEvidence(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Describe how this evidence demonstrates compliance with this control..."
                  rows={4}
                  style={{ ...inputBase, width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${S[200]}`, fontSize: 12, resize: 'vertical', fontFamily: 'inherit' }}
                />
              </div>

              {/* Remediation guidance hint */}
              {attestationPanel.ctrl.remediationGuidance && (
                <div style={{ padding: '10px 14px', borderRadius: 8, background: ACCENT.sky.bg, border: `1px solid ${ACCENT.sky.border}`, marginBottom: 16 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: ACCENT.sky.text, textTransform: 'uppercase', marginBottom: 4 }}>Compliance Guidance</div>
                  <div style={{ fontSize: 11, color: ACCENT.sky.text, lineHeight: 1.5 }}>
                    {attestationPanel.ctrl.remediationGuidance}
                  </div>
                </div>
              )}

              {/* Error */}
              {attestError && (
                <div style={{ padding: '8px 14px', borderRadius: 8, background: ACCENT.rose.bg, border: `1px solid ${ACCENT.rose.border}`, marginBottom: 16, fontSize: 12, color: ACCENT.rose.text }}>
                  <AlertTriangle size={12} style={{ verticalAlign: -2, marginRight: 4 }} />{attestError}
                </div>
              )}

              {/* Actions */}
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button onClick={() => { setAttestationPanel(null); setAttestError(null); }} style={{
                  ...btnBase, padding: '8px 16px', fontSize: 12, fontWeight: 600,
                  borderRadius: 8, background: S[50], color: S[600], border: `1px solid ${S[200]}`,
                }}>Cancel</button>
                <button onClick={handleSubmitAttestation} disabled={attestProcessing || !attestSelectedAgentId || (!attestEvidence.notes && !attestEvidence.externalUrl && !attestEvidence.fileData)} style={{
                  ...btnBase, padding: '8px 20px', fontSize: 12, fontWeight: 700,
                  borderRadius: 8, background: BRAND.gradient, color: '#fff', border: 'none',
                  boxShadow: '0 1px 4px rgba(226,74,58,0.2)',
                  opacity: (attestProcessing || !attestSelectedAgentId || (!attestEvidence.notes && !attestEvidence.externalUrl && !attestEvidence.fileData)) ? 0.5 : 1,
                }}>
                  {attestProcessing ? <><Loader size={12} style={{ animation: 'pcpSpin 1s linear infinite' }} /> Submitting...</> : <><ShieldCheck size={12} /> Submit Evidence & Create Attestation</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Full Attestation Queue Panel (My Queue / Review / History) ═══ */}
      {showAttestQueue && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)',
          zIndex: 10000, display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
          paddingTop: 40,
        }} onClick={() => setShowAttestQueue(false)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: '#fff', borderRadius: 16, width: 860, maxHeight: 'calc(100vh - 80px)',
            overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
            border: `1px solid ${S[200]}`,
          }}>
            {/* ── Header ── */}
            <div style={{
              padding: '20px 24px', borderBottom: `1px solid ${S[200]}`, background: S[50],
              borderRadius: '16px 16px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 12, background: ACCENT.violet.bg,
                  border: `1px solid ${ACCENT.violet.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <ClipboardCheck size={20} style={{ color: ACCENT.violet.icon }} />
                </div>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: S[800] }}>Attestation Queue</div>
                  <div style={{ fontSize: 11, color: S[500] }}>Manage attestation tasks, submit evidence, and review compliance</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button onClick={() => loadAttestQueue()} style={{ ...btnBase, padding: '6px 12px', fontSize: 11, borderRadius: 6, background: S[50], border: `1px solid ${S[200]}`, color: S[600] }}>
                  <RefreshCw size={12} /> Refresh
                </button>
                <button onClick={() => setShowAttestQueue(false)} style={{ ...btnBase, padding: '6px 8px', borderRadius: 6, background: 'transparent', border: 'none', color: S[400] }}>
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* ── Sub-View Tabs ── */}
            <div style={{ padding: '16px 24px 0' }}>
              <div style={{ display: 'flex', gap: 2, background: S[100], borderRadius: 10, padding: 3, marginBottom: 16 }}>
                {[
                  { key: 'MY_QUEUE', label: 'My Queue', Icon: Inbox },
                  { key: 'REVIEW_QUEUE', label: 'Review Queue', Icon: Eye },
                  { key: 'HISTORY', label: 'History', Icon: Clock },
                ].map(v => {
                  const isActive = attestQueueView === v.key;
                  return (
                    <button key={v.key} onClick={() => handleAttestQueueViewChange(v.key)} style={{
                      ...btnBase, flex: 1, padding: '8px 16px', borderRadius: 8, fontSize: 12, fontWeight: isActive ? 700 : 500,
                      background: isActive ? '#fff' : 'transparent',
                      color: isActive ? BRAND.primary : S[500],
                      boxShadow: isActive ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                    }}>
                      <v.Icon size={13} /> {v.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ── Body ── */}
            <div style={{ padding: '0 24px 20px' }}>
              {attestQueueLoading ? (
                <div style={{ textAlign: 'center', padding: 48 }}>
                  <Loader size={22} style={{ animation: 'pcpSpin 1s linear infinite', color: BRAND.primary }} />
                  <div style={{ fontSize: 12, color: S[500], marginTop: 8 }}>Loading attestation queue...</div>
                </div>
              ) : attestQueueItems.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 56, color: S[400] }}>
                  <div style={{
                    width: 56, height: 56, borderRadius: 16, background: S[100],
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    margin: '0 auto 16px',
                  }}>
                    <ClipboardCheck size={24} style={{ color: S[400] }} />
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: S[700], marginBottom: 6 }}>No attestations</div>
                  <div style={{ fontSize: 12, color: S[500] }}>
                    {attestQueueView === 'MY_QUEUE' ? 'No attestation tasks assigned to you.'
                      : attestQueueView === 'REVIEW_QUEUE' ? 'No attestations pending review. All evidence has been processed.'
                      : 'No completed attestation history yet.'}
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {attestQueueItems.map(task => {
                    const isExpanded = expandedQueueId === task.id;
                    const control = frameworkControls.find(c => c.id === task.controlId);
                    const agent = agents.find(a => a.id === task.agentId);
                    const isOverdue = task.dueDate && new Date(task.dueDate) < new Date() && !['APPROVED','REJECTED','CANCELLED','EXPIRED'].includes(task.status);
                    const statusAccent = {
                      CREATED: ACCENT.amber, ASSIGNED: ACCENT.violet, EVIDENCE_SUBMITTED: ACCENT.amber,
                      UNDER_REVIEW: ACCENT.sky, APPROVED: ACCENT.emerald, REJECTED: ACCENT.rose,
                      EXPIRED: ACCENT.slate, CANCELLED: ACCENT.slate,
                    }[task.status] || ACCENT.slate;

                    return (
                      <div key={task.id} style={{
                        background: '#fff', border: `1px solid ${isExpanded ? statusAccent.border : S[200]}`,
                        borderRadius: 12, overflow: 'hidden', position: 'relative', transition: 'all 0.15s ease',
                      }}>
                        {/* Accent bar */}
                        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: isOverdue ? ACCENT.rose.icon : statusAccent.icon, opacity: 0.2 }} />

                        {/* Task Summary Row */}
                        <div onClick={() => setExpandedQueueId(isExpanded ? null : task.id)} style={{
                          padding: '14px 18px', cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: 14,
                        }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 14, fontWeight: 600, color: S[900], marginBottom: 4 }}>
                              {task.controlCode || control?.controlCode || 'Control'} — {task.controlName || control?.controlName || control?.title || task.controlId?.substring(0, 12) + '...'}
                            </div>
                            <div style={{ fontSize: 12, color: S[500], lineHeight: 1.5, marginBottom: 8 }}>
                              {control?.description?.substring(0, 120) || 'Manual attestation task'}
                              {control?.description?.length > 120 ? '...' : ''}
                            </div>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              {/* Status badge */}
                              <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 6,
                                background: statusAccent.bg, color: statusAccent.text,
                                border: `1px solid ${statusAccent.border}`,
                                fontFamily: "'IBM Plex Mono', 'SF Mono', monospace",
                              }}>{task.status?.replace(/_/g, ' ')}</span>
                              {/* Due date badge */}
                              <span style={{
                                fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 6,
                                background: isOverdue ? ACCENT.rose.bg : S[100],
                                color: isOverdue ? ACCENT.rose.text : S[600],
                                border: `1px solid ${isOverdue ? ACCENT.rose.border : S[200]}`,
                              }}>Due: {task.dueDate ? new Date(task.dueDate).toLocaleDateString() : 'N/A'}</span>
                              {/* Agent badge */}
                              {(task.agentName || agent) && <span style={{
                                fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 6,
                                background: S[100], color: S[600], border: `1px solid ${S[200]}`,
                              }}>{task.agentName || agent?.name}</span>}
                              {/* Evidence type */}
                              {control?.evidenceType && <span style={{
                                fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 6,
                                background: control.evidenceType === 'MANUAL' ? '#fef3c7' : control.evidenceType === 'HYBRID' ? ACCENT.violet.bg : ACCENT.sky.bg,
                                color: control.evidenceType === 'MANUAL' ? '#92400e' : control.evidenceType === 'HYBRID' ? ACCENT.violet.text : ACCENT.sky.text,
                              }}>{control.evidenceType}</span>}
                            </div>
                          </div>
                          <div style={{ padding: 4, borderRadius: 6, color: S[400], flexShrink: 0 }}>
                            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                          </div>
                        </div>

                        {/* ── Expanded Detail ── */}
                        {isExpanded && (
                          <div style={{ padding: '0 18px 18px', borderTop: `1px solid ${S[100]}`, animation: 'pcpFadeIn 0.2s ease' }}>

                            {/* Evidence Section */}
                            <div style={{ marginTop: 14 }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: S[600], textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                                <FileText size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                                Evidence Details
                              </div>
                              <div style={{ background: S[50], border: `1px solid ${S[200]}`, borderRadius: 8, padding: 14 }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12 }}>
                                  <div><span style={{ color: S[500] }}>Type:</span> <strong style={{ color: S[700] }}>{task.evidenceType || 'MULTIPLE'}</strong></div>
                                  <div><span style={{ color: S[500] }}>Submitted:</span> <strong style={{ color: S[700] }}>{task.evidenceSubmittedAt ? new Date(task.evidenceSubmittedAt).toLocaleString() : 'Pending'}</strong></div>
                                  <div><span style={{ color: S[500] }}>Submitted By:</span> <strong style={{ color: S[700] }}>{
                                    task.submitterName
                                    || (task.evidenceSubmittedBy === session?.userId ? (authUser?.name || 'You') : null)
                                    || (task.evidenceSubmittedBy ? task.evidenceSubmittedBy.substring(0, 8) + '...' : 'N/A')
                                  }</strong></div>
                                  <div><span style={{ color: S[500] }}>Attestation Freq:</span> <strong style={{ color: S[700] }}>{control?.attestationFrequency || 'N/A'}</strong></div>
                                </div>
                                {task.evidencePayload && (
                                  <div style={{ marginTop: 10 }}>
                                    <div style={{ fontSize: 11, fontWeight: 600, color: S[600], marginBottom: 6 }}>
                                      <Upload size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                                      Uploaded Evidence ({task.evidencePayload?.items?.length || 0} item{task.evidencePayload?.items?.length !== 1 ? 's' : ''}):
                                    </div>
                                    {task.evidencePayload?.items?.map((item, idx) => (
                                      <div key={idx} style={{
                                        padding: '8px 10px', background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 6,
                                        marginBottom: 4, fontSize: 11, display: 'flex', alignItems: 'center', gap: 8,
                                      }}>
                                        <Upload size={12} color={S[400]} />
                                        <div style={{ flex: 1 }}>
                                          <div style={{ color: S[700], fontWeight: 600 }}>{item.fileName || 'Evidence'}</div>
                                          <div style={{ color: S[500], fontSize: 10 }}>
                                            {item.evidenceType} · Uploaded {item.uploadedAt ? new Date(item.uploadedAt).toLocaleString() : ''}
                                            {item.externalUrl && <> · <a href={item.externalUrl} target="_blank" rel="noreferrer" style={{ color: BRAND.primary }}>View Link ↗</a></>}
                                          </div>
                                        </div>
                                        {item.hasFile && item.evidenceId && (
                                          <a
                                            href={api.ccDownloadEvidenceUrl(task.id, item.evidenceId)}
                                            target="_blank"
                                            rel="noreferrer"
                                            style={{
                                              fontSize: 10, color: '#fff', background: BRAND.primary,
                                              padding: '3px 10px', borderRadius: 4, textDecoration: 'none',
                                              fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4,
                                              transition: 'opacity 0.15s',
                                            }}
                                            onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                                            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                                          >
                                            <Download size={10} /> View File
                                          </a>
                                        )}
                                        {item.hasFile && !item.evidenceId && (
                                          <span style={{ fontSize: 10, color: ACCENT.emerald.text, background: ACCENT.emerald.bg, padding: '1px 6px', borderRadius: 4 }}>File Attached</span>
                                        )}
                                      </div>
                                    ))}
                                    {!task.evidencePayload?.items?.length && (
                                      <pre style={{
                                        fontSize: 11, background: '#fff', padding: 8, borderRadius: 6,
                                        overflow: 'auto', maxHeight: 120, whiteSpace: 'pre-wrap', color: S[600],
                                        border: `1px solid ${S[200]}`,
                                      }}>{JSON.stringify(task.evidencePayload, null, 2)}</pre>
                                    )}
                                  </div>
                                )}
                                {!task.evidencePayload && task.status === 'CREATED' && (
                                  <div style={{ marginTop: 8, fontSize: 11, color: S[400], fontStyle: 'italic' }}>No evidence submitted yet.</div>
                                )}
                              </div>
                            </div>

                            {/* Remediation Guidance */}
                            {control?.remediationGuidance && (
                              <div style={{
                                marginTop: 10, padding: '10px 14px', borderRadius: 8,
                                background: '#fffbeb', border: '1px solid #fde68a', fontSize: 11, color: '#92400e',
                              }}>
                                <strong>Expected Evidence:</strong> {control.remediationGuidance}
                              </div>
                            )}

                            {/* AI Recommendation */}
                            {task.aiRecommendation && (
                              <div style={{
                                marginTop: 10, padding: '10px 14px', borderRadius: 8,
                                background: task.aiRecommendation === 'APPROVE' ? ACCENT.emerald.bg : '#fef3c7',
                                border: `1px solid ${task.aiRecommendation === 'APPROVE' ? ACCENT.emerald.border : '#fde68a'}`,
                                fontSize: 11, color: task.aiRecommendation === 'APPROVE' ? ACCENT.emerald.text : '#92400e',
                              }}>
                                <Sparkles size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                                AI Recommendation: <strong>{task.aiRecommendation}</strong>
                                {task.aiConfidenceScore != null && <> (Confidence: {Math.round(task.aiConfidenceScore * 100)}%)</>}
                              </div>
                            )}

                            {/* Review Actions (only for REVIEW_QUEUE view or EVIDENCE_SUBMITTED status) */}
                            {(attestQueueView === 'REVIEW_QUEUE' || task.status === 'EVIDENCE_SUBMITTED') && !['APPROVED','REJECTED','CANCELLED','EXPIRED'].includes(task.status) && (
                              <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${S[200]}` }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: S[600], textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                                  Review Decision
                                </div>
                                <textarea
                                  value={reviewNotes}
                                  onChange={e => setReviewNotes(e.target.value)}
                                  placeholder="Add review notes (optional for approval, recommended for rejection)..."
                                  style={{
                                    width: '100%', minHeight: 60, fontSize: 12, padding: '8px 10px',
                                    border: `1px solid ${S[200]}`, borderRadius: 8, resize: 'vertical',
                                    fontFamily: 'inherit', color: S[700], boxSizing: 'border-box',
                                  }}
                                />
                                <div style={{ display: 'flex', gap: 8, marginTop: 10, justifyContent: 'flex-end' }}>
                                  <button
                                    onClick={() => handleReviewAttestation(task.id, false)}
                                    disabled={reviewingId === task.id}
                                    style={{
                                      ...btnBase, padding: '8px 16px', fontSize: 12, fontWeight: 600,
                                      borderRadius: 8, background: '#fff', color: '#dc2626',
                                      border: '1px solid #fca5a5',
                                      opacity: reviewingId === task.id ? 0.5 : 1,
                                    }}
                                  >
                                    {reviewingId === task.id ? <Loader size={12} style={{ animation: 'pcpSpin 1s linear infinite' }} /> : <X size={12} />}
                                    {' '}Reject
                                  </button>
                                  <button
                                    onClick={() => handleReviewAttestation(task.id, true)}
                                    disabled={reviewingId === task.id}
                                    style={{
                                      ...btnBase, padding: '8px 20px', fontSize: 12, fontWeight: 700,
                                      borderRadius: 8, background: '#059669', color: '#fff', border: 'none',
                                      boxShadow: '0 1px 4px rgba(5,150,105,0.2)',
                                      opacity: reviewingId === task.id ? 0.5 : 1,
                                    }}
                                  >
                                    {reviewingId === task.id ? <Loader size={12} style={{ animation: 'pcpSpin 1s linear infinite' }} /> : <CheckCircle size={12} />}
                                    {' '}Approve
                                  </button>
                                </div>
                              </div>
                            )}

                            {/* Completed status info — detailed for History view */}
                            {['APPROVED','REJECTED','CANCELLED','EXPIRED'].includes(task.status) && (
                              <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${S[200]}` }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: S[600], textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                                  {task.status === 'APPROVED' ? <CheckCircle size={12} style={{ verticalAlign: 'middle', marginRight: 4, color: '#059669' }} /> :
                                   task.status === 'REJECTED' ? <X size={12} style={{ verticalAlign: 'middle', marginRight: 4, color: '#dc2626' }} /> :
                                   <Clock size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />}
                                  Review Outcome
                                </div>
                                <div style={{
                                  padding: 14, borderRadius: 8,
                                  background: task.status === 'APPROVED' ? ACCENT.emerald.bg : task.status === 'REJECTED' ? ACCENT.rose.bg : S[50],
                                  border: `1px solid ${task.status === 'APPROVED' ? ACCENT.emerald.border : task.status === 'REJECTED' ? ACCENT.rose.border : S[200]}`,
                                }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                                    <span style={{
                                      fontSize: 13, fontWeight: 700,
                                      color: task.status === 'APPROVED' ? '#059669' : task.status === 'REJECTED' ? '#dc2626' : S[600],
                                    }}>
                                      {task.status === 'APPROVED' ? '✓ Evidence Approved' :
                                       task.status === 'REJECTED' ? '✗ Evidence Rejected' :
                                       task.status === 'CANCELLED' ? '— Cancelled' : '⏰ Expired'}
                                    </span>
                                  </div>
                                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12 }}>
                                    {task.reviewedAt && (
                                      <div><span style={{ color: S[500] }}>Reviewed:</span> <strong style={{ color: S[700] }}>{new Date(task.reviewedAt).toLocaleString()}</strong></div>
                                    )}
                                    {(task.reviewerName || task.reviewedBy || task.reviewerId) && (() => {
                                      const rId = task.reviewedBy || task.reviewerId;
                                      const rName = task.reviewerName
                                        || (rId === task.evidenceSubmittedBy ? task.submitterName : null)
                                        || (rId === task.assignedTo ? task.assigneeName : null)
                                        || (rId === session?.userId ? (authUser?.name || authUser?.firstName + ' ' + authUser?.lastName || authUser?.email || 'You') : null)
                                        || (rId ? rId.substring(0, 8) + '...' : 'N/A');
                                      return <div><span style={{ color: S[500] }}>Reviewer:</span> <strong style={{ color: S[700] }}>{rName}</strong></div>;
                                    })()}
                                    {task.assigneeName && (
                                      <div><span style={{ color: S[500] }}>Assigned To:</span> <strong style={{ color: S[700] }}>{task.assigneeName}</strong></div>
                                    )}
                                    {task.evidenceSubmittedAt && (
                                      <div><span style={{ color: S[500] }}>Evidence Submitted:</span> <strong style={{ color: S[700] }}>{new Date(task.evidenceSubmittedAt).toLocaleString()}</strong></div>
                                    )}
                                  </div>
                                  {(task.reviewNotes || task.rejectionReason) && (
                                    <div style={{
                                      marginTop: 10, padding: '8px 12px', borderRadius: 6,
                                      background: '#fff', border: `1px solid ${S[200]}`, fontSize: 12, color: S[700],
                                    }}>
                                      <span style={{ fontWeight: 600, color: S[500], fontSize: 11 }}>Review Notes:</span>
                                      <div style={{ marginTop: 4 }}>{task.reviewNotes || task.rejectionReason}</div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ControlDriftPanel() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({});
  const [filterSeverity, setFilterSeverity] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDriftType, setFilterDriftType] = useState(''); // G08: 'SUDDEN' | 'TREND' | ''
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [exceptionWindows, setExceptionWindows] = useState([]); // G07: seasonal baselines
  const [showExceptionModal, setShowExceptionModal] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [alertsRes, dashRes, ewRes] = await Promise.all([
        api.ccGetDriftAlerts({
          severity: filterSeverity || undefined,
          status: filterStatus || undefined,
        }),
        api.ccGetDriftDashboard(),
        api.ccListDriftExceptionWindows(), // G07
      ]);
      if (alertsRes.ok) setAlerts(alertsRes.data?.alerts || (Array.isArray(alertsRes.data) ? alertsRes.data : []));
      if (dashRes.ok) setSummary(dashRes.data || {});
      if (ewRes.ok) setExceptionWindows(ewRes.data || []);
    } catch (_) {}
    setLoading(false);
  }, [filterSeverity, filterStatus]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleUpdateAlert = async (alertId, newStatus) => {
    setUpdatingId(alertId);
    try {
      const res = await api.ccUpdateDriftAlert(alertId, { status: newStatus });
      if (res.ok) {
        setAlerts(prev => prev.map(a => a.id === alertId ? { ...a, status: newStatus } : a));
        loadData(); // refresh summary counts
      }
    } catch (_) {}
    setUpdatingId(null);
  };

  const filteredAlerts = alerts.filter(a => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const matchName = (a.resourceName || '').toLowerCase().includes(term);
      const matchType = (a.resourceType || '').toLowerCase().includes(term);
      const matchDesc = (a.driftDescription || '').toLowerCase().includes(term);
      if (!matchName && !matchType && !matchDesc) return false;
    }
    if (filterDriftType && a.driftType && a.driftType !== filterDriftType) return false; // G08
    return true;
  });

  const totalDrifts = summary.total || filteredAlerts.length;
  const criticalCount = summary.critical || filteredAlerts.filter(a => a.priority === 'CRITICAL' || a.severity === 'CRITICAL').length;
  const openCount = summary.open || filteredAlerts.filter(a => a.status === 'OPEN').length;
  const acknowledgedCount = summary.acknowledged || filteredAlerts.filter(a => a.status === 'ACKNOWLEDGED').length;
  const trendDriftCount = filteredAlerts.filter(a => a.driftType === 'TREND').length; // G08
  const activeExceptionCount = exceptionWindows.filter(w => w.status === 'ACTIVE').length; // G07

  if (loading) return (
    <div style={{ padding: '48px 0', textAlign: 'center' }}>
      <Loader size={24} style={{ animation: 'pcpSpin 1s linear infinite', color: BRAND.primary, margin: '0 auto' }} />
      <div style={{ fontSize: 13, color: S[500], marginTop: 12 }}>Scanning for control drift…</div>
    </div>
  );

  return (
    <div style={{ animation: 'pcpFadeIn 0.3s ease' }}>
      {/* ── Hero Stat Cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 }}>
        {[
          { label: 'Total Drifts', value: totalDrifts, accent: ACCENT.sky, Icon: TrendingUp },
          { label: 'Trend Drifts', value: trendDriftCount, accent: ACCENT.violet, Icon: TrendingDown },
          { label: 'Critical', value: criticalCount, accent: ACCENT.rose, Icon: AlertTriangle },
          { label: 'Open', value: openCount, accent: ACCENT.amber, Icon: Clock },
        ].map((stat, i) => (
          <div key={stat.label} className="pcp-stat-card" style={{
            background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12,
            padding: '18px 20px', position: 'relative', overflow: 'hidden',
            animation: 'pcpSlideUp 0.3s ease', animationDelay: `${i * 0.06}s`, animationFillMode: 'backwards',
          }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: stat.accent.icon, opacity: 0.15 }} />
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: stat.accent.text, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>{stat.label}</div>
                <div style={{ fontSize: 30, fontWeight: 800, color: S[900], lineHeight: 1, letterSpacing: '-0.03em', fontFeatureSettings: "'tnum'" }}>{stat.value}</div>
              </div>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: stat.accent.bg, border: `1px solid ${stat.accent.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <stat.Icon size={18} style={{ color: stat.accent.icon }} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Toolbar: Search + Filters + Refresh ── */}
      <div style={{
        display: 'flex', gap: 10, alignItems: 'center', marginBottom: 20, flexWrap: 'wrap',
        animation: 'pcpSlideUp 0.3s ease', animationDelay: '0.15s', animationFillMode: 'backwards',
      }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: S[400] }} />
          <input
            type="text" placeholder="Search drifts by resource, type, or description…"
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            style={{ ...inputBase, paddingLeft: 32 }}
          />
        </div>
        <select value={filterSeverity} onChange={e => setFilterSeverity(e.target.value)} style={{ ...inputBase, width: 140 }}>
          <option value="">All Severities</option>
          <option value="CRITICAL">Critical</option>
          <option value="HIGH">High</option>
          <option value="MEDIUM">Medium</option>
          <option value="LOW">Low</option>
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ ...inputBase, width: 160 }}>
          <option value="">All Statuses</option>
          <option value="OPEN">Open</option>
          <option value="ACKNOWLEDGED">Acknowledged</option>
          <option value="RESOLVED">Resolved</option>
        </select>
        <select value={filterDriftType} onChange={e => setFilterDriftType(e.target.value)} style={{ ...inputBase, width: 140 }}>
          <option value="">All Types</option>
          <option value="SUDDEN">Sudden (z-score)</option>
          <option value="TREND">Trend (gradual)</option>
        </select>
        <button className="pcp-btn-ghost" onClick={() => setShowExceptionModal(true)} style={{
          ...btnBase, padding: '8px 14px', fontSize: 12, fontWeight: 600,
          background: activeExceptionCount > 0 ? ACCENT.amber.bg : S[50],
          color: activeExceptionCount > 0 ? ACCENT.amber.text : S[600], borderRadius: 8,
          border: activeExceptionCount > 0 ? `1px solid ${ACCENT.amber.border}` : 'none',
        }}>
          <Clock size={13} /> Exception Windows {activeExceptionCount > 0 && `(${activeExceptionCount})`}
        </button>
        <button className="pcp-btn-ghost" onClick={loadData} style={{
          ...btnBase, padding: '8px 14px', fontSize: 12, fontWeight: 600,
          background: S[50], color: S[600], borderRadius: 8,
        }}>
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {/* ── Drift Alerts List ── */}
      {filteredAlerts.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '60px 24px',
          animation: 'pcpSlideUp 0.3s ease', animationDelay: '0.2s', animationFillMode: 'backwards',
        }}>
          <TrendingUp size={36} style={{ color: S[300], marginBottom: 12 }} />
          <div style={{ fontSize: 16, fontWeight: 700, color: S[700], marginBottom: 4 }}>No control drift detected</div>
          <div style={{ fontSize: 13, color: S[500] }}>Control assignments match approved baselines. Your governance posture is clean.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filteredAlerts.map((alert, i) => {
            const sevStyle = DRIFT_SEVERITY_STYLES[alert.priority || alert.severity] || DRIFT_SEVERITY_STYLES.MEDIUM;
            const statusStyle = DRIFT_STATUS_STYLES[alert.status] || DRIFT_STATUS_STYLES.OPEN;
            const isExpanded = expandedId === alert.id;

            return (
              <div key={alert.id} className="pcp-card" style={{
                background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12, overflow: 'hidden',
                borderLeft: `4px solid ${sevStyle.icon}`,
                animation: 'pcpSlideUp 0.3s ease', animationDelay: `${0.2 + i * 0.04}s`, animationFillMode: 'backwards',
              }}>
                {/* Alert Header */}
                <div
                  style={{ padding: '16px 18px', cursor: 'pointer' }}
                  onClick={() => setExpandedId(isExpanded ? null : alert.id)}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 700, fontSize: 14, color: S[900] }}>
                          {alert.resourceType}: {alert.resourceName}
                        </span>
                        <span style={{
                          fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
                          background: sevStyle.bg, color: sevStyle.text, border: `1px solid ${sevStyle.border}`,
                        }}>
                          {sevStyle.label}
                        </span>
                        <span style={{
                          fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
                          background: statusStyle.bg, color: statusStyle.text, border: `1px solid ${statusStyle.border}`,
                        }}>
                          {statusStyle.label}
                        </span>
                        {/* G08: Drift type badge */}
                        {alert.driftType === 'TREND' && (
                          <span style={{
                            fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
                            background: ACCENT.violet.bg, color: ACCENT.violet.text, border: `1px solid ${ACCENT.violet.border}`,
                          }}>
                            Trend Drift
                          </span>
                        )}
                        {/* G01: Pipeline correlation badge */}
                        {alert.correlationGroupId && (
                          <span style={{
                            fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 4,
                            background: ACCENT.sky.bg, color: ACCENT.sky.text, border: `1px solid ${ACCENT.sky.border}`,
                          }}>
                            Correlated
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: S[500], lineHeight: 1.4 }}>
                        {alert.driftDescription}
                        {alert.driftType === 'TREND' && alert.projectedBreachDate && (
                          <span style={{ color: ACCENT.rose.text, fontWeight: 600 }}>
                            {' '}— Projected breach: {new Date(alert.projectedBreachDate).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                      {alert.detectedAt && (
                        <div style={{ fontSize: 11, color: S[400], marginTop: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Clock size={11} /> Detected {new Date(alert.detectedAt).toLocaleString()}
                        </div>
                      )}
                    </div>
                    <div style={{
                      transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)',
                      transition: 'transform 0.15s', color: S[400], flexShrink: 0,
                    }}>
                      <ChevronDown size={16} />
                    </div>
                  </div>
                </div>

                {/* Expanded Detail */}
                {isExpanded && (
                  <div style={{
                    padding: '0 18px 16px', borderTop: `1px solid ${S[100]}`,
                    animation: 'pcpFadeIn 0.2s ease',
                  }}>
                    <div style={{ paddingTop: 14, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                      {alert.expectedValue && (
                        <div style={{ padding: '10px 14px', borderRadius: 8, background: ACCENT.emerald.bg, border: `1px solid ${ACCENT.emerald.border}` }}>
                          <div style={{ fontSize: 10, fontWeight: 700, color: ACCENT.emerald.text, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>Approved Baseline</div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: S[800] }}>{alert.expectedValue}</div>
                        </div>
                      )}
                      {alert.actualValue && (
                        <div style={{ padding: '10px 14px', borderRadius: 8, background: ACCENT.rose.bg, border: `1px solid ${ACCENT.rose.border}` }}>
                          <div style={{ fontSize: 10, fontWeight: 700, color: ACCENT.rose.text, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>Current Value</div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: S[800] }}>{alert.actualValue}</div>
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div style={{ display: 'flex', gap: 8 }}>
                      {alert.status === 'OPEN' && (
                        <>
                          <button
                            className="pcp-btn-primary"
                            onClick={(e) => { e.stopPropagation(); handleUpdateAlert(alert.id, 'ACKNOWLEDGED'); }}
                            disabled={updatingId === alert.id}
                            style={{
                              ...btnBase, padding: '7px 14px', fontSize: 11, fontWeight: 600,
                              background: ACCENT.amber.bg, color: ACCENT.amber.text, borderRadius: 6,
                              border: `1px solid ${ACCENT.amber.border}`,
                              opacity: updatingId === alert.id ? 0.5 : 1,
                            }}
                          >
                            <Eye size={12} /> Acknowledge
                          </button>
                          <button
                            className="pcp-btn-primary"
                            onClick={(e) => { e.stopPropagation(); handleUpdateAlert(alert.id, 'RESOLVED'); }}
                            disabled={updatingId === alert.id}
                            style={{
                              ...btnBase, padding: '7px 14px', fontSize: 11, fontWeight: 600,
                              background: ACCENT.emerald.bg, color: ACCENT.emerald.text, borderRadius: 6,
                              border: `1px solid ${ACCENT.emerald.border}`,
                              opacity: updatingId === alert.id ? 0.5 : 1,
                            }}
                          >
                            <CheckCircle size={12} /> Resolve
                          </button>
                        </>
                      )}
                      {alert.status === 'ACKNOWLEDGED' && (
                        <button
                          className="pcp-btn-primary"
                          onClick={(e) => { e.stopPropagation(); handleUpdateAlert(alert.id, 'RESOLVED'); }}
                          disabled={updatingId === alert.id}
                          style={{
                            ...btnBase, padding: '7px 14px', fontSize: 11, fontWeight: 600,
                            background: ACCENT.emerald.bg, color: ACCENT.emerald.text, borderRadius: 6,
                            border: `1px solid ${ACCENT.emerald.border}`,
                            opacity: updatingId === alert.id ? 0.5 : 1,
                          }}
                        >
                          <CheckCircle size={12} /> Resolve
                        </button>
                      )}
                      {alert.status === 'RESOLVED' && (
                        <span style={{ fontSize: 11, color: ACCENT.emerald.text, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                          <CheckCircle size={12} /> Drift resolved
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── G07: Exception Windows Modal ── */}
      {showExceptionModal && (
        <ExceptionWindowModal
          windows={exceptionWindows}
          onClose={() => setShowExceptionModal(false)}
          onRefresh={loadData}
        />
      )}
    </div>
  );
}

/* ─── G07: Exception Window Management Modal ─── */
function ExceptionWindowModal({ windows, onClose, onRefresh }) {
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '', description: '', controlId: '', agentId: '',
    startDate: '', endDate: '', adjustedThresholdMultiplier: 1.5,
    suppressAlerts: false,
  });

  const handleCreate = async () => {
    if (!form.name || !form.startDate || !form.endDate) return;
    setSaving(true);
    try {
      const res = await api.ccCreateDriftExceptionWindow({
        ...form,
        adjustedThresholdMultiplier: parseFloat(form.adjustedThresholdMultiplier) || 1.5,
        status: 'ACTIVE',
      });
      if (res.ok) {
        setCreating(false);
        setForm({ name: '', description: '', controlId: '', agentId: '', startDate: '', endDate: '', adjustedThresholdMultiplier: 1.5, suppressAlerts: false });
        onRefresh();
      }
    } catch (_) {}
    setSaving(false);
  };

  const handleDelete = async (id) => {
    try {
      await api.ccDeleteDriftExceptionWindow(id);
      onRefresh();
    } catch (_) {}
  };

  return (
    <Modal onClose={onClose} title="Drift Exception Windows">
      <div style={{ minWidth: 520, maxHeight: '70vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: S[500] }}>
            Define time windows where drift thresholds are relaxed (e.g., seasonal peaks, maintenance).
          </div>
          {!creating && (
            <button onClick={() => setCreating(true)} style={{
              ...btnBase, padding: '6px 14px', fontSize: 11, fontWeight: 600,
              background: BRAND.primary, color: '#fff', borderRadius: 6,
            }}>
              <Plus size={12} /> New Window
            </button>
          )}
        </div>

        {creating && (
          <div style={{
            padding: 16, borderRadius: 10, border: `1px solid ${S[200]}`, background: S[25], marginBottom: 16,
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: S[600], marginBottom: 4, display: 'block' }}>Name *</label>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g., Black Friday 2026" style={inputBase} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: S[600], marginBottom: 4, display: 'block' }}>Threshold Multiplier</label>
                <input type="number" step="0.1" min="1" value={form.adjustedThresholdMultiplier}
                  onChange={e => setForm(f => ({ ...f, adjustedThresholdMultiplier: e.target.value }))}
                  style={inputBase} />
              </div>
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: S[600], marginBottom: 4, display: 'block' }}>Description</label>
              <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Reason for exception window" style={inputBase} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: S[600], marginBottom: 4, display: 'block' }}>Start Date *</label>
                <input type="date" value={form.startDate} onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))} style={inputBase} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: S[600], marginBottom: 4, display: 'block' }}>End Date *</label>
                <input type="date" value={form.endDate} onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))} style={inputBase} />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: S[600], marginBottom: 4, display: 'block' }}>Control ID (optional)</label>
                <input value={form.controlId} onChange={e => setForm(f => ({ ...f, controlId: e.target.value }))}
                  placeholder="Specific control UUID" style={inputBase} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: S[600], marginBottom: 4, display: 'block' }}>Agent ID (optional)</label>
                <input value={form.agentId} onChange={e => setForm(f => ({ ...f, agentId: e.target.value }))}
                  placeholder="Specific agent UUID" style={inputBase} />
              </div>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: S[700], marginBottom: 14, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.suppressAlerts}
                onChange={e => setForm(f => ({ ...f, suppressAlerts: e.target.checked }))} />
              Suppress drift alerts during this window
            </label>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setCreating(false)} style={{ ...btnBase, padding: '6px 14px', fontSize: 11, fontWeight: 600, background: S[100], color: S[600], borderRadius: 6 }}>
                Cancel
              </button>
              <button onClick={handleCreate} disabled={saving || !form.name || !form.startDate || !form.endDate}
                style={{ ...btnBase, padding: '6px 14px', fontSize: 11, fontWeight: 600, background: BRAND.primary, color: '#fff', borderRadius: 6, opacity: saving ? 0.5 : 1 }}>
                {saving ? 'Creating…' : 'Create Window'}
              </button>
            </div>
          </div>
        )}

        {/* Active & Scheduled Windows */}
        {windows.length === 0 && !creating ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Clock size={28} style={{ color: S[300], marginBottom: 8 }} />
            <div style={{ fontSize: 13, color: S[500] }}>No exception windows configured</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {windows.map(w => {
              const isActive = w.status === 'ACTIVE';
              const isExpired = w.endDate && new Date(w.endDate) < new Date();
              return (
                <div key={w.id} style={{
                  padding: '12px 14px', borderRadius: 8,
                  border: `1px solid ${isActive ? ACCENT.amber.border : S[200]}`,
                  background: isActive ? ACCENT.amber.bg : isExpired ? S[50] : '#fff',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: S[900] }}>{w.name}</span>
                        <span style={{
                          fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 3,
                          background: isActive ? ACCENT.emerald.bg : isExpired ? S[100] : ACCENT.sky.bg,
                          color: isActive ? ACCENT.emerald.text : isExpired ? S[500] : ACCENT.sky.text,
                        }}>
                          {isActive ? 'ACTIVE' : isExpired ? 'EXPIRED' : w.status}
                        </span>
                        {w.suppressAlerts && (
                          <span style={{ fontSize: 9, fontWeight: 600, padding: '1px 6px', borderRadius: 3, background: ACCENT.rose.bg, color: ACCENT.rose.text }}>
                            Alerts Suppressed
                          </span>
                        )}
                      </div>
                      {w.description && <div style={{ fontSize: 11, color: S[500], marginBottom: 4 }}>{w.description}</div>}
                      <div style={{ fontSize: 11, color: S[600] }}>
                        {w.startDate && new Date(w.startDate).toLocaleDateString()} → {w.endDate && new Date(w.endDate).toLocaleDateString()}
                        {w.adjustedThresholdMultiplier && <span style={{ marginLeft: 8, color: S[400] }}>×{w.adjustedThresholdMultiplier} threshold</span>}
                      </div>
                    </div>
                    <button onClick={() => handleDelete(w.id)} style={{ ...btnBase, padding: 4, color: S[400], background: 'none' }}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// CONTROL SCORECARD PANEL — Agent performance scores against assigned controls
// ═══════════════════════════════════════════════════════════════════════════════

const AUTONOMY_STYLES = {
  SELF_AUTONOMOUS: { bg: '#dcfce7', text: '#166534', border: '#bbf7d0', label: 'Self-Autonomous', icon: '🟢' },
  SUPERVISED:      { bg: '#dbeafe', text: '#1e40af', border: '#bfdbfe', label: 'Supervised',      icon: '🔵' },
  RESTRICTED:      { bg: '#fef3c7', text: '#92400e', border: '#fde68a', label: 'Restricted',      icon: '🟡' },
  PROBATION:       { bg: '#fef2f2', text: '#991b1b', border: '#fecaca', label: 'Probation',       icon: '🔴' },
};

const TRANSITION_STYLES = {
  MANUAL_PROMOTION:  { Icon: ArrowUpCircle,  color: '#16a34a', label: 'Manual Promotion' },
  AUTO_PROMOTION:    { Icon: TrendingUp,     color: '#16a34a', label: 'Auto Promotion' },
  MANUAL_REGRESSION: { Icon: ArrowDownCircle, color: '#dc2626', label: 'Manual Regression' },
  AUTO_REGRESSION:   { Icon: TrendingDown,   color: '#dc2626', label: 'Auto Regression' },
  PROBATION_START:   { Icon: AlertTriangle,  color: '#ca8a04', label: 'Probation Start' },
  PROBATION_END:     { Icon: CheckCircle,    color: '#16a34a', label: 'Probation End' },
  SYSTEM_OVERRIDE:   { Icon: Shield,         color: S[600],    label: 'System Override' },
};

function ControlScorecardPanel() {
  const [overview, setOverview] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [promotionEligible, setPromotionEligible] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentDetail, setAgentDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [agentNameMap, setAgentNameMap] = useState(new Map());
  // G01/G02/G15: Pipeline management
  const [pipelines, setPipelines] = useState([]);
  const [showPipelineSection, setShowPipelineSection] = useState(false);
  const [showPipelineCreate, setShowPipelineCreate] = useState(false);
  const [pipelineForm, setPipelineForm] = useState({ name: '', description: '', pipelineType: 'SEQUENTIAL', agentIds: '' });
  const [savingPipeline, setSavingPipeline] = useState(false);

  // Three-tier agent name resolution: direct agents → deployment records → individual lookups
  useEffect(() => {
    (async () => {
      try {
        const nameMap = new Map();
        const [agentRes, deployRes] = await Promise.all([
          api.getAgents({}),
          api.getDeployments({}),
        ]);
        // Tier 1: Direct agents — these always have names (exclude system agents)
        const directAgents = (agentRes.ok ? (Array.isArray(agentRes.data) ? agentRes.data : agentRes.data?.agents || []) : [])
          .filter(a => !a.isSystemAgent && a.category !== 'system');
        directAgents.forEach(a => { if (a.id && a.name) nameMap.set(a.id, a.name); });

        // Tier 2: Deployment records that carry agent names
        const deployments = deployRes.ok ? (Array.isArray(deployRes.data) ? deployRes.data : deployRes.data?.deployments || []) : [];
        const missingIds = [];
        deployments.forEach(d => {
          const aId = d.agentId || d.agent?.id;
          if (aId && !nameMap.has(aId)) {
            const knownName = d.agentName || d.agent?.name || d.name;
            if (knownName) { nameMap.set(aId, knownName); }
            else { missingIds.push(aId); }
          }
        });

        // Tier 3: Individual lookups for any still-unresolved IDs
        if (missingIds.length > 0) {
          const lookups = await Promise.allSettled(missingIds.map(id => api.getAgent(id)));
          missingIds.forEach((aId, idx) => {
            const result = lookups[idx];
            const data = result.status === 'fulfilled' && result.value?.ok ? result.value.data : null;
            if (!nameMap.has(aId)) nameMap.set(aId, data?.name || `Agent ${aId.substring(0, 8)}…`);
          });
        }
        setAgentNameMap(nameMap);
      } catch (_) {}
    })();
  }, []);

  const loadOverview = useCallback(async () => {
    setLoading(true);
    try {
      const [ovRes, alertRes, promoRes, pipeRes] = await Promise.all([
        api.getComplianceOverview(),
        api.getComplianceAlerts(75),
        api.getPromotionEligible(90, 100),
        api.ccListPipelines(), // G01/G02/G15
      ]);
      if (ovRes.ok) setOverview(ovRes.data);
      if (alertRes.ok) setAlerts(alertRes.data || []);
      if (promoRes.ok) setPromotionEligible(promoRes.data || []);
      if (pipeRes.ok) setPipelines(Array.isArray(pipeRes.data) ? pipeRes.data : pipeRes.data?.pipelines || []);
    } catch (_) {}
    setLoading(false);
  }, []);

  useEffect(() => { loadOverview(); }, [loadOverview]);

  const loadAgentDetail = useCallback(async (agentId) => {
    setSelectedAgent(agentId);
    try {
      const res = await api.getAgentCompliance(agentId);
      if (res.ok) setAgentDetail(res.data);
    } catch (_) {}
  }, []);

  const findAgentName = useCallback((agentId) => {
    if (agentNameMap.has(agentId)) return agentNameMap.get(agentId);
    return `${(agentId || '').substring(0, 8)}…`;
  }, [agentNameMap]);

  const handleRecalculate = async (agentId) => {
    try { await api.recalculateCompliance(agentId); await loadAgentDetail(agentId); await loadOverview(); } catch (_) {}
  };
  const handlePromote = async (agentId, level) => {
    try {
      // G09: Validate autonomy ceiling before promoting
      const valRes = await api.ccValidateAutonomyTransition(agentId, level);
      if (valRes.ok && valRes.data?.blocked) {
        alert(`Autonomy ceiling reached: ${valRes.data.reason || 'Regulatory constraint prevents this promotion'}. Max allowed: ${valRes.data.maxLevel || 'N/A'}`);
        return;
      }
      await api.promoteAgentAutonomy(agentId, level, 'Manual promotion from Control Scorecard');
      await loadAgentDetail(agentId);
      await loadOverview();
    } catch (_) {}
  };
  const handleRegress = async (agentId, level) => {
    try { await api.regressAgentAutonomy(agentId, level, 'Manual regression from Control Scorecard'); await loadAgentDetail(agentId); await loadOverview(); } catch (_) {}
  };

  // G01/G02/G15: Pipeline handlers
  const handleCreatePipeline = async () => {
    if (!pipelineForm.name) return;
    setSavingPipeline(true);
    try {
      const agentIdArray = pipelineForm.agentIds.split(',').map(s => s.trim()).filter(Boolean);
      const res = await api.ccCreatePipeline({
        name: pipelineForm.name,
        description: pipelineForm.description,
        pipelineType: pipelineForm.pipelineType,
        agentIds: agentIdArray,
        agentOrder: agentIdArray,
        status: 'ACTIVE',
      });
      if (res.ok) {
        setShowPipelineCreate(false);
        setPipelineForm({ name: '', description: '', pipelineType: 'SEQUENTIAL', agentIds: '' });
        loadOverview();
      }
    } catch (_) {}
    setSavingPipeline(false);
  };

  const handleRecalculatePipeline = async (pipelineId) => {
    try { await api.ccRecalculatePipelineScore(pipelineId); loadOverview(); } catch (_) {}
  };

  const handleArchivePipeline = async (pipelineId) => {
    try { await api.ccArchivePipeline(pipelineId); loadOverview(); } catch (_) {}
  };

  if (loading) return (
    <div style={{ padding: '48px 0', textAlign: 'center' }}>
      <Loader size={24} style={{ animation: 'pcpSpin 1s linear infinite', color: BRAND.primary, margin: '0 auto' }} />
      <div style={{ fontSize: 13, color: S[500], marginTop: 12 }}>Loading control scorecard…</div>
    </div>
  );

  // ── Agent Detail View ──
  if (selectedAgent && agentDetail) {
    const scoreColor = autonomyBandStyle(agentDetail.currentScore).ink;
    const ac = AUTONOMY_STYLES[agentDetail.recommendedAutonomy] || AUTONOMY_STYLES.RESTRICTED;

    return (
      <div style={{ animation: 'pcpFadeIn 0.3s ease' }}>
        {/* Back button */}
        <button onClick={() => { setSelectedAgent(null); setAgentDetail(null); }} style={{
          ...btnBase, padding: '6px 14px', fontSize: 12, fontWeight: 600,
          background: S[50], color: S[600], borderRadius: 8, marginBottom: 20,
        }}>
          <ChevronRight size={13} style={{ transform: 'rotate(180deg)' }} /> Back to scorecard
        </button>

        {/* Agent Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <div>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: S[900], margin: '0 0 6px' }}>{findAgentName(selectedAgent)}</h3>
            <span style={{
              fontSize: 11, fontWeight: 700, padding: '4px 12px', borderRadius: 6,
              background: ac.bg, color: ac.text, border: `1px solid ${ac.border}`,
            }}>{ac.label}</span>
          </div>
          <button className="pcp-btn-ghost" onClick={() => handleRecalculate(selectedAgent)} style={{
            ...btnBase, padding: '8px 16px', fontSize: 12, fontWeight: 600,
            background: S[50], color: S[600], borderRadius: 8,
          }}>
            <RefreshCw size={13} /> Recalculate
          </button>
        </div>

        {/* Score Hero Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 }}>
          {[
            { label: 'Control Score', value: agentDetail.currentScore != null ? `${agentDetail.currentScore}%` : '—', accent: { ...ACCENT.sky, icon: scoreColor }, Icon: BarChart3 },
            { label: 'Total Evaluations', value: agentDetail.totalEvaluations || 0, accent: ACCENT.violet, Icon: Activity },
            { label: 'Blocks (×3)', value: agentDetail.blockCount || 0, accent: ACCENT.rose, Icon: AlertTriangle },
            { label: 'Warnings (×1)', value: agentDetail.warnCount || 0, accent: ACCENT.amber, Icon: AlertCircle },
          ].map((stat, i) => (
            <div key={stat.label} className="pcp-stat-card" style={{
              background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12,
              padding: '18px 20px', position: 'relative', overflow: 'hidden',
              animation: 'pcpSlideUp 0.3s ease', animationDelay: `${i * 0.06}s`, animationFillMode: 'backwards',
            }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: stat.accent.icon, opacity: 0.15 }} />
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: stat.accent.text || S[500], textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>{stat.label}</div>
                  <div style={{ fontSize: 30, fontWeight: 800, color: S[900], lineHeight: 1, letterSpacing: '-0.03em', fontFeatureSettings: "'tnum'" }}>{stat.value}</div>
                </div>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: stat.accent.bg, border: `1px solid ${stat.accent.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <stat.Icon size={18} style={{ color: stat.accent.icon }} />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Autonomy Management */}
        <div style={{
          background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12, padding: '18px 20px', marginBottom: 20,
          animation: 'pcpSlideUp 0.3s ease', animationDelay: '0.2s', animationFillMode: 'backwards',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: S[400], textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              Autonomy Management
            </div>
            {/* G09: Show regulatory ceiling if applicable */}
            {agentDetail.maxAutonomyLevel && (
              <span style={{
                fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 6,
                background: ACCENT.rose.bg, color: ACCENT.rose.text, border: `1px solid ${ACCENT.rose.border}`,
                display: 'flex', alignItems: 'center', gap: 4,
              }}>
                <Lock size={10} /> Regulatory Ceiling: {(AUTONOMY_STYLES[agentDetail.maxAutonomyLevel] || {}).label || agentDetail.maxAutonomyLevel}
              </span>
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
            {Object.entries(AUTONOMY_STYLES).map(([level, lc]) => {
              const isCurrent = level === agentDetail.recommendedAutonomy;
              const AUTONOMY_RANK = { RESTRICTED: 0, PROBATION: 1, SUPERVISED: 2, SELF_AUTONOMOUS: 3 };
              const isAboveCeiling = agentDetail.maxAutonomyLevel &&
                (AUTONOMY_RANK[level] || 0) > (AUTONOMY_RANK[agentDetail.maxAutonomyLevel] || 3);
              return (
                <button key={level} onClick={() => {
                  if (isCurrent || isAboveCeiling) return;
                  const isPromo = ['SELF_AUTONOMOUS', 'SUPERVISED'].includes(level) && ['RESTRICTED', 'PROBATION'].includes(agentDetail.recommendedAutonomy);
                  if (isPromo) handlePromote(selectedAgent, level);
                  else handleRegress(selectedAgent, level);
                }} className="pcp-card" style={{
                  ...btnBase, padding: '14px 12px', borderRadius: 10, justifyContent: 'center',
                  flexDirection: 'column', gap: 4,
                  background: isAboveCeiling ? S[100] : isCurrent ? lc.bg : '#fff',
                  color: isAboveCeiling ? S[400] : isCurrent ? lc.text : S[500],
                  border: `2px solid ${isAboveCeiling ? S[200] : isCurrent ? lc.text : S[200]}`,
                  cursor: isCurrent || isAboveCeiling ? 'default' : 'pointer',
                  opacity: isAboveCeiling ? 0.5 : isCurrent ? 1 : 0.7,
                  transition: 'all 0.15s ease',
                  position: 'relative',
                }}>
                  <span style={{ fontSize: 13, fontWeight: 700 }}>{lc.label}</span>
                  {isCurrent && <span style={{ fontSize: 10, fontWeight: 600 }}>Current</span>}
                  {isAboveCeiling && (
                    <span style={{ fontSize: 9, fontWeight: 600, color: ACCENT.rose.text }}>
                      <Lock size={9} /> Regulatory Limit
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Transition History */}
        <div style={{
          background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12, padding: '18px 20px',
          animation: 'pcpSlideUp 0.3s ease', animationDelay: '0.25s', animationFillMode: 'backwards',
        }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: S[400], textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 14 }}>
            Autonomy Transition History
          </div>
          {(agentDetail.transitions || []).length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 0', color: S[400], fontSize: 13 }}>
              No transitions recorded for this agent
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {(agentDetail.transitions || []).slice(0, 10).map((t, i) => {
                const ts = TRANSITION_STYLES[t.triggerType] || TRANSITION_STYLES.SYSTEM_OVERRIDE;
                return (
                  <div key={t.id || i} style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                    borderRadius: 8, background: S[50], border: `1px solid ${S[100]}`,
                    animation: 'pcpFadeIn 0.2s ease', animationDelay: `${i * 0.03}s`, animationFillMode: 'backwards',
                  }}>
                    <ts.Icon size={18} style={{ color: ts.color, flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: S[700] }}>
                        {t.previousLevel?.replace(/_/g, ' ')} → {t.newLevel?.replace(/_/g, ' ')}
                      </div>
                      <div style={{ fontSize: 11, color: S[500] }}>
                        {ts.label}{t.reason ? ` — ${t.reason}` : ''}
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: S[400], fontFeatureSettings: "'tnum'", flexShrink: 0 }}>
                      {t.createdAt ? new Date(t.createdAt).toLocaleDateString() : ''}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Overview ──
  return (
    <div style={{ animation: 'pcpFadeIn 0.3s ease' }}>
      {/* ── Hero Stat Cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 }}>
        {[
          { label: 'Average Score', value: overview?.averageScore ? `${overview.averageScore.toFixed(1)}%` : '—', sub: `Across ${overview?.totalAgents || 0} agents`, accent: ACCENT.sky, Icon: BarChart3 },
          { label: 'Below Threshold', value: overview?.agentsBelowThreshold || 0, sub: 'Agents below 75%', accent: ACCENT.rose, Icon: AlertTriangle },
          { label: 'On Probation', value: overview?.agentsOnProbation || 0, sub: 'Time-limited restriction', accent: ACCENT.amber, Icon: Clock },
          { label: 'Promotion Ready', value: promotionEligible.length, sub: 'Eligible for advancement', accent: ACCENT.emerald, Icon: ArrowUpCircle },
        ].map((stat, i) => (
          <div key={stat.label} className="pcp-stat-card" style={{
            background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12,
            padding: '18px 20px', position: 'relative', overflow: 'hidden',
            animation: 'pcpSlideUp 0.3s ease', animationDelay: `${i * 0.06}s`, animationFillMode: 'backwards',
          }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: stat.accent.icon, opacity: 0.15 }} />
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: stat.accent.text, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>{stat.label}</div>
                <div style={{ fontSize: 30, fontWeight: 800, color: S[900], lineHeight: 1, letterSpacing: '-0.03em', fontFeatureSettings: "'tnum'" }}>{stat.value}</div>
                <div style={{ fontSize: 11, color: S[500], marginTop: 6 }}>{stat.sub}</div>
              </div>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: stat.accent.bg, border: `1px solid ${stat.accent.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <stat.Icon size={18} style={{ color: stat.accent.icon }} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Autonomy Distribution ── */}
      {overview?.autonomyDistribution && (
        <div style={{
          background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12, padding: '18px 20px', marginBottom: 20,
          animation: 'pcpSlideUp 0.3s ease', animationDelay: '0.15s', animationFillMode: 'backwards',
        }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: S[400], textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 14 }}>
            Autonomy Distribution
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Object.keys(overview.autonomyDistribution).length}, 1fr)`, gap: 12 }}>
            {Object.entries(overview.autonomyDistribution).map(([level, count], i) => {
              const ac = AUTONOMY_STYLES[level] || AUTONOMY_STYLES.RESTRICTED;
              return (
                <div key={level} style={{
                  padding: '16px 12px', borderRadius: 10, background: ac.bg,
                  border: `1px solid ${ac.border}`, textAlign: 'center',
                  animation: 'pcpSlideUp 0.2s ease', animationDelay: `${0.2 + i * 0.05}s`, animationFillMode: 'backwards',
                }}>
                  <div style={{ fontSize: 26, fontWeight: 800, color: ac.text, lineHeight: 1, marginBottom: 4 }}>{count}</div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: ac.text }}>{ac.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Two-Column: Alerts + Promotion Ready ── */}
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16,
        animation: 'pcpSlideUp 0.3s ease', animationDelay: '0.25s', animationFillMode: 'backwards',
      }}>
        {/* Control Alerts */}
        <div style={{ background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12, padding: '18px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: ACCENT.rose.bg, border: `1px solid ${ACCENT.rose.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <AlertTriangle size={14} style={{ color: ACCENT.rose.icon }} />
            </div>
            <span style={{ fontSize: 13, fontWeight: 700, color: S[900] }}>Control Alerts</span>
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 6, marginLeft: 4,
              background: alerts.length > 0 ? ACCENT.rose.bg : ACCENT.emerald.bg,
              color: alerts.length > 0 ? ACCENT.rose.text : ACCENT.emerald.text,
              border: `1px solid ${alerts.length > 0 ? ACCENT.rose.border : ACCENT.emerald.border}`,
            }}>{alerts.length}</span>
          </div>
          {alerts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '28px 16px', color: S[400] }}>
              <CheckCircle size={24} style={{ marginBottom: 8, opacity: 0.3 }} />
              <div style={{ fontSize: 13, fontWeight: 600, color: S[600] }}>All agents above threshold</div>
              <div style={{ fontSize: 11, color: S[500], marginTop: 2 }}>No agents scoring below 75%</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {alerts.slice(0, 6).map(a => (
                <div key={a.id} onClick={() => loadAgentDetail(a.agentId)} className="pcp-card" style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
                  borderRadius: 8, border: `1px solid ${S[100]}`, cursor: 'pointer',
                  background: S[25], transition: 'all 0.15s',
                }}>
                  <div style={{
                    fontSize: 15, fontWeight: 700, color: ACCENT.rose.icon,
                    fontFeatureSettings: "'tnum'", minWidth: 52,
                  }}>{a.score}%</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: S[800], overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {findAgentName(a.agentId)}
                    </div>
                    <div style={{ fontSize: 11, color: S[500] }}>
                      {a.blockCount} blocks · {a.warnCount} warns
                    </div>
                  </div>
                  <ChevronRight size={14} color={S[400]} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Promotion Eligible */}
        <div style={{ background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12, padding: '18px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: ACCENT.emerald.bg, border: `1px solid ${ACCENT.emerald.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ArrowUpCircle size={14} style={{ color: ACCENT.emerald.icon }} />
            </div>
            <span style={{ fontSize: 13, fontWeight: 700, color: S[900] }}>Promotion Eligible</span>
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 6, marginLeft: 4,
              background: ACCENT.emerald.bg, color: ACCENT.emerald.text, border: `1px solid ${ACCENT.emerald.border}`,
            }}>{promotionEligible.length}</span>
          </div>
          {promotionEligible.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '28px 16px', color: S[400] }}>
              <ArrowUpCircle size={24} style={{ marginBottom: 8, opacity: 0.3 }} />
              <div style={{ fontSize: 13, fontWeight: 600, color: S[600] }}>No agents eligible</div>
              <div style={{ fontSize: 11, color: S[500], marginTop: 2 }}>Agents need ≥90% score and ≥100 evals</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {promotionEligible.slice(0, 6).map(a => (
                <div key={a.id} onClick={() => loadAgentDetail(a.agentId)} className="pcp-card" style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
                  borderRadius: 8, border: `1px solid ${S[100]}`, cursor: 'pointer',
                  background: S[25], transition: 'all 0.15s',
                }}>
                  <div style={{
                    fontSize: 15, fontWeight: 700, color: ACCENT.emerald.icon,
                    fontFeatureSettings: "'tnum'", minWidth: 52,
                  }}>{a.score}%</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: S[800], overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {findAgentName(a.agentId)}
                    </div>
                    <div style={{ fontSize: 11, color: S[500] }}>
                      {a.totalEvaluations} evals — {a.recommendedAutonomy?.replace(/_/g, ' ')}
                    </div>
                  </div>
                  <ChevronRight size={14} color={S[400]} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── G01/G02/G15: Agent Pipeline Management ── */}
      <div style={{
        marginTop: 20, background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12,
        animation: 'pcpSlideUp 0.3s ease', animationDelay: '0.3s', animationFillMode: 'backwards',
      }}>
        <div
          onClick={() => setShowPipelineSection(!showPipelineSection)}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '14px 20px', cursor: 'pointer',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: ACCENT.violet.bg, border: `1px solid ${ACCENT.violet.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Layers size={14} style={{ color: ACCENT.violet.icon }} />
            </div>
            <span style={{ fontSize: 14, fontWeight: 700, color: S[900] }}>Agent Pipelines</span>
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 6,
              background: ACCENT.violet.bg, color: ACCENT.violet.text, border: `1px solid ${ACCENT.violet.border}`,
            }}>
              {pipelines.filter(p => p.status === 'ACTIVE').length} active
            </span>
          </div>
          <div style={{ transform: showPipelineSection ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.15s', color: S[400] }}>
            <ChevronDown size={16} />
          </div>
        </div>

        {showPipelineSection && (
          <div style={{ padding: '0 20px 18px', borderTop: `1px solid ${S[100]}`, animation: 'pcpFadeIn 0.2s ease' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 14, marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: S[500] }}>Multi-agent workflows with composite compliance scoring and effective autonomy.</div>
              {!showPipelineCreate && (
                <button onClick={() => setShowPipelineCreate(true)} style={{
                  ...btnBase, padding: '6px 14px', fontSize: 11, fontWeight: 600,
                  background: BRAND.primary, color: '#fff', borderRadius: 6,
                }}>
                  <Plus size={12} /> New Pipeline
                </button>
              )}
            </div>

            {/* Create Pipeline Form */}
            {showPipelineCreate && (
              <div style={{ padding: 14, borderRadius: 10, border: `1px solid ${S[200]}`, background: S[25], marginBottom: 14 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 600, color: S[600], marginBottom: 4, display: 'block' }}>Pipeline Name *</label>
                    <input value={pipelineForm.name} onChange={e => setPipelineForm(f => ({ ...f, name: e.target.value }))}
                      placeholder="e.g., Claims Processing Pipeline" style={inputBase} />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 600, color: S[600], marginBottom: 4, display: 'block' }}>Type</label>
                    <select value={pipelineForm.pipelineType} onChange={e => setPipelineForm(f => ({ ...f, pipelineType: e.target.value }))} style={inputBase}>
                      <option value="SEQUENTIAL">Sequential</option>
                      <option value="PARALLEL">Parallel</option>
                      <option value="DAG">DAG (Directed)</option>
                    </select>
                  </div>
                </div>
                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: S[600], marginBottom: 4, display: 'block' }}>Description</label>
                  <input value={pipelineForm.description} onChange={e => setPipelineForm(f => ({ ...f, description: e.target.value }))}
                    placeholder="Pipeline purpose and workflow description" style={inputBase} />
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: S[600], marginBottom: 4, display: 'block' }}>Agent IDs (comma-separated, in execution order)</label>
                  <input value={pipelineForm.agentIds} onChange={e => setPipelineForm(f => ({ ...f, agentIds: e.target.value }))}
                    placeholder="agent-uuid-1, agent-uuid-2, agent-uuid-3" style={inputBase} />
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button onClick={() => setShowPipelineCreate(false)} style={{ ...btnBase, padding: '6px 14px', fontSize: 11, fontWeight: 600, background: S[100], color: S[600], borderRadius: 6 }}>
                    Cancel
                  </button>
                  <button onClick={handleCreatePipeline} disabled={savingPipeline || !pipelineForm.name}
                    style={{ ...btnBase, padding: '6px 14px', fontSize: 11, fontWeight: 600, background: BRAND.primary, color: '#fff', borderRadius: 6, opacity: savingPipeline ? 0.5 : 1 }}>
                    {savingPipeline ? 'Creating…' : 'Create Pipeline'}
                  </button>
                </div>
              </div>
            )}

            {/* Pipeline List */}
            {pipelines.length === 0 && !showPipelineCreate ? (
              <div style={{ textAlign: 'center', padding: '32px 0' }}>
                <Layers size={28} style={{ color: S[300], marginBottom: 8 }} />
                <div style={{ fontSize: 13, color: S[500] }}>No pipelines configured</div>
                <div style={{ fontSize: 11, color: S[400], marginTop: 2 }}>Create a pipeline to track multi-agent workflow compliance.</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {pipelines.map(p => {
                  // `|| 0` is deliberately gone: a pipeline with no score yet is unrated, not
                  // zero, and must not be painted as a failing one.
                  const scoreBand = autonomyBandStyle(p.complianceScore);
                  const scoreColor = scoreBand.accent;
                  const autoStyle = AUTONOMY_STYLES[p.effectiveAutonomyLevel] || AUTONOMY_STYLES.RESTRICTED;
                  return (
                    <div key={p.id} style={{
                      padding: '14px 16px', borderRadius: 10, border: `1px solid ${S[200]}`, background: '#fff',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                            <span style={{ fontSize: 14, fontWeight: 700, color: S[900] }}>{p.name}</span>
                            <span style={{
                              fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 3,
                              background: p.status === 'ACTIVE' ? ACCENT.emerald.bg : S[100],
                              color: p.status === 'ACTIVE' ? ACCENT.emerald.text : S[500],
                            }}>{p.status}</span>
                            <span style={{
                              fontSize: 9, fontWeight: 600, padding: '1px 6px', borderRadius: 3,
                              background: ACCENT.sky.bg, color: ACCENT.sky.text,
                            }}>{p.pipelineType || 'SEQUENTIAL'}</span>
                          </div>
                          {p.description && <div style={{ fontSize: 11, color: S[500], marginBottom: 6 }}>{p.description}</div>}
                          <div style={{ display: 'flex', gap: 16, fontSize: 12, alignItems: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <span style={{ fontWeight: 700, color: scoreColor.icon, fontFeatureSettings: "'tnum'" }}>
                                {p.complianceScore != null ? `${p.complianceScore.toFixed(1)}%` : '—'}
                              </span>
                              <span style={{ fontSize: 10, color: S[400] }}>score</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <span style={{
                                fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 3,
                                background: autoStyle.bg, color: autoStyle.text, border: `1px solid ${autoStyle.border}`,
                              }}>{autoStyle.label}</span>
                              <span style={{ fontSize: 10, color: S[400] }}>effective autonomy</span>
                            </div>
                            <div style={{ fontSize: 11, color: S[400] }}>
                              {(p.agentIds || []).length} agents
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                          <button onClick={() => handleRecalculatePipeline(p.id)} title="Recalculate scores" style={{
                            ...btnBase, padding: '5px 8px', fontSize: 10, fontWeight: 600,
                            background: S[50], color: S[500], borderRadius: 5,
                          }}>
                            <RefreshCw size={11} />
                          </button>
                          <button onClick={() => handleArchivePipeline(p.id)} title="Archive pipeline" style={{
                            ...btnBase, padding: '5px 8px', fontSize: 10, fontWeight: 600,
                            background: S[50], color: S[400], borderRadius: 5,
                          }}>
                            <Archive size={11} />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// CONTROL CONFIG PANEL
// ═══════════════════════════════════════════════════════════════════════════════

function ControlConfigPanel() {
  const { session, memberships, isSuperAdmin } = useAuth();
  const [scopeOrg, setScopeOrg] = useState(session?.orgId || '');
  const [scopeTenant, setScopeTenant] = useState(session?.tenantId || '');
  const [scopeAgent, setScopeAgent] = useState('');
  const [agents, setAgents] = useState([]);
  const [effective, setEffective] = useState([]);
  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [availableFrameworks, setAvailableFrameworks] = useState([]);
  const [selectedFrameworkId, setSelectedFrameworkId] = useState('');
  const [complianceLevel, setComplianceLevel] = useState('REQUIRED');
  const [assigning, setAssigning] = useState(false);
  const [inheritanceChain, setInheritanceChain] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rulesPanel, setRulesPanel] = useState({ open: false, frameworkId: null, frameworkName: '' });
  const [enforcementRules, setEnforcementRules] = useState([]);
  const [loadingRules, setLoadingRules] = useState(false);
  const [togglingRuleId, setTogglingRuleId] = useState(null);
  const [orgNameMap, setOrgNameMap] = useState({});
  const [tenantNameMap, setTenantNameMap] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const [orgRes, tenantRes] = await Promise.all([api.getOrganizations(), api.getTenants()]);
        if (orgRes.ok && Array.isArray(orgRes.data)) {
          const map = {}; orgRes.data.forEach(o => { if (o.id && o.name) map[o.id] = o.name; }); setOrgNameMap(map);
        }
        if (tenantRes.ok && Array.isArray(tenantRes.data)) {
          const map = {}; tenantRes.data.forEach(t => { if (t.id && t.name) map[t.id] = t.name; }); setTenantNameMap(map);
        }
      } catch (_) {}
    })();
  }, []);

  const _isSuperAdmin = typeof isSuperAdmin === 'function' ? isSuperAdmin() : false;
  const resolveOrgName = useCallback((id) => {
    const m = (memberships || []).find(m => m.orgId === id);
    return m?.orgName || orgNameMap[id] || `Org ${(id || '').substring(0, 8)}`;
  }, [memberships, orgNameMap]);
  const resolveTenantName = useCallback((id) => {
    const m = (memberships || []).find(m => m.tenantId === id);
    return m?.tenantName || tenantNameMap[id] || `Tenant ${(id || '').substring(0, 8)}`;
  }, [memberships, tenantNameMap]);

  const orgOptions = useMemo(() => {
    const orgs = new Map();
    if (_isSuperAdmin && orgNameMap && Object.keys(orgNameMap).length > 0) Object.entries(orgNameMap).forEach(([id, n]) => orgs.set(id, n));
    (memberships || []).forEach(m => { if (m.orgId && !orgs.has(m.orgId)) orgs.set(m.orgId, resolveOrgName(m.orgId)); });
    if (session?.orgId && !orgs.has(session.orgId)) orgs.set(session.orgId, resolveOrgName(session.orgId));
    return Array.from(orgs.entries());
  }, [memberships, session?.orgId, resolveOrgName, _isSuperAdmin, orgNameMap]);

  const [orgTenants, setOrgTenants] = useState([]);
  useEffect(() => {
    setOrgTenants([]);
    if (scopeOrg) {
      api.getTenantsByOrg(scopeOrg).then(res => {
        if (res.ok && Array.isArray(res.data)) {
          setOrgTenants(res.data);
          const nn = {}; res.data.forEach(t => { if (t.id && t.name) nn[t.id] = t.name; });
          setTenantNameMap(prev => ({ ...prev, ...nn }));
        } else setOrgTenants([]);
      }).catch(() => setOrgTenants([]));
    }
  }, [scopeOrg]);

  const tenantOptions = useMemo(() => {
    const tenants = new Map();
    if (scopeOrg) {
      orgTenants.forEach(t => { if (t.id) tenants.set(t.id, t.name || resolveTenantName(t.id)); });
      (memberships || []).forEach(m => { if (m.tenantId && m.orgId === scopeOrg && !tenants.has(m.tenantId)) tenants.set(m.tenantId, resolveTenantName(m.tenantId)); });
    } else {
      (memberships || []).forEach(m => { if (m.tenantId && !tenants.has(m.tenantId)) tenants.set(m.tenantId, resolveTenantName(m.tenantId)); });
    }
    return Array.from(tenants.entries());
  }, [memberships, scopeOrg, resolveTenantName, orgTenants]);

  useEffect(() => {
    setAgents([]);
    (async () => {
      try {
        // Fetch agents directly registered + agents deployed to instances
        const [agentRes, deployRes] = await Promise.all([
          api.getAgents({ orgId: scopeOrg || undefined, tenantId: scopeTenant || undefined }),
          api.getDeployments({ orgId: scopeOrg || undefined, tenantId: scopeTenant || undefined }),
        ]);
        const agentMap = new Map();
        // Direct agents — these have proper names (exclude system agents)
        const directAgents = (agentRes.ok ? (Array.isArray(agentRes.data) ? agentRes.data : agentRes.data?.agents || []) : [])
          .filter(a => !a.isSystemAgent && a.category !== 'system');
        directAgents.forEach(a => { if (a.id) agentMap.set(a.id, a); });

        // Agents from deployments (deployed through instances/environments)
        const deployments = deployRes.ok ? (Array.isArray(deployRes.data) ? deployRes.data : deployRes.data?.deployments || []) : [];
        // Collect deployment agent IDs that aren't in the direct list — need name resolution
        const missingIds = [];
        deployments.forEach(d => {
          const aId = d.agentId || d.agent?.id;
          if (aId && !agentMap.has(aId)) {
            const knownName = d.agentName || d.agent?.name;
            if (knownName) {
              agentMap.set(aId, { id: aId, name: knownName, environment: d.environment, status: d.status });
            } else {
              missingIds.push({ aId, environment: d.environment, status: d.status });
            }
          }
        });

        // Resolve missing agent names individually
        if (missingIds.length > 0) {
          const lookups = await Promise.allSettled(
            missingIds.map(({ aId }) => api.getAgent(aId))
          );
          missingIds.forEach(({ aId, environment, status }, idx) => {
            const result = lookups[idx];
            const agentData = result.status === 'fulfilled' && result.value?.ok ? result.value.data : null;
            if (!agentMap.has(aId)) {
              agentMap.set(aId, {
                id: aId,
                name: agentData?.name || `Agent ${aId.substring(0, 8)}…`,
                environment,
                status,
              });
            }
          });
        }

        setAgents(Array.from(agentMap.values()));
      } catch (_) {}
    })();
  }, [scopeOrg, scopeTenant]);

  useEffect(() => {
    setEffective([]); setInheritanceChain(null);
    (async () => {
      setLoading(true);
      try {
        const params = {};
        if (scopeOrg) params.orgId = scopeOrg;
        if (scopeTenant) params.tenantId = scopeTenant;
        if (scopeAgent) params.agentId = scopeAgent;
        const fetches = [api.ccGetEffectiveCompliance(params), api.ccGetInheritanceChain(scopeAgent || undefined, scopeTenant || undefined, scopeOrg || undefined)];
        // Also fetch FrameworkBindings for the selected agent (cross-visibility with GEL wizard)
        if (scopeAgent) fetches.push(api.ccGetAgentFrameworkBindings(scopeAgent));
        const [cfgRes, chainRes, bindingsRes] = await Promise.all(fetches);
        let effectiveData = [];
        if (cfgRes.ok) { const d = cfgRes.data; effectiveData = Array.isArray(d) ? d : d?.frameworks || d?.configs || []; }
        // Merge FrameworkBindings that have no matching ComplianceConfig (e.g., created via GEL wizard)
        if (bindingsRes?.ok || (bindingsRes?.data && !bindingsRes?.error)) {
          const bindings = Array.isArray(bindingsRes?.data ?? bindingsRes) ? (bindingsRes?.data ?? bindingsRes) : [];
          const existingFwIds = new Set(effectiveData.map(e => e.frameworkId || e.id));
          for (const b of bindings) {
            if (b.frameworkId && !existingFwIds.has(b.frameworkId) && b.status !== 'ARCHIVED' && b.status !== 'FAILED') {
              effectiveData.push({
                id: b.id, frameworkId: b.frameworkId, name: b.frameworkName || b.frameworkCode,
                frameworkName: b.frameworkName, frameworkCode: b.frameworkCode,
                complianceLevel: 'REQUIRED', controlCount: b.controlMappings?.length || 0,
                _source: 'GEL_BINDING', _bindingStatus: b.status, _bindingId: b.id,
              });
            }
          }
        }
        setEffective(effectiveData);
        if (chainRes.ok) setInheritanceChain(chainRes.data);
      } catch (_) {}
      setLoading(false);
    })();
  }, [scopeOrg, scopeTenant, scopeAgent]);

  const loadAvailableFrameworks = useCallback(async () => {
    try {
      const res = await api.ccListFrameworks();
      if (res.ok) setAvailableFrameworks(Array.isArray(res.data) ? res.data : res.data?.frameworks || []);
    } catch (_) {}
  }, []);

  const handleAssign = async () => {
    if (!selectedFrameworkId) return;
    setAssigning(true);
    try {
      const params = { frameworkId: selectedFrameworkId, complianceLevel };
      if (scopeOrg) params.orgId = scopeOrg;
      if (scopeTenant) params.tenantId = scopeTenant;
      if (scopeAgent) params.agentId = scopeAgent;
      const res = await api.ccAddFrameworkRequirement(params);
      if (res.ok) {
        setIsAssignOpen(false); setSelectedFrameworkId('');
        const cfgRes = await api.ccGetEffectiveCompliance({ orgId: scopeOrg || undefined, tenantId: scopeTenant || undefined, agentId: scopeAgent || undefined });
        if (cfgRes.ok) { const d = cfgRes.data; setEffective(Array.isArray(d) ? d : d?.frameworks || d?.configs || []); }
      }
    } catch (_) {}
    setAssigning(false);
  };

  const handleRemoveFramework = async (fw) => {
    if (!fw) return;
    // Send the best ID we have — backend handles both ComplianceConfig IDs and FrameworkBinding IDs
    // and cascades through the full provenance chain:
    //   ComplianceConfig → FrameworkBinding → PolicyRules → PolicyBindings
    const removeId = fw.configId || fw._bindingId || fw.id;
    if (!removeId) return;

    try {
      const res = await api.ccRemoveConfig(removeId);
      if (!res.ok) console.warn('[Remove] Backend returned error:', res.error);

      // Reload effective compliance regardless — binding may have been archived server-side
      const cfgRes = await api.ccGetEffectiveCompliance({ orgId: scopeOrg || undefined, tenantId: scopeTenant || undefined, agentId: scopeAgent || undefined });
      if (cfgRes.ok) { const d = cfgRes.data; setEffective(Array.isArray(d) ? d : d?.frameworks || d?.configs || []); }
    } catch (_) {}
  };

  const openRulesPanel = async (fwId, fwName) => {
    setRulesPanel({ open: true, frameworkId: fwId, frameworkName: fwName }); setLoadingRules(true);
    try {
      const res = await api.ccGetFrameworkEnforcementRules(fwId);
      if (res.ok) {
        const raw = Array.isArray(res.data) ? res.data : res.data?.rules || [];
        setEnforcementRules(raw.map(r => ({ ...r, id: r.id || r.ruleId })));
      }
    } catch (_) {}
    setLoadingRules(false);
  };

  const toggleRule = async (ruleId, currentEnabled) => {
    setTogglingRuleId(ruleId);
    try {
      const res = await api.ccToggleEnforcementRule(ruleId, !currentEnabled);
      if (res.ok) setEnforcementRules(prev => prev.map(r => r.id === ruleId ? { ...r, enabled: !currentEnabled } : r));
    } catch (_) {}
    setTogglingRuleId(null);
  };

  const assignedCount = Array.isArray(effective) ? effective.length : 0;
  const requiredCount = Array.isArray(effective) ? effective.filter(f => f.complianceLevel === 'REQUIRED').length : 0;
  const totalControlsAssigned = Array.isArray(effective) ? effective.reduce((s, f) => s + (f.controlCount || 0), 0) : 0;

  if (loading) return (
    <div style={{ padding: '48px 0', textAlign: 'center' }}>
      <Loader size={24} style={{ animation: 'pcpSpin 1s linear infinite', color: BRAND.primary, margin: '0 auto' }} />
      <div style={{ fontSize: 13, color: S[500], marginTop: 12 }}>Loading control config…</div>
    </div>
  );

  return (
    <div style={{ animation: 'pcpFadeIn 0.3s ease' }}>
      {/* ── Hero Stat Cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 }}>
        {[
          { label: 'Assigned Frameworks', value: assignedCount, accent: ACCENT.sky, Icon: Target },
          { label: 'Required', value: requiredCount, accent: ACCENT.rose, Icon: Lock },
          { label: 'Controls Covered', value: totalControlsAssigned, accent: ACCENT.violet, Icon: Shield },
          { label: 'Agents in Scope', value: agents.length, accent: ACCENT.emerald, Icon: Bot },
        ].map((stat, i) => (
          <div key={stat.label} className="pcp-stat-card" style={{
            background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12,
            padding: '18px 20px', position: 'relative', overflow: 'hidden',
            animation: 'pcpSlideUp 0.3s ease', animationDelay: `${i * 0.06}s`, animationFillMode: 'backwards',
          }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: stat.accent.icon, opacity: 0.15 }} />
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: stat.accent.text, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>{stat.label}</div>
                <div style={{ fontSize: 30, fontWeight: 800, color: S[900], lineHeight: 1, letterSpacing: '-0.03em', fontFeatureSettings: "'tnum'" }}>{stat.value}</div>
              </div>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: stat.accent.bg, border: `1px solid ${stat.accent.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <stat.Icon size={18} style={{ color: stat.accent.icon }} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Scope Selector ── */}
      <div style={{
        background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12, padding: '18px 20px', marginBottom: 20,
        animation: 'pcpSlideUp 0.3s ease', animationDelay: '0.15s', animationFillMode: 'backwards',
      }}>
        <div style={{ fontSize: 11, fontWeight: 800, color: S[400], textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>
          Assignment Scope
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: S[600], marginBottom: 6 }}>
              <Building2 size={13} color={ACCENT.sky.icon} /> Organization
            </label>
            <select value={scopeOrg} onChange={e => { setScopeOrg(e.target.value); setScopeTenant(''); setScopeAgent(''); }} style={{ ...inputBase }}>
              <option value="">— All Organizations —</option>
              {orgOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: S[600], marginBottom: 6 }}>
              <Users size={13} color={ACCENT.violet.icon} /> Tenant
            </label>
            <select value={scopeTenant} onChange={e => { setScopeTenant(e.target.value); setScopeAgent(''); }} disabled={!scopeOrg} style={{ ...inputBase, opacity: scopeOrg ? 1 : 0.5 }}>
              <option value="">— All Tenants —</option>
              {tenantOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: S[600], marginBottom: 6 }}>
              <Bot size={13} color={ACCENT.emerald.icon} /> Agent
            </label>
            <select value={scopeAgent} onChange={e => setScopeAgent(e.target.value)} style={{ ...inputBase }}>
              <option value="">— All Agents —</option>
              {agents.map(a => (
                <option key={a.id} value={a.id}>
                  {a.name || a.id}{a.environment ? ` (${a.environment})` : ''}{a.status ? ` — ${a.status}` : ''}
                  {a.isExternal ? ' [EXT]' : ''}{a.agentClassification && a.agentClassification !== 'INTERNAL' ? ` (${a.agentClassification})` : ''}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* ── G04: External Agent Classification ── */}
      {scopeAgent && (() => {
        const selAgent = agents.find(a => a.id === scopeAgent);
        if (!selAgent) return null;
        // isExternal overrides default "INTERNAL" classification (backend defaults agentClassification to INTERNAL even for external agents)
        const cls = (selAgent.isExternal && (!selAgent.agentClassification || selAgent.agentClassification === 'INTERNAL'))
          ? 'EXTERNAL_API'
          : (selAgent.agentType === 'EXTERNAL' && (!selAgent.agentClassification || selAgent.agentClassification === 'INTERNAL'))
            ? 'EXTERNAL_API'
            : selAgent.agentClassification || 'INTERNAL';
        const clsStyle = {
          INTERNAL: { bg: ACCENT.emerald.bg, text: ACCENT.emerald.text, border: ACCENT.emerald.border, label: 'Internal' },
          EXTERNAL_API: { bg: ACCENT.violet.bg, text: ACCENT.violet.text, border: ACCENT.violet.border, label: 'External (API)' },
          EXTERNAL_SDK: { bg: ACCENT.sky.bg, text: ACCENT.sky.text, border: ACCENT.sky.border, label: 'External (SDK)' },
          HYBRID: { bg: ACCENT.amber.bg, text: ACCENT.amber.text, border: ACCENT.amber.border, label: 'Hybrid' },
        }[cls] || { bg: S[100], text: S[600], border: S[200], label: cls };

        return (
          <div style={{
            padding: '10px 16px', borderRadius: 8, marginBottom: 16,
            background: clsStyle.bg, border: `1px solid ${clsStyle.border}`,
            display: 'flex', alignItems: 'center', gap: 10, fontSize: 12,
            animation: 'pcpSlideUp 0.2s ease',
          }}>
            <Globe2 size={14} style={{ color: clsStyle.text }} />
            <span style={{ fontWeight: 700, color: clsStyle.text }}>{clsStyle.label}</span>
            {selAgent.vendorName && <span style={{ color: clsStyle.text, opacity: 0.8 }}>· {selAgent.vendorName}</span>}
            {selAgent.compatibilityStatus && (
              <span style={{
                fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 3, marginLeft: 4,
                background: selAgent.compatibilityStatus === 'VERIFIED' ? ACCENT.emerald.bg : selAgent.compatibilityStatus === 'INCOMPATIBLE' ? ACCENT.rose.bg : ACCENT.amber.bg,
                color: selAgent.compatibilityStatus === 'VERIFIED' ? ACCENT.emerald.text : selAgent.compatibilityStatus === 'INCOMPATIBLE' ? ACCENT.rose.text : ACCENT.amber.text,
              }}>
                {selAgent.compatibilityStatus}
              </span>
            )}
            {selAgent.trustScore != null && (
              <span style={{ marginLeft: 'auto', fontWeight: 700, fontFeatureSettings: "'tnum'", color: clsStyle.text }}>
                Trust: {selAgent.trustScore}%
              </span>
            )}
          </div>
        );
      })()}

      {/* ── Inheritance Chain ── */}
      {inheritanceChain && (
        <div style={{
          padding: '10px 16px', borderRadius: 8, marginBottom: 16,
          background: ACCENT.sky.bg, border: `1px solid ${ACCENT.sky.border}`,
          display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: ACCENT.sky.text,
          animation: 'pcpSlideUp 0.2s ease',
        }}>
          <Link2 size={14} /> <strong>Inheritance:</strong> {inheritanceChain.chain?.join(' → ') || 'Direct assignment'}
        </div>
      )}

      {/* ── Assigned Frameworks Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 800, color: S[400], textTransform: 'uppercase', letterSpacing: '0.1em' }}>
          Assigned Frameworks
        </div>
        <button className="pcp-btn-primary" onClick={() => { setIsAssignOpen(true); setSelectedFrameworkId(''); loadAvailableFrameworks(); }} style={{
          ...btnBase, padding: '8px 16px', fontSize: 12, fontWeight: 600,
          background: BRAND.gradient, color: '#fff', borderRadius: 8,
        }}>
          <Plus size={14} /> Assign Framework
        </button>
      </div>

      {/* ── Assigned Frameworks Grid ── */}
      {(!Array.isArray(effective) || effective.length === 0) ? (
        <div style={{ textAlign: 'center', padding: '48px 24px', color: S[500] }}>
          <Target size={32} style={{ marginBottom: 12, opacity: 0.3 }} />
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4, color: S[700] }}>No frameworks assigned</div>
          <div style={{ fontSize: 12, color: S[500] }}>Assign compliance frameworks to this scope to get started.</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12 }}>
          {effective.map((fw, i) => {
            const levelInfo = COMPLIANCE_LEVELS.find(cl => cl.value === fw.complianceLevel) || COMPLIANCE_LEVELS[0];
            return (
              <div key={fw.id || i} className="pcp-card" style={{
                background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12, overflow: 'hidden',
                animation: 'pcpSlideUp 0.3s ease', animationDelay: `${i * 0.04}s`, animationFillMode: 'backwards',
              }}>
                <div style={{ height: 3, background: levelInfo.accent.icon, opacity: 0.4 }} />
                <div style={{ padding: '16px 18px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: S[900], lineHeight: 1.3, marginBottom: 4 }}>{fw.name || fw.frameworkName}</div>
                      <div style={{ fontSize: 11, color: S[500], display: 'flex', gap: 6, alignItems: 'center' }}>
                        {fw.controlCount || 0} controls
                        {fw._source === 'GEL_BINDING' && (
                          <span style={{
                            fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 3,
                            background: 'linear-gradient(90deg, #f97316, #f59e0b)', color: '#fff',
                          }}>GEL Bridge</span>
                        )}
                        {fw._bindingStatus && (
                          <span style={{
                            fontSize: 9, fontWeight: 600, padding: '1px 5px', borderRadius: 3,
                            background: fw._bindingStatus === 'ACTIVE' ? ACCENT.emerald.bg : fw._bindingStatus === 'TESTING' ? ACCENT.amber.bg : S[100],
                            color: fw._bindingStatus === 'ACTIVE' ? ACCENT.emerald.text : fw._bindingStatus === 'TESTING' ? ACCENT.amber.text : S[600],
                          }}>{fw._bindingStatus}</span>
                        )}
                      </div>
                    </div>
                    <span style={{
                      fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 4,
                      background: levelInfo.accent.bg, color: levelInfo.accent.text,
                      border: `1px solid ${levelInfo.accent.border}`,
                    }}>
                      {levelInfo.label}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 4, marginTop: 10 }}>
                    <button className="pcp-action-btn" onClick={() => openRulesPanel(fw.frameworkId || fw.id, fw.name || fw.frameworkName)} style={{
                      ...btnBase, padding: '5px 10px', fontSize: 10, fontWeight: 600,
                      background: S[50], color: S[600], borderRadius: 5, flex: 1, justifyContent: 'center',
                    }}>
                      <Zap size={10} /> Enforcement Rules
                    </button>
                    <button className="pcp-action-btn" onClick={() => handleRemoveFramework(fw)} style={{
                      ...btnBase, padding: '5px 10px', fontSize: 10, fontWeight: 600,
                      background: ACCENT.rose.bg, color: ACCENT.rose.text, borderRadius: 5,
                    }}>
                      <Trash2 size={10} /> Remove
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Assign Framework Modal ── */}
      <Modal isOpen={isAssignOpen} onClose={() => setIsAssignOpen(false)} title="Assign Framework to Scope">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: S[600], marginBottom: 6 }}>Framework</label>
            <select value={selectedFrameworkId} onChange={e => setSelectedFrameworkId(e.target.value)} style={{ ...inputBase }}>
              <option value="">— Select Framework —</option>
              {availableFrameworks.map(fw => <option key={fw.id} value={fw.id}>{fw.name}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: S[600], marginBottom: 6 }}>Compliance Level</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {COMPLIANCE_LEVELS.map(cl => (
                <button key={cl.value} onClick={() => setComplianceLevel(cl.value)} style={{
                  ...btnBase, flex: 1, padding: '10px 12px', borderRadius: 8, justifyContent: 'center',
                  flexDirection: 'column', gap: 4,
                  background: complianceLevel === cl.value ? cl.accent.bg : '#fff',
                  color: complianceLevel === cl.value ? cl.accent.text : S[500],
                  border: `2px solid ${complianceLevel === cl.value ? cl.accent.icon : S[200]}`,
                  transition: 'all 0.15s ease',
                }}>
                  <span style={{ fontSize: 12, fontWeight: 700 }}>{cl.label}</span>
                  <span style={{ fontSize: 10, fontWeight: 400, lineHeight: 1.3, textAlign: 'center' }}>{cl.desc}</span>
                </button>
              ))}
            </div>
          </div>
          <div style={{
            padding: '10px 14px', borderRadius: 8, background: S[50], border: `1px solid ${S[200]}`,
            fontSize: 11, color: S[500],
          }}>
            <strong>Scope:</strong>{' '}
            {scopeOrg ? resolveOrgName(scopeOrg) : 'All Orgs'}
            {scopeTenant ? ` → ${resolveTenantName(scopeTenant)}` : ''}
            {scopeAgent ? ` → ${(agents.find(a => a.id === scopeAgent)?.name || scopeAgent)}` : ''}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <button className="pcp-btn-primary" onClick={handleAssign} disabled={assigning || !selectedFrameworkId} style={{
              ...btnBase, padding: '9px 20px', fontSize: 12, fontWeight: 600,
              background: (assigning || !selectedFrameworkId) ? S[300] : BRAND.gradient, color: '#fff', borderRadius: 8,
            }}>
              {assigning ? 'Assigning…' : 'Assign Framework'}
            </button>
            <button className="pcp-btn-ghost" onClick={() => setIsAssignOpen(false)} style={{
              ...btnBase, padding: '9px 16px', fontSize: 12, fontWeight: 600,
              background: 'transparent', color: S[600], borderRadius: 8,
            }}>
              Cancel
            </button>
          </div>
        </div>
      </Modal>

      {/* ── Enforcement Rules Modal ── */}
      <Modal isOpen={rulesPanel.open} onClose={() => setRulesPanel({ ...rulesPanel, open: false })} title={`Enforcement Rules: ${rulesPanel.frameworkName}`}>
        {loadingRules ? (
          <div style={{ textAlign: 'center', padding: 32 }}>
            <Loader size={20} style={{ animation: 'pcpSpin 1s linear infinite', color: BRAND.primary }} />
          </div>
        ) : enforcementRules.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 32, color: S[500] }}>
            <Zap size={24} style={{ marginBottom: 8, opacity: 0.3 }} />
            <div style={{ fontSize: 13, fontWeight: 600 }}>No enforcement rules</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {/* ── Select All / Unselect All Bar ── */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '8px 14px', background: ACCENT.sky.bg, borderRadius: 8, border: `1px solid ${ACCENT.sky.border}`,
            }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: ACCENT.sky.text }}>
                {enforcementRules.filter(r => r.enabled).length} of {enforcementRules.length} rules enabled
              </span>
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  onClick={async () => {
                    const ids = enforcementRules.filter(r => !r.enabled).map(r => r.id);
                    if (ids.length === 0) return;
                    try {
                      const res = await api.ccBulkToggleEnforcementRules(ids, true);
                      if (res.ok) setEnforcementRules(prev => prev.map(r => ({ ...r, enabled: true })));
                    } catch (_) {}
                  }}
                  style={{
                    ...btnBase, padding: '4px 12px', fontSize: 11, fontWeight: 600, borderRadius: 6,
                    background: ACCENT.emerald.bg, color: ACCENT.emerald.text, border: `1px solid ${ACCENT.emerald.border}`,
                  }}
                >
                  <CheckCircle size={12} /> Select All
                </button>
                <button
                  onClick={async () => {
                    const ids = enforcementRules.filter(r => r.enabled).map(r => r.id);
                    if (ids.length === 0) return;
                    try {
                      const res = await api.ccBulkToggleEnforcementRules(ids, false);
                      if (res.ok) setEnforcementRules(prev => prev.map(r => ({ ...r, enabled: false })));
                    } catch (_) {}
                  }}
                  style={{
                    ...btnBase, padding: '4px 12px', fontSize: 11, fontWeight: 600, borderRadius: 6,
                    background: S[100], color: S[600], border: `1px solid ${S[200]}`,
                  }}
                >
                  <X size={12} /> Unselect All
                </button>
              </div>
            </div>

            {/* ── Individual Rules ── */}
            {enforcementRules.map(rule => (
              <div key={rule.id} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '12px 14px', background: S[25], borderRadius: 8, border: `1px solid ${S[100]}`,
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: S[900] }}>{rule.name}</div>
                  {rule.description && <div style={{ fontSize: 11, color: S[500], marginTop: 2 }}>{rule.description}</div>}
                </div>
                <button onClick={() => toggleRule(rule.id, rule.enabled || false)} disabled={togglingRuleId === rule.id} style={{
                  ...btnBase, padding: '4px 10px', fontSize: 11, fontWeight: 600, borderRadius: 6,
                  background: rule.enabled ? ACCENT.emerald.bg : S[100],
                  color: rule.enabled ? ACCENT.emerald.text : S[500],
                  border: `1px solid ${rule.enabled ? ACCENT.emerald.border : S[200]}`,
                  opacity: togglingRuleId === rule.id ? 0.5 : 1,
                }}>
                  {rule.enabled ? <><ToggleRight size={12} /> On</> : <><ToggleLeft size={12} /> Off</>}
                </button>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
}
